import { itemDotColour } from "./itemDot";
import { DaySpanEditor } from "./DaySpanEditor";
import { continuingRows, isDaySlotKey, spanLabel } from "./daySpan";
import { type CSSProperties, type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { COMPARE_TAB_IDS, withCompareTabs } from "./compare/compareTabs";
import {
  ApiError,
  ATTRIBUTE_TABLE_LIMITS,
  type DialDocument,
  type ResolvedItem,
  type DocumentCategory,
  type DocumentTag,
  type FedSectionOrigin,
  type GeneratedDimension,
  type GeneratedItem,
  type GeneratedLink,
  type GeneratedNodeAttribute,
  type GeneratedStatus,
  type InstanceDetail,
  type InstanceMember,
  type ItemStatus,
  type ItemStatusEvent,
  type MessageDto,
  type NotificationSubscriptionDto,
  type SegmentRequirement,
  type SegmentStatusEvent,
  type SetNodeAttributeRequest,
  attributeScopeApplies,
  attributeShownAt,
  isFileAttributeType,
  isListAttributeType,
  isSafeHttpUrl,
  isPersonAttributeType,
  isContactAttributeType,
  isResponsibleAttributeType,
  formatResponsibleAttributeValue,
  type ContactInvite,
  isTableAttributeType,
  isUserAttributeType,
  itemScopeApplies,
  nodeAttributePairingWarnings,
  serialisePersonAttributeValue,
  serialiseVideoAttributeValue,
  type InstancePerson,
  type InstanceContact,
  type ClearedField,
  type PreviousAttributeValue,
  type ItemSpan,
} from "@keydials/api-client";
import { RichText, richTextHasContent, richTextToPlain, isSafeHref, HELP_ANCHORS } from "@keydials/ui";
import { useWorkspaceServices, useCapabilities, useTabStripDisplay } from "../context";
import type { DialVM, VSection, VSegment } from "../dialModel";
import { article, DEFAULT_SEGMENT_NOUN, dialLabel, itemNouns, segmentNoun, simpleItemCards, type SegmentNoun } from "../vocabulary";
import type { ReviewScheduleInput } from "../types";
import { Icon } from "../components/Icon";
import { FieldsetRuns, useReportFieldsetDirty } from "./attributeFieldsets";
import { OverviewChecklist } from "./OverviewChecklist";
import { type ChecklistFrame, type ChecklistRow, SHARED_FIELDS_TITLE, buildOverviewChecklist } from "./overviewChecklistModel";
import { GuidanceVideos, VideoPlayer, classifyVideoUrl } from "../components/GuidanceVideos";
import { attributeValuesInUse, listAttributeState } from "./listAttribute";
import { serialiseTableRows, tableAttributeState } from "./tableAttribute";
import { Sheet } from "../components/Sheet";
import { CHANNELS, activeChannels } from "../lib/subscriptions";
import { GuidanceDocuments } from "../components/GuidanceDocuments";
import { RichTextEditor } from "../components/RichTextEditor";
import { AxisDotRun, StatusDot } from "../components/primitives";
import { StatusPicker } from "./StatusPicker";
import { TabStrip, type TabStripItem } from "../components/TabStrip";
import { ItemTabs, type ItemTabDef, type ItemTabId } from "./ItemTabs";
import { SubscribeButton } from "./SubscribeButton";
import { HiddenTag } from "./HiddenTag";
import { ActivityPanel } from "./ActivityPanel";
import { ConversationThread, TranscriptMessageRow, type RestrictedMessaging } from "./ConversationThread";
import { transcriptTitle } from "./transcriptExport";
import { hexA, ragLabel, fmtDate, isPastDate, timeAgo, utcDay } from "../lib/ui";
import {
  type AttributeFilesContext,
  type AttributeNodeScope,
  attributeLabelFor,
  fileAttributeState,
  segmentFilesTabDocs,
  withAttachedDocument,
  withoutDocument,
} from "./fileAttribute";
import { memberLabel, userAttributeState } from "./userAttribute";
import { personAttributeState, personLabel } from "./personAttribute";
import { contactAttributeState, contactPrefillFor, mailtoHref, telHref } from "./contactAttribute";
import { type ResponsibleContext, responsibleAbove, responsibleContextOf, responsibleState } from "./responsibleAttribute";
import { AllocateButton, AllocateSheet, AllocationBadge, type AllocateOpen, type AllocationAccess, type AllocationUi } from "./AllocationControls";
import { assignmentAt } from "./allocation";
import { commentRequiredFor, gateKeys, gateValueFilled, missingGateAttributes, reviewDateOffered, reviewDateRequiredFor } from "./statusGate";
import { itemsTabIncompleteHint, itemsTabIsDangerous } from "./itemsTabStatus";
import { requirementHint, requirementMessage, segmentIsLocked } from "./segmentRequirements";
import { itemAxisDots, itemStatusKeyOf as itemKeyOf, segmentAxisDots } from "./axisDots";
import { categoryOptions, documentGroups, resolvePendingTag, tagPillStyle } from "./uploadTags";
import { rememberTab, rememberedTab, useRememberedTab } from "./rememberedTabs";
import { DEFAULT_ITEM_DATE_LABEL, itemDateBadge, itemDateLabel } from "../itemDateLabel";

type TabId = "overview" | "info" | "items" | "guidance" | "discussion" | "files" | "activity" | "changes";

/** KEY-821: changing or deleting one of the viewer's own appointments from its card. Offered only where the server
 * says the viewer may (`item.calendar.canEdit`: their own, one-off, live event in an account that lets dials write). */
export interface AppointmentEditControls {
  onEdit: (itemKey: string) => void;
  onDelete: (itemKey: string) => void;
}

/** Inline item add/remove controls for the Items tab — gated to dial-instance admins (the customisation gate). */
export interface ItemEditControls {
  /** Show the "Add item" button (canCustomise && policy.allowAddItems). */
  canAdd: boolean;
  /** Show a delete control on custom (instance-added) items (canCustomise). */
  canRemoveCustom: boolean;
  /** Show a hide control on base (definition) items (canCustomise && allowDisableBaseNodes). */
  canHideBase: boolean;
  /** Allow inline rename of custom (instance-added) items (canCustomise). */
  canRenameCustom: boolean;
  /** Allow inline rename of base (definition) items (canCustomise && allowEditWording). */
  canRenameBase: boolean;
  /** Per-segment item cap (null = unlimited); disables Add once reached. */
  maxItemsPerSegment: number | null;
  /** Append a new item to this segment; resolves to the new item's key (null on failure) so it can be expanded. */
  onAdd: () => Promise<string | null>;
  /** Remove an item: hard-delete a pristine custom item, or hide it (custom-with-activity or base) reversibly. */
  onRemove: (itemKey: string, mode: "delete" | "hide") => void;
  /** Rename an item (custom or base) — no-op if the trimmed name is empty or unchanged. */
  onRename: (itemKey: string, name: string, isCustom: boolean) => void;
  /** KEY-859: set when an added item on a day happens — all day, or from/until; null clears it. Absent, no editor. */
  onSetSpan?: (itemKey: string, span: ItemSpan | null) => void;
}

/**
 * Per-action-point controls for the "auto-set status on file upload" feature, surfaced only when the
 * definition has it on. The whole action point can be switched (cascades to its items), and each item can
 * be switched individually — effective enablement is segment AND item.
 */
/** One status axis of one item, and how an upload is (or isn't) wired to it. */
export interface UploadAutomationAxis {
  key: string;
  label: string;
  statuses: { key: string; name: string }[];
  /** Whether an upload on this item currently moves this axis. */
  enabled: boolean;
  /** The status it would set, or null when the axis is off or its target no longer exists. */
  targetStatusKey: string | null;
  /** True for the axis the DEFINITION names — the only one that is on without being asked for, and the
   * only one that has a target to fall back on when no status is chosen here. */
  isDefault: boolean;
}

export interface UploadAutomationControls {
  /** True when this dial sets the action point's own status (a Simple dial, or an itemised action point
   * that has no items yet) rather than its items'. */
  simple: boolean;
  /** Simple only: the statuses selectable for the action point's own target. */
  statusOptions: { key: string; name: string }[];
  /** Resolve a status key to its display name (falls back to the key). */
  statusNameOf: (key: string | null | undefined) => string;
  /** The dial-wide default target status key. */
  defaultStatusKey: string;
  /** Simple only: whether the action point's own automation is on, and its target override. */
  segmentEnabled: boolean;
  segmentStatusKey: string | null;
  onSetSegment: (enabled: boolean, statusKey: string | null) => void;
  /** Itemised: every axis this item is SCORED ON, with its current wiring. Empty for an item with none —
   * an item is never offered a palette belonging to an axis it doesn't carry, which is the whole of
   * KEY-279. */
  axesFor: (itemKey: string) => UploadAutomationAxis[];
  onSetItemAxis: (itemKey: string, dimensionKey: string, enabled: boolean, statusKey: string | null) => void;
}

/** A compact on/off switch used for the upload-automation toggles. */
function UploadAutoToggle({ on, onChange, label, title, disabled = false }: { on: boolean; onChange: (next: boolean) => void; label: string; title?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!on)}
      className="kd-focusable"
      style={{
        display: "inline-flex", alignItems: "center", gap: 7, font: "inherit", fontSize: 11.5, fontWeight: 700,
        padding: "4px 9px", borderRadius: 99, cursor: disabled ? "default" : "pointer", whiteSpace: "nowrap", opacity: disabled ? 0.5 : 1,
        border: `var(--border-w) solid ${on ? "var(--accent-border)" : "var(--border)"}`,
        background: on ? "var(--accent-soft)" : "var(--surface)",
        color: on ? "var(--accent-text)" : "var(--text-muted)",
      }}
    >
      <span style={{ position: "relative", width: 26, height: 15, borderRadius: 99, flex: "none", transition: "background 0.12s", background: on ? "var(--accent, #6366f1)" : "var(--border)" }}>
        <span style={{ position: "absolute", top: 2, left: on ? 13 : 2, width: 11, height: 11, borderRadius: 99, background: "#fff", transition: "left 0.12s" }} />
      </span>
      {label}
    </button>
  );
}

/** A compact dropdown for choosing a node's upload-automation target status, with an "inherit" first option. */
function UploadStatusSelect({ value, inheritLabel, options, disabled = false, onPick }: {
  value: string | null;
  inheritLabel: string;
  options: { key: string; name: string }[];
  disabled?: boolean;
  onPick: (statusKey: string | null) => void;
}) {
  return (
    <select
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onPick(e.target.value || null)}
      className="kd-focusable"
      title="Choose the status this upload sets, or inherit the default"
      style={{
        font: "inherit", fontSize: 11.5, fontWeight: 600, padding: "3px 8px", borderRadius: "var(--radius-sm)",
        border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text-muted)",
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, maxWidth: 190,
      }}
    >
      <option value="">{inheritLabel}</option>
      {options.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
    </select>
  );
}

/**
 * What an upload does to ONE item — one row per status axis the item is scored on (KEY-279).
 *
 * It used to be a single row offering the dial's one dimension, whatever the item was actually scored on:
 * an item carrying only Risk was shown an Evidence palette and told an upload would set it, which it never
 * could. Now an item with three axes gets three rows and an item with none gets nothing at all.
 */
function ItemUploadAutomation({ item, controls }: { item: GeneratedItem; controls: UploadAutomationControls }) {
  const axes = controls.axesFor(item.key);
  if (axes.length === 0) {
    return null;
  }
  return (
    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
      {axes.map((axis) => {
        const name = (k: string | null) => axis.statuses.find((st) => st.key === k)?.name ?? k ?? "";
        return (
          <div key={axis.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 0 }}>
              {axis.enabled && axis.targetStatusKey
                ? <>Uploading a file sets <strong style={{ color: "var(--text)" }}>{axis.label}</strong> to <strong style={{ color: "var(--text)" }}>{name(axis.targetStatusKey)}</strong></>
                : <>Uploading a file leaves <strong style={{ color: "var(--text)" }}>{axis.label}</strong> alone</>}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {axis.enabled && (
                <UploadStatusSelect
                  value={axis.targetStatusKey}
                  /* No "inherit" option off the default axis: nothing could supply a target from another
                     palette, so the server refuses an axis switched on without one. */
                  inheritLabel={axis.isDefault ? `Default · ${name(controls.defaultStatusKey)}` : "Choose a status…"}
                  options={axis.statuses}
                  onPick={(statusKey) => controls.onSetItemAxis(item.key, axis.key, true, statusKey)}
                />
              )}
              <UploadAutoToggle
                on={axis.enabled}
                onChange={(next) => controls.onSetItemAxis(
                  item.key,
                  axis.key,
                  next,
                  // Switching a non-default axis on needs a target immediately — the server refuses one
                  // without, so offer its first status rather than a request that bounces.
                  next && !axis.isDefault ? (axis.targetStatusKey ?? axis.statuses[0]?.key ?? null) : axis.targetStatusKey,
                )}
                label={axis.enabled ? "On" : "Off"}
                title={`Switch auto-status-on-upload for ${axis.label}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** The action-point-level upload-automation row: effective target + a status override + the on/off toggle.
 * Reused for the itemised Items header ("each item") and the simple-dial Files banner ("this action point"). */
function SegmentAutomationControl({ controls, noun, toggleTitle, onLabel, offLabel, style }: {
  controls: UploadAutomationControls;
  /** The thing the status is set ON — "this action point" / "each item". Already-rendered prose, not a
   *  vocabulary: the caller composes it from {@link SegmentNoun} where it names an action point. */
  noun: string;
  /** Hover text for the on/off switch, which names the node in the dial's own words (KEY-539). */
  toggleTitle: string;
  onLabel: string;
  offLabel: string;
  style?: CSSProperties;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", ...style }}>
      <span style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 0 }}>
        Uploading a file sets {noun} to <strong style={{ color: "var(--text)" }}>{controls.statusNameOf(controls.segmentStatusKey ?? controls.defaultStatusKey)}</strong>.
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <UploadStatusSelect
          value={controls.segmentStatusKey}
          inheritLabel={`Default · ${controls.statusNameOf(controls.defaultStatusKey)}`}
          options={controls.statusOptions}
          disabled={!controls.segmentEnabled}
          onPick={(statusKey) => controls.onSetSegment(controls.segmentEnabled, statusKey)}
        />
        <UploadAutoToggle
          on={controls.segmentEnabled}
          onChange={(next) => controls.onSetSegment(next, controls.segmentStatusKey)}
          label={controls.segmentEnabled ? onLabel : offLabel}
          title={toggleTitle}
        />
      </div>
    </div>
  );
}

// ---- Status-gate guided fill (KEY-248): a gated status pick is parked, not fired. The fields the gate
// needs surface (they hide while empty at rest) and highlight amber; the pick commits by itself the moment
// the last one is saved. The server still re-checks on write — this flow just means nobody meets it as a 400.

const GATE_AMBER = "#b45309";

/** The notice shown while a gated status pick is parked: what's needed, and a way out. */
function PendingGateNotice({ statusName, missing, onCancel, style }: {
  statusName: string;
  missing: GeneratedNodeAttribute[];
  onCancel: () => void;
  style?: CSSProperties;
}) {
  const labels = missing.map((d) => d.label);
  const list = labels.length <= 2 ? labels.join(" and ") : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
  return (
    <div
      role="status"
      style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "9px 12px", borderRadius: "var(--radius-sm)",
        border: `var(--border-w) solid ${hexA(GATE_AMBER, 0.4)}`, background: hexA(GATE_AMBER, 0.07),
        fontSize: 12.5, color: "var(--st-amber, #b45309)",
        ...style,
      }}
    >
      <span style={{ minWidth: 0 }}>
        <strong>{statusName}</strong> needs {list} filled in first — it will be applied when the highlighted
        {labels.length === 1 ? " field is" : " fields are"} saved.
      </span>
      <button
        type="button"
        onClick={onCancel}
        className="kd-focusable"
        style={{
          font: "inherit", fontSize: 12, fontWeight: 700, padding: "3px 10px", marginLeft: "auto",
          borderRadius: "var(--radius-sm)", border: `var(--border-w) solid ${hexA(GATE_AMBER, 0.4)}`,
          background: "var(--surface)", color: "var(--st-amber, #b45309)", cursor: "pointer", whiteSpace: "nowrap",
        }}
      >
        Cancel
      </button>
    </div>
  );
}

/** Rings a field a parked status pick is waiting on; renders children untouched otherwise. */
function GateHighlight({ demanded, statusName, children, style }: {
  demanded: boolean;
  statusName?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  if (!demanded) {
    return <>{children}</>;
  }
  return (
    <div style={{
      border: `var(--border-w) solid ${hexA(GATE_AMBER, 0.55)}`,
      background: hexA(GATE_AMBER, 0.05),
      borderRadius: "var(--radius-sm)",
      padding: "9px 11px",
      ...style,
    }}>
      {children}
      {statusName && (
        <div style={{ marginTop: 5, fontSize: 11.5, fontWeight: 600, color: "var(--st-amber, #b45309)" }}>
          Needed to set {statusName}
        </div>
      )}
    </div>
  );
}

/**
 * The prompt a parked status pick shows when it needs things only the user can type (KEY-412 / KEY-919): a
 * required review date, a comment saying why, or both. Shared by an action point's own status and an item's
 * per-axis status so the two can't drift. It owns the in-progress comment and — when both are needed — the
 * date chosen so far, and calls `onCommit` exactly once with everything the change needs: the server refuses
 * the status without them, so they have to travel in the one request.
 *
 * `dateWord` is the date as the prompt names it ("a review date", "a target date"), or null when no date is
 * needed. `renderDate` draws the date control and is handed the callback to report a chosen date through.
 */
function PendingStatusPrompt({ statusName, dateWord, needsComment, compact, onCancel, onCommit, renderDate }: {
  statusName: string;
  dateWord: string | null;
  needsComment: boolean;
  compact?: boolean;
  onCancel: () => void;
  onCommit: (review: ReviewScheduleInput) => void;
  renderDate: (onChosen: (reviewDate: string | null, revertToStatusKey: string | null) => void) => ReactNode;
}) {
  const [comment, setComment] = useState("");
  const [chosen, setChosen] = useState<{ reviewDate: string | null; revertToStatusKey: string | null } | null>(null);
  const needsDate = dateWord !== null;
  const trimmed = comment.trim();
  const canSet = trimmed !== "" && (!needsDate || chosen !== null);

  const onDateChosen = (reviewDate: string | null, revertToStatusKey: string | null) => {
    if (!needsComment) {
      onCommit({ reviewDate, revertToStatusKey });
    } else if (trimmed) {
      onCommit({ reviewDate, revertToStatusKey, comment: trimmed });
    } else {
      setChosen({ reviewDate, revertToStatusKey }); // still waiting on the comment
    }
  };

  const what = needsDate && needsComment ? `${dateWord} and a comment saying why`
    : needsDate ? dateWord
    : "a comment saying why";
  const fontSize = compact ? 12.5 : 13;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize, color: "var(--text-muted)" }}>
          <strong style={{ color: "var(--text)" }}>{statusName}</strong> needs {what} before it can be set.
        </span>
        <button
          onClick={onCancel}
          className="kd-focusable"
          style={{ ...reviewPill, padding: compact ? "4px 9px" : "5px 10px", fontSize: compact ? 12 : 12.5, color: "var(--text-muted)" }}
        >
          Cancel
        </button>
      </div>
      {needsDate && !chosen && renderDate(onDateChosen)}
      {needsDate && chosen?.reviewDate && (
        <span style={{ fontSize: compact ? 12 : 12.5, color: "var(--text-muted)" }}>
          Date chosen: {fmtDate(utcDay(chosen.reviewDate))}
        </span>
      )}
      {needsComment && (
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: compact ? 12 : 12.5, fontWeight: 600, color: "var(--text)" }}>
          Why? — {statusName} needs a comment
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={compact ? 2 : 3}
            className="kd-focusable"
            style={{
              font: "inherit", fontSize, fontWeight: 400, padding: "7px 10px", resize: "vertical",
              borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)",
              background: "var(--surface)", color: "var(--text)",
            }}
          />
        </label>
      )}
      {needsComment && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={() => onCommit({
              reviewDate: chosen?.reviewDate ?? null,
              revertToStatusKey: chosen?.revertToStatusKey ?? null,
              comment: trimmed,
            })}
            disabled={!canSet}
            className="kd-focusable"
            style={{
              ...reviewPill, padding: compact ? "4px 12px" : "5px 14px", fontSize: compact ? 12 : 12.5, fontWeight: 700,
              background: "var(--accent)", color: "var(--on-accent, #fff)", borderColor: "var(--accent)",
              opacity: canSet ? 1 : 0.5, cursor: canSet ? "pointer" : "default",
            }}
          >
            Set
          </button>
          {needsDate && !chosen && (
            <span style={{ fontSize: compact ? 11.5 : 12, color: "var(--text-faint)" }}>Choose the date first.</span>
          )}
        </div>
      )}
    </div>
  );
}

/** What NodeAttributesBlock needs to hide gate-backed fields at rest and highlight the demanded ones. */
interface SegmentGateContext {
  /** Every attribute key any status of this action point's palette gates on (hidden while empty). */
  gateKeySet: ReadonlySet<string>;
  /** The keys a parked pick is currently waiting on (revealed + highlighted). */
  demandedKeys: ReadonlySet<string>;
  /** The parked status's display name, for the per-field caption. */
  pendingStatusName?: string;
}

export interface ActionPointPanelProps {
  /** KEY-532: the level chip, rendered beside this panel's title in the Dynamic workspace. See the
   * note in panels.tsx — it used to sit in a card of its own above, which is what the projection's
   * frame and beam then landed on. Omitted in Classic, which keeps the strip. */
  scopePill?: ReactNode;
  vm: DialVM;
  section: VSection;
  segment: VSegment;
  itemStatusIndex: Map<string, string>;
  history: SegmentStatusEvent[];
  itemHistory: ItemStatusEvent[];
  statusName: (statusKey: string) => string;
  canSet: (status: GeneratedStatus, sectionKey: string, segmentKey: string, itemKey?: string) => boolean;
  /** KEY-916: who may hand nodes out here; absent when the host offers no allocation. */
  allocation?: AllocationAccess;
  /** Reload the dial — after an allocation changes who has what. */
  onRefresh?: () => void;
  /** Set when this action point belongs to a section another organisation feeds onto the dial (KEY-257
   * phase 3). The caller withholds every edit gate in that case; this is what says why on screen. */
  fedFrom?: FedSectionOrigin | null;
  canEditDocs: boolean;
  /** Whether this reader may SEE that files exist at all (KEY-537). False removes the Files tab rather than
   * leaving an empty one — the server returns nothing for such a reader, and a filename is itself the
   * disclosure a hidden file is hidden for. Defaults to true where the host can't answer. */
  canListDocs?: boolean;
  /** Add an upload tag to this dial (KEY-499) — resolves to an error message to show inline, or null on
   * success. Travels the same route as canEditDocs, since it answers to the same permission. */
  onCreateDocumentTag?: (label: string) => Promise<string | null>;
  /** Whether the user may edit node-attribute fields (decoupled from documents so the admin preview can
   * offer try-outable attributes while uploads stay off). */
  canEditAttributes: boolean;
  attributeAccess?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  /** Whether the user may restore an earlier document version (instance admins only). */
  canManageDocVersions: boolean;
  onSetSegmentStatus: (statusKey: string, review?: ReviewScheduleInput) => void;
  /** Schedule (date + revert target) or clear an auto-revert on this segment (Phase 6, KEY-51). */
  onScheduleRevert: (reviewDate: string | null, revertToStatusKey: string | null,
    reminderLeadDays: number | null, reminderRepeatDays: number | null,
    recurrenceUnit: string | null, recurrenceCount: number | null) => void;
  onSetItemStatus: (itemKey: string, dimensionKey: string, statusKey: string, review?: ReviewScheduleInput) => void;
  /** Schedule (date + revert target) or clear an auto-revert on one item's status axis (KEY-376). */
  onScheduleItemRevert: (itemKey: string, dimensionKey: string, reviewDate: string | null,
    revertToStatusKey: string | null, reminderLeadDays: number | null, reminderRepeatDays: number | null,
    recurrenceUnit: string | null, recurrenceCount: number | null) => void;
  /** Set (or clear) a node-attribute value (KEY-161), then refresh the instance. */
  onSetNodeAttribute: (request: SetNodeAttributeRequest) => void | Promise<void>;
  /** Notification preference rows for this instance + a reloader (for the Subscribe control). */
  subs: NotificationSubscriptionDto[];
  onSubsChanged: () => void | Promise<void>;
  /** Whether the current user has a phone number on file — gates the SMS channel (KEY-453). */
  meHasPhoneNumber?: boolean;
  /** Discussion (lives in the Discussion tab). */
  meUserId: string | null;
  canPost: boolean;
  canModerate: boolean;
  restricted?: RestrictedMessaging;
  /** Inline item add/remove controls + their permission gates (instance admins only); itemised dials. */
  itemEdit?: ItemEditControls;
  appointmentEdit?: AppointmentEditControls;
  /** Set the auto-set-status-on-upload overlay (on/off + target-status override) for this action point
   * (itemKey null) or one item; undefined = feature off/not permitted. */
  onSetUploadAutomation?: (itemKey: string | null, dimensionKey: string | null, enabled: boolean, statusKey: string | null) => void;
  /** Open this action point's section — where its Section details are edited (KEY-681). */
  onOpenSection?: () => void;
  /** KEY-859: go to another time of day (where something running on into this one is listed). */
  onOpenSegment?: (segmentKey: string) => void;
  /** Look back's compare mode is on: the strip shrinks to the compare set and this is the Changes tab. */
  compareChanges?: ReactNode;
  /** Look back's compare card (Then beside Now): shown in place of the Overview tab while comparing. */
  compareOverview?: ReactNode;
}

export function ActionPointPanel({
  scopePill,
  vm,
  section,
  segment,
  itemStatusIndex,
  history,
  itemHistory,
  statusName,
  canSet,
  allocation,
  onRefresh,
  fedFrom = null,
  canEditDocs,
  canListDocs = true,
  onCreateDocumentTag,
  canEditAttributes,
  attributeAccess,
  canManageDocVersions,
  onSetSegmentStatus,
  onScheduleRevert,
  onSetItemStatus,
  onScheduleItemRevert,
  onSetNodeAttribute,
  subs,
  onSubsChanged,
  meHasPhoneNumber,
  meUserId,
  canPost,
  canModerate,
  restricted,
  itemEdit,
  appointmentEdit,
  onSetUploadAutomation,
  onOpenSection,
  onOpenSegment,
  compareChanges,
  compareOverview,
}: ActionPointPanelProps) {
  const { api, getAccessToken } = useWorkspaceServices();
  const caps = useCapabilities();
  // KEY-259: THIS action point drives its own status whenever it isn't scored by items — a Simple dial's
  // action point, or an itemised one that has no items yet. Every per-action-point decision below keys off
  // the segment, not vm.simple (a whole-dial property): on an itemised dial the two now differ per segment.
  const actsSimple = !segment.itemActive;
  // KEY-512. Empty for an action point that carries its own status: it has no items for an axis to read.
  const segmentDots = segmentAxisDots(vm.dimensions, itemStatusIndex, section.key, segment.key, segment.items);
  // An itemised action point opens on its items — except while comparing, where the Then/Now card is on
  // Overview and is what the reader came for.
  const defaultTab: TabId = actsSimple || compareOverview ? "overview" : "items";
  // KEY-774: once the reader has picked a tab, every action point opens on it — moving to the next one keeps
  // them on Items, or Guidance, rather than resetting. Held outside this panel (see rememberedTabs), because the
  // Dynamic workspace remounts it on every pick.
  const [remembered, remember] = useRememberedTab<TabId>("actionPoint");
  // Look back's compare mode keeps its own picks: it opens each node on the Then/Now card, and a tab chosen
  // while comparing (Changes, say) mustn't become the live dial's tab. Cleared when compare mode ends, so the
  // remembered tab comes straight back.
  const [comparePick, setComparePick] = useState<TabId | null>(null);
  const comparing = !!compareOverview;
  useEffect(() => {
    if (!comparing) {
      setComparePick(null);
    }
  }, [comparing]);
  const tab: TabId = comparing ? (comparePick ?? defaultTab) : (remembered ?? defaultTab);
  const setTab = comparing ? setComparePick : remember;
  const [docs, setDocs] = useState<DialDocument[]>([]);
  // KEY-569: which instance/section `docs` was last loaded for. Until it matches, an empty list means "not
  // known yet" rather than "no files", so the Overview list mustn't call a file field's upload gone.
  const [docsLoadedFor, setDocsLoadedFor] = useState<string | null>(null);
  // KEY-569: the field a row on Overview asked Info to bring into view (`Segment:key` / `Dial:key`).
  const [focusField, setFocusField] = useState<string | null>(null);
  const clearFocusField = useCallback(() => setFocusField(null), []);

  const instanceId = vm.detail.id;
  // KEY-539: what this dial calls an action point. vm.detail already reaches every panel, so no new
  // plumbing is needed here — only the three sub-components below that don't receive it.
  // KEY-762: or, where this section names its own lists ("dentist"), the section's word.
  const noun = segmentNoun(vm.detail, section.key);
  // KEY-753: what THIS section calls its items — "appointments" on a family dial's Appointments section.
  const itemWords = itemNouns(vm.detail, section.key);

  // Bind the scope of THIS action point so status gating is per-section/segment: a user who is Editor on one
  // section and Reader elsewhere sees the Editor options only here (the server re-checks on write).
  const canSetHere = useCallback((status: GeneratedStatus) => canSet(status, section.key, segment.key), [canSet, section.key, segment.key]);
  // KEY-916: allocation — on when the dial sends its allocations at all (the server's switch).
  const [allocating, setAllocating] = useState<AllocateOpen | null>(null);
  const allocationUi: AllocationUi | undefined = vm.detail.assignments != null
    ? { instanceId, access: allocation, onRefresh, open: setAllocating }
    : undefined;
  const apScope = { sectionKey: section.key, segmentKey: segment.key };
  const apAssignment = allocationUi ? assignmentAt(vm.detail, apScope) : null;
  const apName = `${section.label} › ${segment.name}`;
  // An item's statuses ask at the item (KEY-918), so its own Responsible person counts.
  const canSetItem = useCallback(
    (status: GeneratedStatus, itemKey: string) => canSet(status, section.key, segment.key, itemKey),
    [canSet, section.key, segment.key],
  );

  // Status-gate guided fill for THIS action point's own status (actsSimple only — an item-scored action
  // point takes no manual status, so its gates bite on the items instead; see ItemRow).
  const segmentAttrDecls = useMemo(
    () => (vm.detail.nodeAttributes ?? []).filter(
      (d) => d.target === "Segment" && attributeScopeApplies(d.target, d.appliesToKeys, section.key, segment.key, "")),
    [vm.detail.nodeAttributes, section.key, segment.key]);
  const segmentAttrValueOf = useCallback(
    (attrKey: string) =>
      (vm.detail.nodeAttributeValues ?? []).find((v) =>
        v.nodeType === "Segment" && v.attributeKey === attrKey && v.sectionKey === section.key
        && v.segmentKey === segment.key && v.itemKey === "")?.value ?? "",
    [vm.detail.nodeAttributeValues, section.key, segment.key]);
  const segmentGateKeySet = useMemo(() => gateKeys(segment.palette), [segment.palette]);
  // What the CALLER would actually hear a reminder on here, read from the same subscription rows the
  // Subscribe control writes — so the sheet can never promise a channel Subscribe has switched off.
  const myChannels = useMemo(
    () => [...activeChannels(subs, { sectionKey: section.key, segmentKey: segment.key })]
      .map((c) => CHANNELS.find((x) => x.key === c)?.label ?? c),
    [subs, section.key, segment.key]);
  const [pendingStatusKey, setPendingStatusKey] = useState<string | null>(null);
  const pendingStatus = pendingStatusKey ? segment.palette.find((s) => s.key === pendingStatusKey) : undefined;
  const pendingMissing = pendingStatus ? missingGateAttributes(pendingStatus, segmentAttrDecls, segmentAttrValueOf) : [];
  // KEY-412: an action point has no per-item expiry switch, so only the status's own policy applies here.
  const pendingNeedsReviewDate = !!pendingStatus && pendingMissing.length === 0
    && reviewDateRequiredFor(pendingStatus, true, segment.reviewDate);
  // KEY-919: a status flagged "Needs a comment" wants one saying why — but only for a genuine change; re-picking
  // the status the action point already holds is exempt on the server, so it is here too.
  const segmentNeedsComment = (target: GeneratedStatus | undefined) =>
    !!target && target.key !== segment.status?.key && commentRequiredFor(target);
  const pendingNeedsComment = !!pendingStatus && pendingMissing.length === 0 && segmentNeedsComment(pendingStatus);

  const pickSegmentStatus = (statusKey: string) => {
    const target = segment.palette.find((s) => s.key === statusKey);
    // KEY-916: a status that hands the work on asks who to first, and travels with them.
    if (allocationUi && target?.handsOn && target.key !== segment.status?.key) {
      allocationUi.open({
        scope: apScope, nodeName: apName, mode: "handOn", handOnStatusName: target.name,
        onHandOn: (handOnTo) => {
          setAllocating(null);
          onSetSegmentStatus(statusKey, { reviewDate: null, revertToStatusKey: null, handOnTo });
        },
      });
      return;
    }
    // Only park the pick when the user can actually fill the fields in; otherwise the flow could never
    // complete, so let the server answer as before (its refusal message names the field). Per-attribute
    // rules (KEY-299) gate the same way: one required field the user can't see or edit un-parks the lot.
    const gateMissing = target && canEditAttributes ? missingGateAttributes(target, segmentAttrDecls, segmentAttrValueOf) : [];
    const missing = gateMissing.some((d) => {
      const a = attributeAccess?.(d);
      return a !== undefined && (!a.see || !a.edit);
    })
      ? []
      : gateMissing;
    if (missing.length === 0) {
      // As on an item: a status that requires a review date can't be applied first and dated after — nor
      // can one that needs a comment (KEY-919) be applied and explained later. Park it and ask.
      if (reviewDateRequiredFor(target, true, segment.reviewDate) || segmentNeedsComment(target)) {
        setPendingStatusKey(statusKey);
        setTab("overview");
        return;
      }
      setPendingStatusKey(null);
      onSetSegmentStatus(statusKey);
      return;
    }
    setPendingStatusKey(statusKey);
    setTab("info"); // the fields live on the Info tab (KEY-485; they were on Overview)
  };

  // Commit the parked pick the moment its last required field lands — saved values come back through the
  // host's instance refresh, so this re-check on every render is exactly the trigger we need.
  useEffect(() => {
    if (!pendingStatusKey) {
      return;
    }
    const target = segment.palette.find((s) => s.key === pendingStatusKey);
    if (!target) {
      setPendingStatusKey(null);
      return;
    }
    if (missingGateAttributes(target, segmentAttrDecls, segmentAttrValueOf).length === 0) {
      if (reviewDateRequiredFor(target, true, segment.reviewDate) || segmentNeedsComment(target)) {
        return; // waiting on a date / comment the user types, not on a value a refresh brings
      }
      setPendingStatusKey(null);
      onSetSegmentStatus(pendingStatusKey);
    }
  }, [pendingStatusKey, segment.palette, segmentAttrDecls, segmentAttrValueOf, onSetSegmentStatus]);

  // Auto-set-status-on-upload controls for this action point — present only when the definition has the
  // feature on, the host wired the setter, and the user may upload.
  const uploadAutomation: UploadAutomationControls | undefined = (() => {
    const ua = vm.detail.uploadAutomation;
    if (!ua || !onSetUploadAutomation || !canEditDocs || !caps.documents) {
      return undefined;
    }
    const settings = vm.detail.uploadAutomationSettings ?? [];
    const rowFor = (itemKey: string | null, dimensionKey: string | null) =>
      settings.find((s) => s.sectionKey === section.key && s.segmentKey === segment.key
        && (s.itemKey ?? null) === itemKey && (s.dimensionKey ?? null) === dimensionKey);
    const segRow = rowFor(null, null);
    // The action point's own switch is no longer offered, but it still holds its items off by inheritance
    // where somebody switched it off before KEY-279 — so the per-item default has to read it.
    const actionPointEnabled = segRow?.enabled ?? true;
    const statusOptions = segment.palette.map((s) => ({ key: s.key, name: s.name }));
    const nameByKey = new Map(statusOptions.map((s) => [s.key, s.name] as const));
    return {
      simple: actsSimple,
      statusOptions,
      statusNameOf: (key) => (key ? (nameByKey.get(key) ?? key) : ""),
      defaultStatusKey: ua.statusKey,
      segmentEnabled: actionPointEnabled,
      segmentStatusKey: segRow?.statusKey ?? null,
      onSetSegment: (enabled, statusKey) => onSetUploadAutomation(null, null, enabled, statusKey),
      axesFor: (itemKey) => (vm.detail.dimensions ?? [])
        .filter((d) => itemScopeApplies(d.appliesToItemKeys, section.key, segment.key, itemKey))
        .map((d) => {
          const row = rowFor(itemKey, d.key);
          const isDefault = !!ua.dimensionKey && ua.dimensionKey === d.key;
          const keys = new Set(d.palette.statuses.map((st) => st.key));
          const valid = (k: string | null | undefined) => (k && keys.has(k) ? k : null);
          // Mirrors the server's UploadAutomationAxis.Resolve — same three levels, same fallbacks, so the
          // switch never claims something the apply path wouldn't do.
          const enabled = row ? row.enabled : isDefault && actionPointEnabled;
          const target = !enabled
            ? null
            : valid(row?.statusKey) ?? (isDefault ? valid(ua.statusKey) : null);
          return {
            key: d.key,
            label: d.label,
            statuses: d.palette.statuses.map((st) => ({ key: st.key, name: st.name })),
            enabled,
            targetStatusKey: target,
            isDefault,
          };
        }),
      onSetItemAxis: (itemKey, dimensionKey, enabled, statusKey) =>
        onSetUploadAutomation(itemKey, dimensionKey, enabled, statusKey),
    };
  })();

  const reload = useCallback(async () => {
    // Files are a live-only feature; skip the fetch entirely in preview.
    if (!caps.documents) {
      return;
    }
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      // Fetched per SECTION, not per segment: a Section-target file attribute's files have no segment key,
      // so a segment-filtered fetch would never see them. Segment/item lists filter this down client-side.
      setDocs(await api.listDocuments(token, instanceId, section.key));
      setDocsLoadedFor(`${instanceId}/${section.key}`);
    } catch {
      // keep whatever we have; upload/download errors surface separately
    }
  }, [instanceId, section.key]);

  // KEY-774: the tab is no longer reset here — it is remembered across nodes (see `tab` above). Keyed on the
  // node alone: an action point gaining its first item flips actsSimple, and that mustn't count as a move.
  useEffect(() => {
    setPendingStatusKey(null); // a parked pick belongs to the node it was made on
    setFocusField(null);
    void reload();
  }, [segment.key, section.key, reload]);

  // Files backing a Document/Image/Video attribute belong to that field, not to the node's evidence list —
  // used for the item rows' own inline file sections, which stay evidence-only.
  const evidence = docs.filter((d) => d.segmentKey === segment.key && !d.attributeKey);
  // The Files TAB is broader (KEY-461 decision 4): the segment's own evidence and attribute files, plus its
  // items' — additive to the Info tab's per-field rendering, not a replacement for it.
  const filesTabDocs = segmentFilesTabDocs(docs, segment.key);
  const attributeFiles: AttributeFilesContext = {
    instanceId,
    docs,
    canUpload: canEditDocs,
    canManageVersions: canManageDocVersions,
    enabled: caps.documents,
    onChanged: reload,
  };

  // Item key → display name, for the file-attribution chips on the Files tab.
  const itemNameByKey = new Map(segment.items.map((i) => [i.key, i.name]));

  // Hoisted out of the JSX because the Info tab has to ask, before the strip is built, whether there is
  // anything behind it — and the gate context is part of that answer: a gate-backed field is hidden while
  // empty, so it must not conjure a tab, but a parked status pick demanding it must.
  const segmentGate: SegmentGateContext | undefined = actsSimple ? {
    gateKeySet: segmentGateKeySet,
    demandedKeys: new Set(pendingMissing.map((d) => d.key)),
    pendingStatusName: pendingStatus?.name,
  } : undefined;
  // Resolved once and handed to both Info and Overview (KEY-569): the Overview list is built from exactly the
  // fields Info draws, so it can never name one Info hides.
  const attributeGroups = nodeAttributeGroups(vm.detail, section.key, segment.key, attributeAccess, segmentGate);
  const showInfoTab = attributeGroups.any;

  // KEY-460: the fields this action point can't be worked without, and hasn't got. The server decides this
  // (a value restricted away from this viewer is still a value), and refuses the item-status write on
  // exactly the same set — so the lock below explains a refusal rather than inventing one.
  const missingRequired = segment.missingRequiredFields ?? [];
  const itemsLocked = segmentIsLocked(missingRequired);
  // Whether THIS person can clear the lock themselves. `every`, not `some`: one field they can't touch
  // leaves the action point locked whatever they do to the others, and sending them to a tab that will
  // refuse them is the dead end this is meant to avoid.
  const canFillRequired =
    canEditAttributes
    && missingRequired.every((m) => {
      const decl = (vm.detail.nodeAttributes ?? []).find((a) => a.target === "Segment" && a.key === m.attributeKey);
      return decl ? (attributeAccess?.(decl).edit ?? true) : false;
    });
  // KEY-569: how this action point's own status frames its Overview list. Only a node that carries its own
  // status has one to frame it with — an item-scored action point lists its fields under its items count.
  // Complete reads as a record rather than a to-do, and an excluded status (Not required) as nothing owed,
  // matching how an item at either status is treated.
  const overviewFrame: ChecklistFrame = !actsSimple ? "todo"
    : segment.status?.isExcludedFromRollup ? "excluded"
    : segment.status?.isTerminal ? "record"
    : "todo";
  const filesReliable = caps.documents && canListDocs && docsLoadedFor === `${instanceId}/${section.key}`;
  const jumpToField = (row: ChecklistRow) => {
    setFocusField(row.id);
    setTab("info");
  };

  // The Items tab stays on EVERY action point of an itemised dial, including one still acting simple — it's
  // the only way to add that first item, which is what switches the action point over to item scoring.
  // Canonical action-point tab order — Overview · Guidance · Key Info · Items · Discussion · Files · Activity.
  // KEY-670: every heading on this strip is the dial's own word where its definition renames the tab — the Wipro
  // tracker's Guidance is "Deliverables" — and the platform's otherwise. Counts stay ours; only the word is theirs.
  // Counts are badges rather than "· N" in the label, so they survive the strip being drawn as icons.
  // KEY-773: the segment's figures less its calendar events, for the Items tab's incomplete warning.
  const ownDone = segment.done - (segment.eventsDone ?? 0);
  const ownTotal = segment.total - (segment.eventsTotal ?? 0);
  const tabs: TabStripItem<TabId>[] = [
    { id: "overview", label: dialLabel(vm.detail, "overview.segment"), icon: "gauge" },
    { id: "guidance", label: dialLabel(vm.detail, "guidance.segment"), icon: "compass", count: segment.videos?.length ?? 0, helpAnchorId: HELP_ANCHORS.apTabGuidance },
    // KEY-485: this action point's own fields, and the section's. They used to hang off the bottom of the
    // Overview tab, below the description and the guidance links, where they read as a footnote to the
    // prose rather than as the answers someone came to fill in.
    ...(showInfoTab ? [{ id: "info" as TabId, label: dialLabel(vm.detail, "info"), icon: "info" as const, helpAnchorId: HELP_ANCHORS.apTabInfo }] : []),
    ...(!vm.simple ? [{
      id: "items" as TabId,
      label: dialLabel(vm.detail, "items"),
      icon: "listOrdered" as const,
      count: segment.items.length,
      helpAnchorId: HELP_ANCHORS.apTabItems,
      // KEY-461: flag incompleteness where the reader already is, instead of leaving it to the "N of M
      // items complete" chip title nobody hovers to find. KEY-460's lock takes precedence over it: while
      // the action point isn't ready, how complete its items are isn't the thing to say about them.
      //
      // Still reachable, deliberately — NOT `disabled`. A tab that can't be opened can't explain itself,
      // and "why can't I see the items" is exactly the question the pane behind it answers.
      //
      // KEY-773: on the dial's own items only. A time of day counts its calendar appointments, but an appointment
      // still to come isn't unfinished work, so one alone must not turn the tab red.
      tone: itemsLocked || itemsTabIsDangerous(ownDone, ownTotal) ? "danger" as const : undefined,
      title: itemsLocked ? requirementHint(missingRequired) : itemsTabIncompleteHint(ownDone, ownTotal),
    }] : []),
    { id: "discussion", label: dialLabel(vm.detail, "discussion.segment"), icon: "chat", helpAnchorId: HELP_ANCHORS.apTabDiscussion },
    // KEY-537: the tab goes entirely when the dial doesn't let this reader SEE its files. The server
    // returns none for them, so leaving the tab would offer an empty pane that reads as a bug — and a
    // filename is itself the disclosure a hidden file is hidden for.
    ...(canListDocs ? [{ id: "files" as TabId, label: dialLabel(vm.detail, "files.segment"), icon: "clip" as const, count: filesTabDocs.length, helpAnchorId: HELP_ANCHORS.apTabFiles }] : []),
    // No count: the trail is fetched by the tab itself now (KEY-464), so it isn't known until it opens.
    { id: "activity", label: dialLabel(vm.detail, "activity.segment"), icon: "activity", helpAnchorId: HELP_ANCHORS.apTabActivity },
  ];
  const stripDisplay = useTabStripDisplay();
  const shownTabs = compareChanges ? withCompareTabs(tabs) : tabs;
  // A tab this node doesn't have — a remembered Info on an action point with no visible fields, Files where
  // they can't be listed, Items on a Simple dial (KEY-774), or an access change emptying Info under the reader
  // — shows the node's own default instead. Display only: the stored tab is left alone, so it comes back on
  // the next node that has it.
  const activeTab: TabId =
    !shownTabs.some((t) => t.id === tab) ? defaultTab
    : compareChanges && !COMPARE_TAB_IDS.has(tab) ? "overview"
    : !compareChanges && tab === "changes" ? "overview"
    : tab;

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
      {/* KEY-513: the section used to be repeated here as a faint eyebrow. The scope header said "in
          <section>" beside the level chip, so this was the same fact twice, in two styles. KEY-532 moved the
          chip itself into this row — in the Dynamic workspace the header's own card is gone, because being a
          card meant the projection's frame and beam landed on IT rather than on the panel below. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {scopePill}
        <h2 style={{ fontSize: 23, fontWeight: 800, margin: 0, color: "var(--text)", lineHeight: 1.2 }}>{segment.name}</h2>
        <HiddenTag hidden={segment.isHidden} />
      </div>
      {fedFrom && (
        // Said plainly and at the top, because everything below is read-only and the reason is not
        // something a viewer could otherwise work out: this is another organisation's work, shown here.
        <div
          style={{
            marginTop: 10,
            padding: "8px 11px",
            borderRadius: "var(--radius-sm)",
            border: "var(--border-w) solid var(--border)",
            background: "var(--surface-2)",
            fontSize: 12.5,
            color: "var(--text-muted)",
          }}
        >
          Fed to you by <strong style={{ color: "var(--text)" }}>{fedFrom.organisationName}</strong> from
          &ldquo;{fedFrom.dialName}&rdquo;. You are watching it — it counts towards their dial, not yours,
          and only they can change it.
        </div>
      )}

      {/* KEY-461: the status chip below is a read-only rollup on an itemised action point — the only way
          to change it is to update the items behind it, which isn't obvious from a chip that looks like
          every other (interactive) status control elsewhere on the panel. */}
      {!actsSimple && (
        <p style={{ margin: "16px 0 0", fontSize: 13, lineHeight: 1.5, color: "var(--text-muted)" }}>
          This {noun.one}&rsquo;s status comes from its items — update them on the Items tab to change it.
        </p>
      )}

      {/* status + meta row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginTop: actsSimple ? 16 : 10 }}>
        {actsSimple ? (
          <StatusPicker statuses={segment.palette} current={segment.status} canSet={canSetHere} onPick={pickSegmentStatus} />
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: "var(--radius-sm)",
              border: `1px solid ${hexA(segment.colour, 0.35)}`,
              background: hexA(segment.colour, 0.12),
              color: segment.colour,
              fontWeight: 700,
              fontSize: 13.5,
            }}
            title={segment.band ? `${segment.done} of ${segment.total} ${itemWords.many} complete` : undefined}
          >
            <span style={{ width: 9, height: 9, borderRadius: 99, background: segment.colour }} />
            {/* KEY-485. The chip is coloured by whatever resolved this action point's status, so it names
                that too — a segment carrying a threshold ladder used to show the ladder's colour under the
                words "Some complete", which reads as the ladder having fired when it hasn't. Below the
                ladder's lowest rung nothing resolves on purpose; say so rather than borrowing the RAG
                band's wording for it. A segment with no rule at all keeps that wording, as before. */}
            {segment.displayStatus
              ? segment.displayStatus.name
              : segment.rollup?.belowFirstThreshold
                ? "Below the first threshold"
                : segment.band ? ragLabel(segment.band, itemWords) : `No ${itemWords.many}`}
          </span>
        )}
        {/* KEY-512: the chip above names the ONE thing that resolved this action point's colour, which on a
            multi-axis dial leaves every other axis unsaid. These dots say where each of them stands — the
            worst status its items sit at, the same reading the dial's own rollup axis gets. */}
        {segmentDots.length > 1 && (
          <span
            title={`The worst status each scoring axis sits at across this ${noun.one}'s items`}
            style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
          >
            <span aria-hidden style={{ fontSize: 12, fontWeight: 600, color: "var(--text-faint)" }}>Axes</span>
            <AxisDotRun dots={segmentDots} size={9} label="Worst per axis" />
          </span>
        )}
        {actsSimple && <TrendChip trend={segmentTrend(history.filter((h) => h.sectionKey === section.key && h.segmentKey === segment.key), segment.palette)} />}
        {actsSimple && (
          <ReviewControl
            segment={segment}
            statusName={statusName}
            canEdit={!!segment.status && canSetHere(segment.status)}
            dialLadder={vm.detail.reviewReminderLeadDays ?? null}
            channels={myChannels}
            onSchedule={onScheduleRevert}
          />
        )}
        {/* KEY-916: who has this action point, or the button to hand it out. */}
        {allocationUi && (apAssignment
          ? (
            <AllocationBadge
              instanceId={instanceId}
              assignment={apAssignment}
              access={allocation}
              onChanged={onRefresh}
              onReassign={() => setAllocating({ scope: apScope, nodeName: apName, mode: "allocate", statuses: actsSimple ? segment.palette : undefined })}
            />
          )
          : allocation?.canAllocate(apScope) && (
            <AllocateButton onClick={() => setAllocating({ scope: apScope, nodeName: apName, mode: "allocate", statuses: actsSimple ? segment.palette : undefined })} />
          ))}
        {allocating && (
          <AllocateSheet
            instanceId={instanceId}
            detail={vm.detail}
            scope={allocating.scope}
            nodeName={allocating.nodeName}
            statuses={allocating.statuses}
            mode={allocating.mode}
            handOnStatusName={allocating.handOnStatusName}
            onHandOn={(handOnTo) => {
              const send = allocating.onHandOn;
              setAllocating(null);
              send?.(handOnTo);
            }}
            onClose={() => setAllocating(null)}
            onAllocated={onRefresh}
          />
        )}
        <div style={{ marginLeft: "auto" }}>
          <SubscribeButton
            instanceId={instanceId}
            scope={{ sectionKey: section.key, segmentKey: segment.key }}
            subs={subs}
            onChanged={onSubsChanged}
            scopeLabel={`this ${noun.one}`}
            meHasPhoneNumber={meHasPhoneNumber}
            helpAnchorId={HELP_ANCHORS.apSubscribe}
          />
        </div>
      </div>

      {actsSimple && pendingStatus && pendingMissing.length > 0 && (
        <PendingGateNotice
          statusName={pendingStatus.name}
          missing={pendingMissing}
          onCancel={() => setPendingStatusKey(null)}
          style={{ marginTop: 12 }}
        />
      )}

      {actsSimple && (pendingNeedsReviewDate || pendingNeedsComment) && pendingStatus && pendingStatusKey && (
        <GateHighlight demanded statusName={pendingStatus.name} style={{ marginTop: 12 }}>
          <PendingStatusPrompt
            key={pendingStatusKey}
            statusName={pendingStatus.name}
            dateWord={pendingNeedsReviewDate ? "a review date" : null}
            needsComment={pendingNeedsComment}
            onCancel={() => setPendingStatusKey(null)}
            onCommit={(review) => {
              const statusKey = pendingStatusKey;
              setPendingStatusKey(null);
              onSetSegmentStatus(statusKey, review);
            }}
            renderDate={(onChosen) => (
              <ReviewSchedule
                reviewDate={null}
                revertTo={null}
                overdue={false}
                palette={segment.palette}
                statusName={statusName}
                canEdit
                nodeKey={`${segment.key}|required`}
                nodeLabel="required review date"
                forceOpen
                onSchedule={(reviewDate, revertToStatusKey) => onChosen(reviewDate, revertToStatusKey)}
              />
            )}
          />
        </GateHighlight>
      )}

      {/* tabs */}
      <TabStrip<TabId>
        items={shownTabs}
        active={activeTab}
        onSelect={setTab}
        variant="underline"
        ariaLabel={`${segment.name} panes`}
        style={{ marginTop: 20 }}
        {...stripDisplay}
      />

      <div style={{ paddingTop: 18 }}>
        {activeTab === "overview" && compareOverview}
        {activeTab === "overview" && !compareOverview && (
          <OverviewTab segment={segment}>
            <OverviewChecklist
              model={buildOverviewChecklist({
                segmentDecls: attributeGroups.segDecls,
                placedDecls: attributeGroups.placedHere,
                segmentValueOf: segmentAttrValueOf,
                dialValueOf: (attrKey) =>
                  (vm.detail.nodeAttributeValues ?? []).find((v) => v.nodeType === "Dial" && v.attributeKey === attrKey)?.value ?? "",
                canEditAttributes,
                access: attributeAccess,
                files: attributeFiles,
                filesReliable,
                scope: { sectionKey: section.key, segmentKey: segment.key },
                neededFirstKeys: new Set(missingRequired.map((m) => m.attributeKey)),
                demandedKeys: segmentGate?.demandedKeys ?? new Set<string>(),
              })}
              frame={overviewFrame}
              noun={noun}
              statusName={segment.status?.name}
              pendingStatusName={pendingStatus?.name}
              simple={actsSimple}
              onJump={showInfoTab ? jumpToField : undefined}
            />
          </OverviewTab>
        )}
        {activeTab === "info" && showInfoTab && (
          <NodeAttributesBlock
            detail={vm.detail}
            sectionKey={section.key}
            segmentKey={segment.key}
            canEdit={canEditAttributes}
            access={attributeAccess}
            onSet={onSetNodeAttribute}
            files={attributeFiles}
            segmentGate={segmentGate}
            groups={attributeGroups}
            focusField={focusField}
            onFocused={clearFocusField}
            onOpenSection={onOpenSection}
          />
        )}
        {activeTab === "items" && !vm.simple && itemsLocked && (
          <RequirementLock
            noun={noun}
            missing={missingRequired}
            canFill={canFillRequired}
            onOpenInfo={showInfoTab ? () => setTab("info") : undefined}
          />
        )}
        {activeTab === "items" && !vm.simple && !itemsLocked && (
          <ItemsList
            segment={segment}
            dimensions={vm.dimensions}
            sectionKey={section.key}
            itemStatusIndex={itemStatusIndex}
            canSet={canSetItem}
            allocationUi={allocationUi}
            sectionLabel={section.label}
            onSetItemStatus={onSetItemStatus}
            onScheduleItemRevert={onScheduleItemRevert}
            dialLadder={vm.detail.reviewReminderLeadDays ?? null}
            channels={myChannels}
            detail={vm.detail}
            canEditAttributes={canEditAttributes}
            attributeAccess={attributeAccess}
            onSetNodeAttribute={onSetNodeAttribute}
            attributeFiles={attributeFiles}
            itemEdit={itemEdit}
            appointmentEdit={appointmentEdit}
            onOpenSegment={onOpenSegment}
            docs={evidence}
            itemHistory={itemHistory}
            instanceId={instanceId}
            canEditDocs={canEditDocs}
            onCreateDocumentTag={onCreateDocumentTag}
            canManageDocVersions={canManageDocVersions}
            documentsEnabled={caps.documents}
            onDocsChanged={reload}
            uploadAutomation={uploadAutomation}
            messagesEnabled={caps.messages}
            meUserId={meUserId}
            canPost={canPost}
            canModerate={canModerate}
            restricted={restricted}
          />
        )}
        {activeTab === "guidance" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Prose text={segment.guidance ?? ""} fallback="No guidance added yet." muted />
            <GuidanceVideos videos={segment.videos} />
            <GuidanceDocuments documents={segment.documents} />
            <ExternalResources links={segment.links} />
          </div>
        )}
        {activeTab === "discussion" &&
          (caps.messages ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <ConversationThread
                noun={noun}
                instanceId={instanceId}
                sectionKey={section.key}
                segmentKey={segment.key}
                meUserId={meUserId}
                canPost={canPost}
                canModerate={canModerate}
                restricted={restricted}
                exportTitle={transcriptTitle(vm.detail, section.key, segment.key)}
              />
              {!actsSimple && (
                <ItemDiscussionsTranscript
                  noun={noun}
                  meUserId={meUserId}
                  instanceId={instanceId}
                  sectionKey={section.key}
                  segmentKey={segment.key}
                  itemNameByKey={itemNameByKey}
                />
              )}
            </div>
          ) : (
            <DisabledTab label="Discussion isn’t available in preview." />
          ))}
        {activeTab === "files" &&
          (caps.documents ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {uploadAutomation && uploadAutomation.simple && (
                <SegmentAutomationControl
                  controls={uploadAutomation}
                  noun={`this ${noun.one}`}
                  toggleTitle={`Switch auto-status-on-upload for this ${noun.one}`}
                  onLabel="On"
                  offLabel="Off"
                  style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface-inset)" }}
                />
              )}
              <Documents
                docs={filesTabDocs}
                canEdit={canEditDocs}
                tags={vm.detail.documentTags ?? []}
                onCreateTag={onCreateDocumentTag}
                canManageVersions={canManageDocVersions}
                instanceId={instanceId}
                sectionKey={section.key}
                segmentKey={segment.key}
                onChanged={reload}
                uploadLabel={`Upload a file to this ${noun.one}`}
                attribution={actsSimple ? undefined : (d) => (
                  <>
                    <FileAttributionChip label={d.itemKey ? (itemNameByKey.get(d.itemKey) ?? "Item") : noun.One} kind={d.itemKey ? "item" : "node"} />
                    {/* KEY-461 decision 4: an attribute-backed file also shows next to its field on the
                        Info tab — this chip is additive, not a replacement for that. */}
                    {d.attributeKey && (
                      <FileAttributionChip
                        label={(() => {
                          const fieldLabel = attributeLabelFor(vm.detail.nodeAttributes ?? [], d.attributeKey);
                          return fieldLabel ? `Field · ${fieldLabel}` : "Field";
                        })()}
                        kind="field"
                      />
                    )}
                  </>
                )}
              />
            </div>
          ) : (
            <DisabledTab label="File attachments aren’t available in preview." />
          ))}
        {/* KEY-464: the same feed the dial and the section show, narrowed to this action point — so a field
            change here appears alongside the status changes it explains, which it never used to. */}
        {activeTab === "activity" && (
          <ActivityPanel instanceId={instanceId} sectionKey={section.key} segmentKey={segment.key} card={false} noun={noun} />
        )}
        {activeTab === "changes" && compareChanges}
      </div>
    </div>
  );
}

// ---- Trend (KEY-161): direction of the most recent status change, derived from history ----

function segmentTrend(events: SegmentStatusEvent[], palette: GeneratedStatus[]): "up" | "down" | "flat" | null {
  // Events are newest-first. A gate-skipped auto-set (KEY-278) records no movement, so it would read as
  // "Steady" and mask the direction of the last real change — take the newest change that actually moved.
  const changes = events.filter((e) => e.source !== "AutomationSkipped");
  if (changes.length === 0) return null;
  const orderOf = (k?: string | null) => palette.find((s) => s.key === k)?.order;
  const to = orderOf(changes[0].toStatusKey);
  const from = orderOf(changes[0].fromStatusKey);
  if (to == null || from == null) return null;
  return to > from ? "up" : to < from ? "down" : "flat";
}

function TrendChip({ trend }: { trend: "up" | "down" | "flat" | null }) {
  if (!trend) return null;
  const cfg = {
    up: { icon: "▲", label: "Improving", color: "#2f8a28" },
    down: { icon: "▼", label: "Declining", color: "#c11118" },
    flat: { icon: "■", label: "Steady", color: "var(--text-faint)" },
  }[trend];
  return (
    <span title="Direction of the latest status change" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: cfg.color }}>
      <span style={{ fontSize: 10 }}>{cfg.icon}</span>{cfg.label}
    </span>
  );
}

// ---- Node attributes (KEY-161): declared metadata fields + their per-node values ----

const attrLabel: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--text-faint)",
  marginBottom: 5,
};

/**
 * An attribute field's label, plus its author-written guidance (KEY-288) as subtext beneath — what should
 * be entered here, and why. One component behind every label site (Text/Date/RichText, User, File) so the
 * note appears for every attribute kind, including the read-only branch, without extra plumbing per type.
 *
 * This has to live in the label, not in the block that renders the field list: a viewer whose access rules
 * out seeing this attribute never reaches a label call at all (KEY-299's filtering happens above, per
 * declaration), so hiding the note falls out for free instead of needing its own check.
 */
function AttributeLabel({ decl }: { decl: GeneratedNodeAttribute }) {
  return (
    <div>
      <div style={attrLabel}>{decl.label}</div>
      {richTextHasContent(decl.guidance) && (
        <RichText value={decl.guidance} style={{ marginBottom: 5, fontSize: 12, color: "var(--text-faint)", fontStyle: "italic" }} />
      )}
    </div>
  );
}

/**
 * The line under a fed field saying where its answer came from (KEY-483) — "From Site Safety · Owner".
 *
 * It is the whole explanation for why the field cannot be typed in, so it is shown whenever the field is
 * fed, not only when there is an answer: a blank box with no note reads as "nobody has filled this in",
 * which is a different (and wrong) thing to tell somebody. When the child this group named is no longer
 * allocated there is no name to give, and the note says only that much rather than inventing one — the
 * admin editor is where that stale reference gets fixed.
 */
function AttributeProvenance({ from }: { from?: string | null }) {
  return (
    <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-faint)", fontStyle: "italic" }}>
      {from ? `From ${from}` : "From a subdial that is no longer part of this group"}
    </div>
  );
}

const attrInput: CSSProperties = {
  padding: "7px 10px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 13.5,
  width: "100%",
  boxSizing: "border-box",
};

/** The small role/state tag beside a name on a User attribute (KEY-267). */
const attrChip: CSSProperties = {
  padding: "1px 7px",
  borderRadius: 999,
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2, var(--surface))",
  color: "var(--text-muted)",
  fontSize: 11.5,
  fontWeight: 600,
  whiteSpace: "nowrap",
};

/** One titled group of attribute fields — a segment's "Details" or a section's "Section details". */
function AttributeGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 10 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </div>
  );
}

/**
 * The Section-target attributes that should render on this section: declared, in scope for it (KEY-269),
 * and visible to this viewer (KEY-299). One definition shared by both places section fields are drawn.
 */
function visibleSectionDecls(
  detail: InstanceDetail,
  sectionKey: string,
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean },
): GeneratedNodeAttribute[] {
  return (detail.nodeAttributes ?? []).filter((d) =>
    d.target === "Section"
    && (access?.(d).see ?? true)
    && attributeScopeApplies(d.target, d.appliesToKeys, sectionKey, "", ""));
}

/**
 * The dial-wide attributes (KEY-434) the author has ALSO placed on this node (KEY-378), visible to this
 * viewer (KEY-299). Pass `""` for the levels below the node being asked about — a section asks for
 * `(…, sectionKey, "", "")`, an action point `(…, sectionKey, segmentKey, "")`, an item all three.
 *
 * These are not the node's own fields. There is exactly one value per dial behind them, so the same
 * answer appears on every node it is placed on, and the node keys never reach the value at all.
 */
function placedDialDecls(
  detail: InstanceDetail,
  access: ((decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean }) | undefined,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
): GeneratedNodeAttribute[] {
  return (detail.nodeAttributes ?? [])
    .filter((d) =>
      d.target === "Dial"
      && (access?.(d).see ?? true)
      && attributeShownAt(d.shownAtKeys, sectionKey, segmentKey, itemKey))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * The placed dial-wide fields as their own titled group (KEY-378), on whichever node they were placed.
 *
 * The title says what the fields are, because the whole hazard of this feature is a field that looks
 * local: editing it here changes the same answer on every other node it appears on, and on the dial's own
 * details. Identical to {@link DialAttributesBlock}'s rendering by design — the value carries no key path
 * at all, so both read the same cell and both write `{ target: "Dial", sectionKey: "" }`. That is what
 * makes "the same value wherever it's displayed" true by construction rather than by any syncing.
 *
 * No file context is passed: the file-backed kinds can't be placed (their values name documents validated
 * against one exact node, and a dial-wide field has none), and the server drops a placement on them. No
 * pairing notes either — a pairing reads within one level, and both halves of a dial-level pair already
 * show together on the dial's own details.
 */
function PlacedDialAttributes({ detail, decls, canEdit, access, onSet, revealKeys }: {
  detail: InstanceDetail;
  decls: GeneratedNodeAttribute[];
  canEdit: boolean;
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  onSet: (request: SetNodeAttributeRequest) => void | Promise<void>;
  /** Keys of fields a row on Overview just sent someone to (KEY-569), whose groups should arrive open. */
  revealKeys?: ReadonlySet<string>;
}) {
  if (decls.length === 0) {
    return null;
  }
  const rowOf = (attrKey: string) =>
    (detail.nodeAttributeValues ?? []).find((v) => v.nodeType === "Dial" && v.attributeKey === attrKey);
  const valueOf = (attrKey: string) => rowOf(attrKey)?.value ?? "";
  const sourcedOf = (attrKey: string) => rowOf(attrKey)?.sourcedFromLabel ?? null;
  return (
    <AttributeGroup title={SHARED_FIELDS_TITLE}>
      <FieldsetRuns
        decls={decls}
        revealKeys={revealKeys}
        render={(d) => (
          // `data-attribute-field` is how a row on Overview finds this field (KEY-569).
          <div key={d.key} data-attribute-field={`Dial:${d.key}`}>
            <AttributeField
              noun={segmentNoun(detail)}
              decl={d}
              value={valueOf(d.key)}
              sourcedFrom={sourcedOf(d.key)}
              canEdit={canEdit && (access?.(d).edit ?? true)}
              scope={{ sectionKey: "", segmentKey: "" }}
              members={detail.members}
              people={detail.people}
              contacts={detail.contacts}
              responsible={responsibleContextOf(detail)}
              instanceId={detail.id}
              clearedFields={detail.clearedFields}
              suggestions={attributeValuesInUse(detail.nodeAttributeValues, d.key)}
              onSave={(value) => onSet({ target: "Dial", sectionKey: "", attributeKey: d.key, value })}
            />
          </div>
        )}
      />
    </AttributeGroup>
  );
}

/**
 * A section's own declared attributes and their values — the "Section details" group.
 *
 * Rendered in TWO places on purpose (KEY-279): inside every action point of the section (via
 * {@link NodeAttributesBlock}, where it has always lived) and standalone in the section view, which is
 * where someone who declared a Section attribute looks for it — and the only place it is reachable at all
 * on a section with no action points. Both surfaces go through this one component so the fields, their
 * scoping and their access rules have a single source of truth.
 *
 * `files` is the caller's already-loaded section documents. Omit it and the block loads its own; that
 * fetch is skipped entirely when the documents capability is off (the admin draft preview), so the
 * preview still makes no network call.
 */
export function SectionAttributesBlock({
  detail,
  sectionKey,
  canEdit,
  access,
  onSet,
  files,
  fileGrants,
  card = false,
  elsewhere,
}: {
  detail: InstanceDetail;
  sectionKey: string;
  canEdit: boolean;
  /** Per-attribute access (KEY-299): unseeable fields don't render, uneditable ones render read-only. */
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  onSet: (request: SetNodeAttributeRequest) => void | Promise<void>;
  /** The caller's loaded documents; absent = this block fetches the section's own. */
  files?: AttributeFilesContext;
  /** Only consulted when `files` is absent — the file grants a self-supplied context should carry.
   * Defaults to no file rights, which is the safe answer for a caller that didn't say. */
  fileGrants?: { canUpload: boolean; canManageVersions: boolean };
  /** Wrap the group in a panel card — how the standalone section-view surface presents it. */
  card?: boolean;
  /** KEY-681: set when this is the copy shown inside an action point rather than the
   * section itself. The section's details are one value per section, so they're edited in one place — here
   * they render read-only with a pointer back, and `onOpenSection` (when the host can navigate) makes it a link. */
  elsewhere?: { onOpenSection?: () => void };
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const caps = useCapabilities();
  const [ownDocs, setOwnDocs] = useState<DialDocument[]>([]);
  const instanceId = detail.id;
  const loadsOwnDocs = files === undefined && caps.documents;

  // The action point's loader, lifted: fetched per SECTION, not per segment, because a Section-target
  // file attribute's files carry no segment key. Skipped when the caller already holds the documents (so
  // an action point never fetches twice) and when files are off entirely.
  const reloadOwn = useCallback(async () => {
    if (!loadsOwnDocs) {
      return;
    }
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      setOwnDocs(await api.listDocuments(token, instanceId, sectionKey));
    } catch {
      // keep whatever we have; upload/download errors surface separately
    }
  }, [loadsOwnDocs, instanceId, sectionKey]);

  useEffect(() => {
    void reloadOwn();
  }, [reloadOwn]);

  const decls = visibleSectionDecls(detail, sectionKey, access);
  // KEY-378: dial-wide fields the author placed on this section render alongside the section's own.
  const placed = placedDialDecls(detail, access, sectionKey, "", "");
  if (decls.length === 0 && placed.length === 0) {
    return null;
  }

  const rowOf = (attrKey: string) =>
    (detail.nodeAttributeValues ?? []).find((v) =>
      v.nodeType === "Section" && v.attributeKey === attrKey && v.sectionKey === sectionKey
      && v.segmentKey === "" && v.itemKey === "");
  const valueOf = (attrKey: string) => rowOf(attrKey)?.value ?? "";
  const sourcedOf = (attrKey: string) => rowOf(attrKey)?.sourcedFromLabel ?? null;

  const filesContext: AttributeFilesContext = files ?? {
    instanceId,
    docs: ownDocs,
    canUpload: fileGrants?.canUpload ?? false,
    canManageVersions: fileGrants?.canManageVersions ?? false,
    enabled: caps.documents,
    onChanged: reloadOwn,
  };

  const group = (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {decls.length > 0 && (
        <AttributeGroup title="Section details">
          {elsewhere && (
            <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: -4 }}>
              These belong to the whole section, so they're changed there.{" "}
              {elsewhere.onOpenSection && (
                <button type="button" onClick={elsewhere.onOpenSection}
                  style={{ background: "none", border: 0, padding: 0, font: "inherit", color: "var(--accent-text)", cursor: "pointer", textDecoration: "underline" }}>
                  Open the section
                </button>
              )}
            </div>
          )}
          <FieldsetRuns
            decls={decls}
            render={(d) => (
              <AttributeField
                noun={segmentNoun(detail, sectionKey)}
                key={d.key}
                decl={d}
                value={valueOf(d.key)}
                sourcedFrom={sourcedOf(d.key)}
                canEdit={!elsewhere && canEdit && (access?.(d).edit ?? true)}
                scope={{ sectionKey, segmentKey: "" }}
                files={filesContext}
                members={detail.members}
              people={detail.people}
              contacts={detail.contacts}
              responsible={responsibleContextOf(detail)}
              contactPrefill={contactPrefillFor(d, decls, valueOf, detail.contacts)}
              instanceId={detail.id}
              clearedFields={detail.clearedFields}
                suggestions={attributeValuesInUse(detail.nodeAttributeValues, d.key)}
                onSave={(value) => onSet({ target: "Section", sectionKey, attributeKey: d.key, value })}
              />
            )}
          />
          <AttributePairingNotes decls={decls} valueOf={valueOf} />
        </AttributeGroup>
      )}
      <PlacedDialAttributes detail={detail} decls={placed} canEdit={canEdit} access={access} onSet={onSet} />
    </div>
  );

  return card ? <div className="kd-card" style={{ padding: "var(--pad-card)" }}>{group}</div> : group;
}

/** The dial's own field declarations this viewer may see, in author order. */
function dialAttributeDecls(
  detail: InstanceDetail,
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean },
): GeneratedNodeAttribute[] {
  return (detail.nodeAttributes ?? [])
    .filter((d) => d.target === "Dial" && (access?.(d).see ?? true))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Does this dial declare any of its own fields the viewer may see? The host needs to know *before* it
 * renders {@link DialAttributesBlock}, because that block draws nothing when there are none — and an
 * About tab whose only content is nothing is worse than no About tab at all.
 */
export function hasDialAttributes(
  detail: InstanceDetail,
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean },
): boolean {
  return dialAttributeDecls(detail, access).length > 0;
}

/**
 * The dial's own attributes (KEY-434) — facts about the whole instance rather than any part of it, such
 * as who to ring in an emergency. Rendered wherever the host puts the dial's own details, because there
 * is no node to hang them on.
 *
 * A field the dial cannot be opened without is captured before the dial ever renders, so by the time this
 * block is seen it is showing an answered field. That is the point: the values stay ordinary attributes,
 * editable here like any other, rather than something you can only ever set once.
 */
export function DialAttributesBlock({ detail, canEdit, access, onSet, card = false }: {
  detail: InstanceDetail;
  canEdit: boolean;
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  onSet: (request: SetNodeAttributeRequest) => void | Promise<void>;
  card?: boolean;
}) {
  const decls = dialAttributeDecls(detail, access);
  if (decls.length === 0) {
    return null;
  }

  // A dial-level value carries no key path at all — all three levels are empty strings.
  const rowOf = (attrKey: string) =>
    (detail.nodeAttributeValues ?? []).find((v) => v.nodeType === "Dial" && v.attributeKey === attrKey);
  const valueOf = (attrKey: string) => rowOf(attrKey)?.value ?? "";
  const sourcedOf = (attrKey: string) => rowOf(attrKey)?.sourcedFromLabel ?? null;

  const group = (
    <AttributeGroup title="About this dial">
      <FieldsetRuns
        decls={decls}
        render={(d) => (
          <AttributeField
            noun={segmentNoun(detail)}
            key={d.key}
            decl={d}
            value={valueOf(d.key)}
            sourcedFrom={sourcedOf(d.key)}
            canEdit={canEdit && (access?.(d).edit ?? true)}
            scope={{ sectionKey: "", segmentKey: "" }}
            members={detail.members}
              people={detail.people}
              contacts={detail.contacts}
              responsible={responsibleContextOf(detail)}
              contactPrefill={contactPrefillFor(d, decls, valueOf, detail.contacts)}
              instanceId={detail.id}
              clearedFields={detail.clearedFields}
            suggestions={attributeValuesInUse(detail.nodeAttributeValues, d.key)}
            onSave={(value) => onSet({ target: "Dial", sectionKey: "", attributeKey: d.key, value })}
          />
        )}
      />
      <AttributePairingNotes decls={decls} valueOf={valueOf} />
    </AttributeGroup>
  );

  return card ? <div className="kd-card" style={{ padding: "var(--pad-card)" }}>{group}</div> : group;
}

/**
 * The four groups {@link NodeAttributesBlock} draws, resolved in one place so the Info tab (KEY-485) can
 * ask whether there is anything to draw *before* it offers itself. Previously the block simply returned
 * null when empty, which is enough for a sibling but not for a tab: a tab that opens onto nothing is
 * worse than a tab that was never there.
 */
function nodeAttributeGroups(
  detail: InstanceDetail,
  sectionKey: string,
  segmentKey: string,
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean },
  segmentGate?: SegmentGateContext,
) {
  const valueOf = (attrKey: string) =>
    (detail.nodeAttributeValues ?? []).find((v) =>
      v.nodeType === "Segment" && v.attributeKey === attrKey && v.sectionKey === sectionKey
      && v.segmentKey === segmentKey && v.itemKey === "")?.value ?? "";

  // A Segment attribute scoped to specific nodes only renders on those nodes (KEY-269); an empty/absent
  // scope = all. On top of that, a gate-backed Segment field stays hidden while it's empty — it only
  // surfaces when a status pick demands it, or once it carries a value (KEY-248 guided fill). Section
  // fields are never gate-enforced, so they always show.
  const segDecls = (detail.nodeAttributes ?? []).filter((d) =>
    d.target === "Segment"
    && (access?.(d).see ?? true)
    && attributeScopeApplies(d.target, d.appliesToKeys, sectionKey, segmentKey, "")
    && (!segmentGate
      || segmentGate.demandedKeys.has(d.key)
      || !segmentGate.gateKeySet.has(d.key)
      || gateValueFilled(valueOf(d.key))));
  // KEY-378: dial-wide fields placed on this action point. The section block carries the ones placed on
  // the section, so this only asks about the segment.
  const placedHere = placedDialDecls(detail, access, sectionKey, segmentKey, "");
  const sectionDecls = visibleSectionDecls(detail, sectionKey, access);
  const placedOnSection = placedDialDecls(detail, access, sectionKey, "", "");

  return {
    segDecls,
    placedHere,
    any: segDecls.length > 0 || placedHere.length > 0 || sectionDecls.length > 0 || placedOnSection.length > 0,
  };
}

function NodeAttributesBlock({ detail, sectionKey, segmentKey, canEdit, onSet, files, segmentGate, access, groups, focusField, onFocused, onOpenSection }: {
  detail: InstanceDetail;
  sectionKey: string;
  segmentKey: string;
  canEdit: boolean;
  /** Per-attribute access (KEY-299): unseeable fields don't render, uneditable ones render read-only. */
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  onSet: (request: SetNodeAttributeRequest) => void | Promise<void>;
  files: AttributeFilesContext;
  /** Present only when this action point carries its own status: hides its palette's gate-backed fields
   * while empty, and highlights the ones a parked status pick is waiting on. */
  segmentGate?: SegmentGateContext;
  /** What {@link nodeAttributeGroups} resolved for this render — the same object the Overview list is built
   * from (KEY-569), so the two can't disagree about which fields exist here. */
  groups: ReturnType<typeof nodeAttributeGroups>;
  /** `Segment:key` / `Dial:key` of a field to bring into view on arrival, from a row on Overview (KEY-569). */
  focusField?: string | null;
  /** Called once that field has been brought into view, so it isn't done again on the next render. */
  onFocused?: () => void;
  /** Navigate to this action point's section, where its Section details are edited (KEY-681). */
  onOpenSection?: () => void;
}) {
  const rowOf = (attrKey: string) =>
    (detail.nodeAttributeValues ?? []).find((v) =>
      v.nodeType === "Segment" && v.attributeKey === attrKey && v.sectionKey === sectionKey
      && v.segmentKey === segmentKey && v.itemKey === "");
  const valueOf = (attrKey: string) => rowOf(attrKey)?.value ?? "";
  const sourcedOf = (attrKey: string) => rowOf(attrKey)?.sourcedFromLabel ?? null;

  const { segDecls, placedHere, any } = groups;
  const rootRef = useRef<HTMLDivElement>(null);
  // KEY-569: bring the field a row on Overview pointed at into view, with the cursor in it. Its group was
  // already mounted open (`revealKeys` below), because scrolling to an element inside a `display: none` card
  // does nothing. Found by walking the wrappers rather than a selector built from the key, which an author is
  // free to fill with quotes.
  useEffect(() => {
    if (!focusField) {
      return;
    }
    const target = Array.from(rootRef.current?.querySelectorAll<HTMLElement>("[data-attribute-field]") ?? [])
      .find((el) => el.dataset.attributeField === focusField);
    if (target) {
      target.scrollIntoView?.({ block: "center", behavior: "smooth" });
      const control = target.querySelector<HTMLElement>(
        "input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable='true']")
        ?? target.querySelector<HTMLElement>("button:not([disabled])");
      control?.focus({ preventScroll: true });
    }
    onFocused?.();
  }, [focusField, onFocused]);
  const revealFor = (target: "Segment" | "Dial") =>
    focusField?.startsWith(`${target}:`) ? new Set([focusField.slice(target.length + 1)]) : undefined;

  if (!any) {
    return null;
  }

  return (
    <div ref={rootRef} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {segDecls.length > 0 && (
        <AttributeGroup title="Details">
          <FieldsetRuns
            decls={segDecls}
            demandedKeys={segmentGate?.demandedKeys}
            revealKeys={revealFor("Segment")}
            render={(d) => (
              // `data-attribute-field` is how a row on Overview finds this field (KEY-569).
              <div key={d.key} data-attribute-field={`Segment:${d.key}`}>
                <GateHighlight
                  demanded={!!segmentGate?.demandedKeys.has(d.key)}
                  statusName={segmentGate?.pendingStatusName}
                >
                  <AttributeField
                    noun={segmentNoun(detail, sectionKey)}
                    decl={d}
                    value={valueOf(d.key)}
                    sourcedFrom={sourcedOf(d.key)}
                    canEdit={canEdit && (access?.(d).edit ?? true)}
                    scope={{ sectionKey, segmentKey }}
                    files={files}
                    members={detail.members}
              people={detail.people}
              contacts={detail.contacts}
              responsible={responsibleContextOf(detail)}
              contactPrefill={contactPrefillFor(d, segDecls, valueOf, detail.contacts)}
              instanceId={detail.id}
              clearedFields={detail.clearedFields}
                    suggestions={attributeValuesInUse(detail.nodeAttributeValues, d.key)}
                    onSave={(value) => onSet({ target: "Segment", sectionKey, segmentKey, attributeKey: d.key, value })}
                  />
                </GateHighlight>
              </div>
            )}
          />
          <AttributePairingNotes decls={segDecls} valueOf={valueOf} />
        </AttributeGroup>
      )}
      <PlacedDialAttributes detail={detail} decls={placedHere} canEdit={canEdit} access={access} onSet={onSet} revealKeys={revealFor("Dial")} />
      {/* The same block the section view renders standalone — here it keeps a section's fields within
          reach of the action point someone is already looking at, read-only (KEY-681): one value per
          section, edited on the section. `files` is passed, so it won't refetch. */}
      <SectionAttributesBlock
        detail={detail}
        sectionKey={sectionKey}
        canEdit={canEdit}
        access={access}
        onSet={onSet}
        files={files}
        elsewhere={{ onOpenSection }}
      />
    </div>
  );
}

/**
 * The advisory note for attributes the definition pairs (KEY-248): "Target date is filled in but Sign-off
 * isn't". Nothing is blocked — the pairing is symmetric, so refusing either field on its own would leave
 * neither settable first — this only points out the half-finished pair.
 */
function AttributePairingNotes({ decls, valueOf }: {
  decls: GeneratedNodeAttribute[];
  valueOf: (attrKey: string) => string;
}) {
  const warnings = nodeAttributePairingWarnings(decls, valueOf);
  if (warnings.length === 0) {
    return null;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {warnings.map((w) => (
        <div key={`${w.filledLabel}/${w.emptyLabel}`} style={{ fontSize: 12.5, color: "var(--st-amber, #b45309)" }}>
          {w.filledLabel} is filled in but {w.emptyLabel} isn’t — these two go together.
        </div>
      ))}
    </div>
  );
}

/** The HTML input type for a typed attribute kind. Email/Phone (KEY-434) get the right keyboard and
 * autofill; everything the browser might reject on submit is still validated by the server. */
function attributeInputType(type: string): string {
  if (type === "Date") return "date";
  if (type === "Email") return "email";
  if (type === "Phone") return "tel";
  return "text";
}

/**
 * KEY-LASTTIME: every attribute field, plus "last time" beneath an EMPTY one that something cleared — a
 * scheduled reset, the next round of a recurring item, a person. The detail says which fields those are;
 * the old value is fetched only when somebody opens it, rendered read-only through the very same field
 * component, and "Bring it back" is the ordinary save, so every rule the field has applies to it again.
 * Wrapped outside rather than inside the field because each kind returns early from its own branch.
 */
function AttributeField(props: Parameters<typeof AttributeFieldInner>[0] & {
  /** The dial these fields belong to; needed only to fetch a previous value. Absent in the admin preview. */
  instanceId?: string;
  /** KEY-LASTTIME: which empty fields were last cleared, from the detail. */
  clearedFields?: ClearedField[] | null;
}) {
  const { instanceId, clearedFields, ...field } = props;
  const nodeType = field.scope.itemKey !== undefined ? "Item" : field.scope.segmentKey ? "Segment" : field.scope.sectionKey ? "Section" : "Dial";
  const cleared = !field.value && instanceId
    ? (clearedFields ?? []).find((c) =>
      c.attributeKey === field.decl.key && c.nodeType === nodeType
      && c.sectionKey === (field.scope.sectionKey ?? "") && c.segmentKey === (field.scope.segmentKey ?? "") && c.itemKey === (field.scope.itemKey ?? ""))
    : undefined;
  return (
    <div>
      <AttributeFieldInner {...field} />
      {cleared && instanceId && <LastTime instanceId={instanceId} cleared={cleared} field={field} />}
    </div>
  );
}

/** "Cleared 6 Jan · last time ▸": the line under an emptied field, and the old value once opened. */
function LastTime({ instanceId, cleared, field }: {
  instanceId: string;
  cleared: ClearedField;
  field: Parameters<typeof AttributeFieldInner>[0];
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [open, setOpen] = useState(false);
  const [previous, setPrevious] = useState<PreviousAttributeValue | null | undefined>(undefined);
  const [restoring, setRestoring] = useState(false);
  const why = cleared.source === "ScheduledReset" ? "by a scheduled reset"
    : cleared.source === "NextRound" ? "for the next round"
    : cleared.source === "User" ? "by hand"
    : "";
  const load = async () => {
    setOpen(true);
    if (previous !== undefined) return;
    const token = getAccessToken();
    if (!token) { setPrevious(null); return; }
    try {
      setPrevious(await api.previousAttributeValue(token, instanceId, {
        target: cleared.nodeType, sectionKey: cleared.sectionKey, segmentKey: cleared.segmentKey, itemKey: cleared.itemKey, attributeKey: cleared.attributeKey,
      }));
    } catch {
      setPrevious(null);
    }
  };
  const bringBack = async () => {
    if (!previous || restoring) return;
    setRestoring(true);
    try {
      await field.onSave(previous.value);
    } finally {
      setRestoring(false);
    }
  };
  return (
    <div style={{ marginTop: 6 }}>
      <button
        type="button"
        className="kd-focusable"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : void load())}
        style={{ background: "none", border: 0, padding: 0, cursor: "pointer", fontSize: 12, color: "var(--text-muted)", textDecoration: "underline dotted" }}
      >
        Cleared {fmtDate(cleared.clearedAtUtc)}{why ? ` ${why}` : ""} · last time {open ? "▾" : "▸"}
      </button>
      {open && (
        <div style={{ marginTop: 6, padding: "10px 12px", borderRadius: 8, border: "var(--border-w) solid var(--border)", background: "var(--surface-2, transparent)" }}>
          {previous === undefined ? (
            <div style={{ fontSize: 12, color: "var(--text-faint)" }}>Loading…</div>
          ) : previous === null ? (
            <div style={{ fontSize: 12, color: "var(--text-faint)" }}>Nothing kept from last time.</div>
          ) : (
            <>
              <AttributeFieldInner {...field} value={previous.value} canEdit={false} />
              {field.canEdit && (
                <button
                  type="button"
                  disabled={restoring}
                  style={{ ...reviewPill, marginTop: 8, background: "var(--accent)", color: "var(--on-accent, #fff)", borderColor: "var(--accent)", opacity: restoring ? 0.6 : 1 }}
                  onClick={() => void bringBack()}
                >
                  {restoring ? "Bringing it back…" : "Bring it back"}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AttributeFieldInner({ decl, value, canEdit, onSave, scope, files, members, people, contacts, responsible, contactPrefill, suggestions, sourcedFrom, noun = DEFAULT_SEGMENT_NOUN }: {
  decl: GeneratedNodeAttribute;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void | Promise<void>;
  scope: AttributeNodeScope;
  files?: AttributeFilesContext;
  /** This instance's members — what a User attribute picks from. Absent in the admin draft preview. */
  members?: InstanceMember[];
  /** This dial's people list (KEY-PEOPLE) — what a Person attribute picks from. Absent in the admin draft preview. */
  people?: InstancePerson[] | null;
  /** This dial's contacts list (KEY-752) — what a Contact attribute picks from. Absent in the admin draft preview. */
  contacts?: InstanceContact[] | null;
  /** KEY-917: what a Responsible person field reads the answer above it from. Absent in the admin draft preview. */
  responsible?: ResponsibleContext;
  /** KEY-752: the address of the contact a "With" field on this node has chosen, when this field is the one
   *  it fills in and nothing is stored here yet (see `contactPrefillFor`). A display suggestion exactly like
   *  a declared default — shown unsaved with Save and Cancel — and preferred to one, being the more specific. */
  contactPrefill?: string | null;
  /** KEY-481: answers already given to this same field elsewhere on this dial. Offered by an OPEN list
   *  only, so the second person answering can reuse the first person's words. Ignored by every other
   *  type, and by a closed list, whose answers are the author's alone. */
  suggestions?: readonly string[];
  /** KEY-483: where a fed field's answer came from — "Site Safety · Owner" — ready to show. Null on an
   *  ordinary field, and also on a fed one whose child this group no longer allocates, which reads blank
   *  with no provenance to offer. Read-only-ness comes from the DECLARATION, never from this: a fed field
   *  with a dangling source must not quietly become editable. */
  sourcedFrom?: string | null;
  /** This dial's word for an action point (KEY-539). Only the file-attribute branch below renders it, but it
   *  arrives here because this is the component every block mounts. */
  noun?: SegmentNoun;
}) {
  // KEY-483: a field answered on a child dial is shown, never edited — the server refuses a write to it,
  // so an editable box here could only ever produce a save that fails or a value nobody would see. Taken
  // from the declaration rather than from the presence of a projected value, so a fed field whose child
  // has gone is still read-only rather than silently reverting to a box people can type in.
  const fed = !!decl.sourceChildDefinitionId;
  if (fed) {
    canEdit = false;
  }
  // KEY-286: the declaration may carry a suggested answer to show in an unanswered field. It is a DISPLAY
  // default only — nothing is stored until Save is pressed. That is what keeps the status gate honest: the
  // gate (server and statusGate.ts alike) reads stored values, and a default never becomes one, so it must
  // never be folded into the `valueOf` closures that feed it.
  const prefill = contactPrefill || (decl.defaultValue ?? "");
  // Once the field has been edited in this mount — including deliberately cleared — the prefill stops
  // re-applying, so "I don't want the suggestion" survives until the panel is reopened.
  const touched = useRef(false);
  const [draft, setDraft] = useState(value || prefill);
  useEffect(() => setDraft(value || (touched.current ? "" : prefill)), [value, prefill]);
  const edit = (next: string) => {
    touched.current = true;
    setDraft(next);
  };
  // In-flight save: the button says so, and a second click (or Enter) can't double-POST the value.
  const [saving, setSaving] = useState(false);
  // KEY-436: an authored group above this field must not fold away over a pending Save. Reported before
  // the early returns below so the hook runs on every path, and keyed off `touched` rather than the bare
  // draft/value comparison — a field merely SHOWING a suggested default (KEY-286) differs from its stored
  // value from the first render, and a group would then never honour "starts collapsed".
  useReportFieldsetDirty(decl.key, touched.current && draft !== value);
  const label = <AttributeLabel decl={decl} />;

  if (isFileAttributeType(decl.type)) {
    return <FileAttributeField decl={decl} value={value} canEdit={canEdit} onSave={onSave} scope={scope} files={files} noun={noun} />;
  }

  if (isUserAttributeType(decl.type)) {
    return <UserAttributeField decl={decl} value={value} canEdit={canEdit} onSave={onSave} members={members} live={files?.enabled === true} />;
  }

  if (isPersonAttributeType(decl.type)) {
    return <PersonAttributeField decl={decl} value={value} canEdit={canEdit} onSave={onSave} people={people} live={files?.enabled === true} />;
  }

  if (isContactAttributeType(decl.type)) {
    return <ContactAttributeField decl={decl} value={value} canEdit={canEdit} onSave={onSave} contacts={contacts} live={files?.enabled === true} />;
  }

  if (isResponsibleAttributeType(decl.type)) {
    return <ResponsibleAttributeField decl={decl} value={value} canEdit={canEdit} onSave={onSave} scope={scope} members={members} contacts={contacts} context={responsible} live={files?.enabled === true || !!responsible} />;
  }

  if (isListAttributeType(decl.type)) {
    return <ListAttributeField decl={decl} value={value} canEdit={canEdit} onSave={onSave} suggestions={suggestions} />;
  }

  if (isTableAttributeType(decl.type)) {
    return <TableAttributeField decl={decl} value={value} canEdit={canEdit} onSave={onSave} />;
  }

  if (!canEdit) {
    return (
      <div>
        {label}
        {decl.type === "RichText"
          ? <Prose text={value} fallback="—" muted />
          : <div style={{ fontSize: 14, color: value ? "var(--text)" : "var(--text-faint)" }}>{value ? (decl.type === "Date" ? fmtDate(value) : value) : "—"}</div>}
        {fed && <AttributeProvenance from={sourcedFrom} />}
      </div>
    );
  }

  // Dirty edits commit via an explicit Save (with Cancel) for every type — no surprise blur-saves,
  // and a deterministic affordance to drive/verify. A pre-filled but unsaved field is dirty on arrival,
  // which is exactly the affordance the default wants: "here is the suggested answer — press Save".
  const dirty = draft !== value;
  const commit = async () => {
    if (!dirty || saving) {
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };
  const saveRow = dirty && (
    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
      <button
        type="button"
        disabled={saving}
        style={{ ...reviewPill, background: "var(--accent)", color: "var(--on-accent, #fff)", borderColor: "var(--accent)", opacity: saving ? 0.6 : 1 }}
        onClick={() => void commit()}
      >
        {saving ? "Saving…" : "Save"}
      </button>
      <button type="button" disabled={saving} style={{ ...reviewPill, opacity: saving ? 0.6 : 1 }} onClick={() => edit(value)}>Cancel</button>
    </div>
  );

  if (decl.type === "RichText") {
    return (
      <div>
        {label}
        <RichTextEditor value={draft} onChange={edit} placeholder={`Add ${decl.label.toLowerCase()}…`} />
        {saveRow}
      </div>
    );
  }

  return (
    <div>
      {label}
      <input
        type={attributeInputType(decl.type)}
        inputMode={decl.type === "Phone" ? "tel" : undefined}
        autoComplete={decl.type === "Email" ? "email" : decl.type === "Phone" ? "tel" : undefined}
        value={draft}
        className="kd-focusable"
        placeholder={`Add ${decl.label.toLowerCase()}…`}
        style={attrInput}
        onChange={(e) => edit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void commit();
          if (e.key === "Escape" && !saving) edit(value);
        }}
      />
      {saveRow}
    </div>
  );
}

/**
 * A List attribute (KEY-481): one answer picked from a list the author wrote out.
 *
 * The value is the option text itself, so this changes how the field is EDITED and nothing about how it is
 * stored or read — a "Region" answered from a list is the same plain string a free-text "Region" was, only
 * one that can now be counted because everyone picked the same spelling of it.
 *
 * Two states earn their own treatment, both for the same reason a User field flags a revoked member rather
 * than blanking them. A value that is no longer one of the author's options is <b>kept and marked</b>:
 * lists get re-authored under running dials, validation happens on the way in and never on the way out, and
 * a field quietly emptying itself because somebody renamed an option would be silent data loss. And an
 * <b>open</b> list offers "Something else…", because that is the whole of what the author switched on —
 * with the answers already given elsewhere on this dial offered alongside the authored ones, so the second
 * person to hit the same cause reuses the first person's words instead of inventing a rival spelling.
 *
 * What a typed answer never does is join the declaration. That is version-owned template data licensed to
 * every organisation using the definition — see {@link listAttributeState}.
 */
function ListAttributeField({ decl, value, canEdit, onSave, suggestions }: {
  decl: GeneratedNodeAttribute;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void | Promise<void>;
  suggestions?: readonly string[];
}) {
  const { choices, current, offList, allowUserValues } = listAttributeState(decl, value, suggestions ?? []);
  // The "write your own" box, open only while someone is using it. Separate from the select so choosing an
  // existing answer stays one click, which is what the field is for.
  const [own, setOwn] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const offListNote = offList && (
    <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
      “{current}” is no longer one of the choices. It’s kept as it was
      {canEdit ? " — pick another answer to change it." : "."}
    </p>
  );

  if (!canEdit) {
    return (
      <div>
        <AttributeLabel decl={decl} />
        <div style={{ fontSize: 14, color: current ? "var(--text)" : "var(--text-faint)" }}>{current || "—"}</div>
        {offListNote}
      </div>
    );
  }

  const commit = async (next: string) => {
    if (saving) {
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setOwn(null);
    } finally {
      setSaving(false);
    }
  };

  // Options carry their POSITION, not their text. An open list accepts any word at all, so no text
  // sentinel is safe — "Something else…" is itself a perfectly legal answer somebody might have typed.
  // Positions are digits, so "+" can never be one, and "" keeps its usual meaning of "clear this".
  const ADD_YOUR_OWN = "+";
  const selected = own !== null ? ADD_YOUR_OWN : current ? String(choices.indexOf(current)) : "";

  return (
    <div>
      <AttributeLabel decl={decl} />
      <select
        className="kd-focusable"
        value={selected}
        aria-label={decl.label}
        disabled={saving}
        style={attrInput}
        onChange={(e) => {
          if (e.target.value === ADD_YOUR_OWN) {
            setOwn("");
            return;
          }
          setOwn(null);
          void commit(e.target.value === "" ? "" : choices[Number(e.target.value)] ?? "");
        }}
      >
        {/* Clearing is a real answer ("nobody has said yet"), so it stays first and always available. */}
        <option value="">— none —</option>
        {choices.map((choice, i) => (
          <option key={choice} value={String(i)}>{choice}</option>
        ))}
        {allowUserValues && <option value={ADD_YOUR_OWN}>Something else…</option>}
      </select>
      {choices.length === 0 && !allowUserValues && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
          This field has no answers to choose from yet. Ask whoever set the dial up to add some.
        </p>
      )}
      {own !== null && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <input
            type="text"
            value={own}
            className="kd-focusable"
            autoFocus
            placeholder={`Add ${decl.label.toLowerCase()}…`}
            style={{ ...attrInput, flex: "1 1 180px", width: "auto" }}
            onChange={(e) => setOwn(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && own.trim()) void commit(own.trim());
              if (e.key === "Escape" && !saving) setOwn(null);
            }}
          />
          <button
            type="button"
            disabled={saving || own.trim().length === 0}
            style={{ ...reviewPill, background: "var(--accent)", color: "var(--on-accent, #fff)", borderColor: "var(--accent)", opacity: saving || own.trim().length === 0 ? 0.6 : 1 }}
            onClick={() => void commit(own.trim())}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" disabled={saving} style={{ ...reviewPill, opacity: saving ? 0.6 : 1 }} onClick={() => setOwn(null)}>Cancel</button>
        </div>
      )}
      {offListNote}
    </div>
  );
}

const tableCellInput: CSSProperties = {
  ...attrInput,
  padding: "5px 8px",
  fontSize: 13,
};

/**
 * A Table attribute (KEY-568): rows of data added and removed against columns the author wrote out.
 *
 * The whole table is ONE stored value (NodeAttributeTableValue), so edits are staged in a local draft and
 * committed with a single Save/Cancel — exactly like a free-text field, and unlike a List's per-choice
 * commit — rather than writing on every keystroke: a row half-typed mid-edit is not a value anybody meant
 * to save, and a table gated `requiredAtFirstRun`/`requiredForNode` must not see it as "changed" until the
 * person is actually done.
 *
 * A value that no longer parses against the CURRENT columns — an admin re-authored them under a running
 * dial — is kept and flagged rather than blanked, the same treatment an off-list List value gets: whatever
 * was recorded stays recorded until somebody deliberately presses Save, which necessarily reshapes every
 * row to the live column count and clears the flag.
 */
function TableAttributeField({ decl, value, canEdit, onSave }: {
  decl: GeneratedNodeAttribute;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void | Promise<void>;
}) {
  const { columns, rows, malformed } = tableAttributeState(decl, value);
  // Mirrors AttributeField's own touched/draft pattern: once somebody edits a cell or adds/removes a row
  // in this mount, the draft stops re-following the saved value, so an in-flight edit survives a value prop
  // that hasn't changed yet.
  const touched = useRef(false);
  const [draft, setDraft] = useState<string[][]>(rows);
  useEffect(() => {
    if (!touched.current) setDraft(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decl.options]);
  const [saving, setSaving] = useState(false);

  if (!canEdit) {
    return (
      <div>
        <AttributeLabel decl={decl} />
        {columns.length === 0
          ? <div style={{ fontSize: 14, color: "var(--text-faint)" }}>—</div>
          : <TableAttributeGrid columns={columns} rows={rows} />}
        {malformed && (
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
            This table’s saved data no longer matches its columns. It’s kept as it was.
          </p>
        )}
      </div>
    );
  }

  const dirty = touched.current && JSON.stringify(draft) !== JSON.stringify(rows);
  const edit = (mutate: (rows: string[][]) => string[][]) => {
    touched.current = true;
    setDraft(mutate);
  };
  const setCell = (r: number, c: number, cell: string) =>
    edit((current) => current.map((row, x) => (x === r ? row.map((v, y) => (y === c ? cell : v)) : row)));
  const addRow = () => edit((current) => [...current, columns.map(() => "")]);
  const removeRow = (r: number) => edit((current) => current.filter((_, x) => x !== r));

  const commit = async () => {
    if (!dirty || saving) {
      return;
    }
    setSaving(true);
    try {
      await onSave(serialiseTableRows(draft));
      touched.current = false;
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => {
    touched.current = false;
    setDraft(rows);
  };

  return (
    <div>
      <AttributeLabel decl={decl} />
      {columns.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--st-amber, #b45309)" }}>
          This field has no columns yet. Ask whoever set the dial up to add some.
        </p>
      ) : (
        <>
          <TableAttributeGrid columns={columns} rows={draft} onCellChange={setCell} onRemoveRow={removeRow} disabled={saving} />
          <button
            type="button"
            className="kd-focusable"
            disabled={saving || draft.length >= ATTRIBUTE_TABLE_LIMITS.maxRows}
            style={{ ...reviewPill, marginTop: 8, opacity: saving || draft.length >= ATTRIBUTE_TABLE_LIMITS.maxRows ? 0.6 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}
            title={draft.length >= ATTRIBUTE_TABLE_LIMITS.maxRows ? `A table holds at most ${ATTRIBUTE_TABLE_LIMITS.maxRows} rows.` : undefined}
            onClick={addRow}
          >
            <Icon name="plus" size={13} /> Add a row
          </button>
        </>
      )}
      {malformed && !dirty && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
          This table’s saved data no longer matches its columns. It’s kept as it was — press Save once you’ve
          fixed the rows below to replace it.
        </p>
      )}
      {dirty && (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button
            type="button"
            disabled={saving}
            style={{ ...reviewPill, background: "var(--accent)", color: "var(--on-accent, #fff)", borderColor: "var(--accent)", opacity: saving ? 0.6 : 1 }}
            onClick={() => void commit()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" disabled={saving} style={{ ...reviewPill, opacity: saving ? 0.6 : 1 }} onClick={cancel}>Cancel</button>
        </div>
      )}
    </div>
  );
}

/** The grid a Table attribute draws, read-only or editable. Columns run left to right in author order; a
 *  row with no cells to show (`columns` empty) is never reached — the caller substitutes a note instead. */
function TableAttributeGrid({ columns, rows, onCellChange, onRemoveRow, disabled }: {
  columns: string[];
  rows: string[][];
  /** Both present together (editable) or both absent (read-only) — never one alone. */
  onCellChange?: (row: number, col: number, value: string) => void;
  onRemoveRow?: (row: number) => void;
  disabled?: boolean;
}) {
  const editable = !!onCellChange;
  const gridTemplateColumns = `repeat(${columns.length}, minmax(90px, 1fr))${editable ? " auto" : ""}`;

  return (
    <div style={{ border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns, minWidth: columns.length * 100 }}>
        {columns.map((col, c) => (
          <div key={c} style={{ ...tableHeaderCell, borderLeft: c === 0 ? "none" : "var(--border-w) solid var(--border)" }}>{col}</div>
        ))}
        {editable && <div style={{ ...tableHeaderCell, borderLeft: "var(--border-w) solid var(--border)" }} />}
        {rows.length === 0 ? (
          <div style={{ gridColumn: `1 / span ${columns.length + (editable ? 1 : 0)}`, padding: "10px 8px", fontSize: 12.5, color: "var(--text-faint)", borderTop: "var(--border-w) solid var(--border)" }}>
            No rows yet.
          </div>
        ) : (
          rows.map((row, r) => (
            <FragmentRow key={r}>
              {columns.map((_, c) => (
                <div key={c} style={{ borderTop: "var(--border-w) solid var(--border)", borderLeft: c === 0 ? "none" : "var(--border-w) solid var(--border)", padding: editable ? 4 : "8px" }}>
                  {editable ? (
                    <input
                      className="kd-focusable"
                      type="text"
                      value={row[c] ?? ""}
                      disabled={disabled}
                      maxLength={ATTRIBUTE_TABLE_LIMITS.maxCellLength}
                      style={tableCellInput}
                      onChange={(e) => onCellChange!(r, c, e.target.value)}
                    />
                  ) : (
                    <span style={{ fontSize: 13.5, color: row[c] ? "var(--text)" : "var(--text-faint)" }}>{row[c] || "—"}</span>
                  )}
                </div>
              ))}
              {editable && (
                <div style={{ borderTop: "var(--border-w) solid var(--border)", borderLeft: "var(--border-w) solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
                  <button type="button" disabled={disabled} title="Remove row" aria-label={`Remove row ${r + 1}`} style={{ ...iconBtn, color: "var(--st-overdue)" }} onClick={() => onRemoveRow!(r)}>
                    <Icon name="trash" size={13} />
                  </button>
                </div>
              )}
            </FragmentRow>
          ))
        )}
      </div>
    </div>
  );
}

/** A CSS-grid "row" is just consecutive cells — `display: contents` keeps each row's cells in the grid's
 *  own column tracks instead of nesting a row inside one grid cell. */
function FragmentRow({ children }: { children: ReactNode }) {
  return <div style={{ display: "contents" }}>{children}</div>;
}

const tableHeaderCell: CSSProperties = {
  padding: "7px 8px",
  fontSize: 11.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "var(--text-faint)",
  background: "var(--surface-2, var(--surface))",
};

/**
 * A User attribute (KEY-267): the value names one of this dial's own members rather than holding text.
 *
 * Everything it needs is already on the instance detail, so the picker costs no extra call and offers
 * exactly the people the server will accept. Two states earn their own treatment. A member whose grant has
 * since been revoked still shows — assignments deliberately outlive access, as every other authored record
 * does — flagged rather than blanked, because silently emptying a field nobody cleared reads as data loss.
 * And the admin draft preview has no instance behind it, so it says so instead of offering an empty picker.
 */
function UserAttributeField({ decl, value, canEdit, onSave, members, live }: {
  decl: GeneratedNodeAttribute;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void | Promise<void>;
  members?: InstanceMember[];
  live: boolean;
}) {
  const { userId, member, unresolved, options } = userAttributeState(value, members ?? []);
  const editable = canEdit && live;

  // Whoever is assigned keeps a row of their own even when the picker wouldn't offer them — an archived
  // account, or someone whose grant has gone. It's disabled (re-picking them is a no-op); what it buys is
  // a closed select that names the person instead of reading as empty, and a raw id that never surfaces.
  // (A resolved member missing from the picker can only be an archived one — those are the rows it drops.)
  const orphan = userId && !options.some((m) => m.userId === userId)
    ? member
      ? `${memberLabel(member)} · archived account`
      : "Someone who is no longer a member of this dial"
    : null;

  return (
    <div>
      <AttributeLabel decl={decl} />
      {editable ? (
        <select
          className="kd-focusable"
          value={userId ?? ""}
          aria-label={decl.label}
          style={attrInput}
          onChange={(e) => void onSave(e.target.value)}
        >
          {/* Clearing is a real answer ("nobody owns this yet"), so it stays first and always available. */}
          <option value="">— none —</option>
          {orphan && <option value={userId!} disabled>{orphan}</option>}
          {options.map((m) => (
            <option key={m.userId} value={m.userId}>
              {memberLabel(m)} · {m.role}
            </option>
          ))}
        </select>
      ) : member ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, color: "var(--text)" }}>{memberLabel(member)}</span>
          <span style={attrChip}>{member.role}</span>
          {member.archived && <span style={attrChip}>Archived account</span>}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: unresolved ? "var(--text-muted)" : "var(--text-faint)" }}>
          {unresolved ? "Someone who is no longer a member of this dial" : "—"}
        </div>
      )}
      {unresolved && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
          This was set before that person’s access to the dial was removed. It’s kept as it was —
          pick {editable ? "someone else to change it" : "a replacement once you have edit access"}.
        </p>
      )}
      {canEdit && !live && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          There are no dial members to choose from in preview — end users will pick one of this dial’s people here.
        </p>
      )}
      {editable && options.length === 0 && !unresolved && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          Nobody has been given access to this dial yet, so there is no one to pick.
        </p>
      )}
    </div>
  );
}

/**
 * A Person attribute (KEY-PEOPLE): the value names one or more entries in the dial's own people list —
 * Settings → Who's who — rather than a dial member, so a family's children, a home's residents or a class's
 * pupils can be named without any of them holding an account. Single-person fields are a select; a field
 * the author allowed more than one on is a row of tick boxes, saved on each change. As for a User field,
 * a value naming someone since removed from the list is kept and flagged rather than blanked.
 */
function PersonAttributeField({ decl, value, canEdit, onSave, people, live }: {
  decl: GeneratedNodeAttribute;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void | Promise<void>;
  people?: InstancePerson[] | null;
  live: boolean;
}) {
  const multiple = decl.allowMultiple === true;
  const { ids, resolved, unresolved, options } = personAttributeState(value, people);
  const editable = canEdit && live;
  const save = (next: string[]) => void onSave(serialisePersonAttributeValue(next, multiple));

  const names = resolved.map((r) => (r.person ? personLabel(r.person) : "Someone no longer on the list"));
  const readOnly = ids.length > 0
    ? (
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {resolved.map((r) => (
          <span key={r.id} style={{ ...attrChip, color: r.person ? undefined : "var(--text-muted)" }}>
            {r.person ? personLabel(r.person) : "Someone no longer on the list"}
          </span>
        ))}
      </div>
    )
    : <div style={{ fontSize: 14, color: "var(--text-faint)" }}>—</div>;

  return (
    <div>
      <AttributeLabel decl={decl} />
      {!editable ? readOnly : multiple ? (
        <div role="group" aria-label={decl.label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {options.map((p) => {
            const on = ids.some((id) => id.toLowerCase() === p.id.toLowerCase());
            return (
              <label key={p.id} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--text)" }}>
                <input
                  type="checkbox"
                  className="kd-focusable"
                  checked={on}
                  onChange={(e) => save(e.target.checked
                    ? [...ids.filter((id) => id.toLowerCase() !== p.id.toLowerCase()), p.id]
                    : ids.filter((id) => id.toLowerCase() !== p.id.toLowerCase()))}
                />
                {personLabel(p)}
              </label>
            );
          })}
          {/* An id naming nobody keeps a row so it can be un-ticked, and so the field never reads as empty. */}
          {resolved.filter((r) => !r.person).map((r) => (
            <label key={r.id} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--text-muted)" }}>
              <input type="checkbox" className="kd-focusable" checked onChange={() => save(ids.filter((id) => id !== r.id))} />
              Someone no longer on the list
            </label>
          ))}
        </div>
      ) : (
        <select
          className="kd-focusable"
          value={ids[0] ?? ""}
          aria-label={decl.label}
          style={attrInput}
          onChange={(e) => save(e.target.value ? [e.target.value] : [])}
        >
          <option value="">— none —</option>
          {ids[0] && !options.some((p) => p.id.toLowerCase() === ids[0].toLowerCase()) && (
            <option value={ids[0]} disabled>Someone no longer on the list</option>
          )}
          {options.map((p) => (
            <option key={p.id} value={p.id}>{personLabel(p)}</option>
          ))}
        </select>
      )}
      {unresolved && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
          {names.length > 1 ? "Someone here" : "This person"} has since been taken off the dial’s list. It’s kept as it was —
          {editable ? " pick someone else to change it." : " a dial admin can change it."}
        </p>
      )}
      {canEdit && !live && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          There is no people list to choose from in preview — end users will pick from the names their dial admin has added under Settings.
        </p>
      )}
      {editable && options.length === 0 && ids.length === 0 && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          Nobody is on this dial’s list yet. A dial admin adds names under Settings → Who’s who.
        </p>
      )}
    </div>
  );
}

/**
 * A Contact attribute (KEY-752): one entry from the dial's contacts list — "With: Smile Dental". Picked like a
 * single Person (the choice saves at once), and shown with what makes a contact worth saving: the address,
 * and a Call link for the number, an Email link for the address — no AI, just `tel:`/`mailto:`. A value
 * naming somebody since taken off the list is kept and flagged, never blanked. When the declaration names a
 * field it fills (`fillsAttributeKey`), that field is pre-filled from the chosen contact's address by the
 * node's own render (see `contactPrefillFor`), not from here: this component saves only itself.
 */
function ContactAttributeField({ decl, value, canEdit, onSave, contacts, live }: {
  decl: GeneratedNodeAttribute;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void | Promise<void>;
  contacts?: InstanceContact[] | null;
  live: boolean;
}) {
  const { id, contact, unresolved, options } = contactAttributeState(value, contacts);
  const editable = canEdit && live;
  const call = telHref(contact?.phone);
  const mail = mailtoHref(contact?.email);
  const linkStyle = { fontSize: 13, color: "var(--accent-text)", textDecoration: "underline" } as const;

  const card = contact && (contact.address || call || mail) ? (
    <div style={{ marginTop: editable ? 6 : 2, display: "flex", flexDirection: "column", gap: 4 }}>
      {contact.address && (
        <div style={{ fontSize: 13, color: "var(--text-muted)", whiteSpace: "pre-line" }}>{contact.address}</div>
      )}
      {(call || mail) && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {call && <a href={call} className="kd-focusable" style={linkStyle} aria-label={`Call ${contact.name} on ${contact.phone}`}>Call {contact.phone}</a>}
          {mail && <a href={mail} className="kd-focusable" style={linkStyle} aria-label={`Email ${contact.name}`}>Email</a>}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div>
      <AttributeLabel decl={decl} />
      {editable ? (
        <select
          className="kd-focusable"
          value={id ?? ""}
          aria-label={decl.label}
          style={attrInput}
          onChange={(e) => void onSave(e.target.value)}
        >
          <option value="">— none —</option>
          {id && unresolved && <option value={id} disabled>A contact no longer on the list</option>}
          {options.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      ) : id ? (
        <div style={{ fontSize: 14, color: contact ? "var(--text)" : "var(--text-muted)" }}>
          {contact ? contact.name : "A contact no longer on the list"}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: "var(--text-faint)" }}>—</div>
      )}
      {card}
      {unresolved && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
          This contact has since been taken off the dial’s list. It’s kept as it was —
          {editable ? " pick another to change it." : " a dial admin can change it."}
        </p>
      )}
      {canEdit && !live && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          There is no contacts list to choose from in preview — end users will pick from the contacts their dial admin has saved under Settings.
        </p>
      )}
      {editable && options.length === 0 && !id && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          No contacts are saved on this dial yet. A dial admin adds them under Settings → Contacts.
        </p>
      )}
    </div>
  );
}

/**
 * A Responsible person field (KEY-917): one dial member or one of the dial's contacts. While the node has no
 * answer of its own it shows the nearest one above it, greyed, with where it came from — the same rule the
 * server reads by. Picking a contact invites them (server-side, on save); the line under a contact says how
 * the last invite went and offers Re-send.
 */
function ResponsibleAttributeField({ decl, value, canEdit, onSave, scope, members, contacts, context, live }: {
  decl: GeneratedNodeAttribute;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void | Promise<void>;
  scope: AttributeNodeScope;
  members?: InstanceMember[];
  contacts?: InstanceContact[] | null;
  context?: ResponsibleContext;
  live: boolean;
}) {
  const { ref, name, unresolved, memberOptions, contactOptions } = responsibleState(value, members, contacts);
  const above = ref ? null : responsibleAbove(context, scope);
  const editable = canEdit && live;
  const current = ref ? formatResponsibleAttributeValue(ref) : "";
  const inheritedText = above ? `${above.name ?? "Somebody no longer on this dial"} · from ${above.from}` : null;

  return (
    <div>
      <AttributeLabel decl={decl} />
      {editable ? (
        <select
          className="kd-focusable"
          value={current}
          aria-label={decl.label}
          style={attrInput}
          onChange={(e) => void onSave(e.target.value)}
        >
          <option value="">{inheritedText ? `— ${inheritedText} —` : "— none —"}</option>
          {ref && unresolved && <option value={current} disabled>Somebody no longer on this dial</option>}
          {memberOptions.length > 0 && (
            <optgroup label="Members">
              {memberOptions.map((m) => (
                <option key={m.userId} value={formatResponsibleAttributeValue({ kind: "Member", id: m.userId.toLowerCase() })}>{memberLabel(m)}</option>
              ))}
            </optgroup>
          )}
          {contactOptions.length > 0 && (
            <optgroup label="Contacts">
              {contactOptions.map((c) => (
                <option key={c.id} value={formatResponsibleAttributeValue({ kind: "Contact", id: c.id.toLowerCase() })}>{c.name}</option>
              ))}
            </optgroup>
          )}
        </select>
      ) : ref ? (
        <div style={{ fontSize: 14, color: name ? "var(--text)" : "var(--text-muted)" }}>{name ?? "Somebody no longer on this dial"}</div>
      ) : inheritedText ? (
        <div style={{ fontSize: 14, color: "var(--text-muted)" }}>{inheritedText}</div>
      ) : (
        <div style={{ fontSize: 14, color: "var(--text-faint)" }}>—</div>
      )}
      {ref?.kind === "Contact" && !unresolved && context && (
        <ContactInviteLine instanceId={context.instanceId} contactId={ref.id} canResend={editable}
          hasEmail={!!(contacts ?? []).find((c) => c.id.toLowerCase() === ref.id)?.email} />
      )}
      {unresolved && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
          This person is no longer a member or on the dial’s contacts list. It’s kept as it was —
          {editable ? " pick someone else to change it." : " an editor can change it."}
        </p>
      )}
      {canEdit && !live && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          There is nobody to choose from in preview — end users pick from the dial’s members and contacts.
        </p>
      )}
    </div>
  );
}

const INVITE_OUTCOME_TEXT: Record<ContactInvite["outcome"], string> = {
  Sent: "Invite sent",
  EmailFailed: "Invite made, but the email didn’t go",
  NoEmail: "Not invited — this contact has no email address. Add one under Settings → Contacts, then re-send.",
  HasAccount: "Not invited — this address already has a KeyDials account. Add them as a member and pick them from Members.",
  Blocked: "Not invited — this dial doesn’t allow guests.",
  InvalidEmail: "Not invited — the contact’s email address doesn’t look right.",
};

/** How the last invite to a picked contact went, and Re-send (KEY-917). */
function ContactInviteLine({ instanceId, contactId, canResend, hasEmail }: {
  instanceId: string;
  contactId: string;
  canResend: boolean;
  hasEmail: boolean;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [last, setLast] = useState<ContactInvite | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!token) return;
    api.listContactInvites(token, instanceId, contactId)
      .then((rows) => { if (!cancelled) setLast(rows[0] ?? null); })
      .catch(() => { if (!cancelled) setLast(null); });
    return () => { cancelled = true; };
  }, [api, getAccessToken, instanceId, contactId]);

  async function resend() {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      setLast(await api.resendContactInvite(token, instanceId, contactId));
    } catch {
      setError("Couldn’t re-send just now — try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  if (last === undefined) return null;
  const when = last ? new Date(last.sentAtUtc).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;
  const ok = last?.outcome === "Sent";
  return (
    <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: 12 }}>
      <span style={{ color: !last || ok ? "var(--text-muted)" : "var(--st-amber, #b45309)" }}>
        {last ? `${INVITE_OUTCOME_TEXT[last.outcome]}${ok || last.outcome === "EmailFailed" ? ` · ${when}` : ""}` : "Not invited yet"}
      </span>
      {canResend && hasEmail && (
        <button type="button" className="kd-focusable" disabled={busy} onClick={() => void resend()}
          style={{ background: "none", border: "none", padding: 0, fontSize: 12, color: "var(--accent-text)", textDecoration: "underline", cursor: "pointer" }}>
          {busy ? "Sending…" : last ? "Re-send" : "Send invite"}
        </button>
      )}
      {error && <span style={{ color: "var(--st-red, #b91c1c)" }}>{error}</span>}
    </div>
  );
}

/**
 * A Document, Image or Video attribute (KEY-247). The value is a JSON reference to files uploaded through
 * the ordinary document pipeline — so these fields inherit its SAS upload, malware scanning and quarantine —
 * and the reference is written only after the bytes have landed, which is what lets the server insist every
 * id names a file already attached here.
 *
 * Editing needs Editor-or-above AND a live backend, not just the attribute-edit gate: the admin preview
 * runs on a mock backend, so it shows the field with an explanation rather than an upload that can only fail.
 */
function FileAttributeField({ decl, value, canEdit, onSave, scope, files, noun = DEFAULT_SEGMENT_NOUN }: {
  decl: GeneratedNodeAttribute;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void | Promise<void>;
  scope: AttributeNodeScope;
  files?: AttributeFilesContext;
  /** This dial's word for an action point (KEY-539), passed down by {@link AttributeField}. */
  noun?: SegmentNoun;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** KEY-938: a demo dial's own words for a refused upload or download — a notice, not a failure. */
  const [notice, setNotice] = useState<string | null>(null);
  const [linkDraft, setLinkDraft] = useState("");

  const isVideo = decl.type === "Video";
  const isImage = decl.type === "Image";
  const state = fileAttributeState(decl, value, scope, canEdit, files);
  const { attached, link, multiple, editable, disabledReason, atCapacity, unresolved } = state;

  async function attach(file: File) {
    const token = getAccessToken();
    if (!token || !files) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const ticket = await api.requestDocumentUpload(token, files.instanceId, {
        sectionKey: scope.sectionKey,
        segmentKey: scope.segmentKey,
        itemKey: scope.itemKey,
        attributeKey: decl.key,
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      });
      await api.uploadDocumentBytes(ticket.uploadUrl, file);
      try {
        await api.confirmDocumentUploaded(token, files.instanceId, ticket.documentId);
      } catch {
        /* the bytes landed; only the auto-status rule is skipped */
      }
      // The value has to follow the upload, not lead it: the server only accepts ids of files already here.
      await files.onChanged();
      await onSave(isVideo
        ? serialiseVideoAttributeValue({ kind: "Upload", documentId: ticket.documentId })
        : withAttachedDocument(state, ticket.documentId));
    } catch (e) {
      // KEY-938: a demo dial refuses the upload at the moment the file would have gone up, with its own
      // words (a 409). The person has seen the whole journey; nothing left their machine. Not an error.
      if (e instanceof ApiError && e.status === 409) {
        setNotice(e.message);
      } else {
        setError(e instanceof ApiError ? e.message : "Upload failed.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function detach(documentId: string) {
    const token = getAccessToken();
    if (!token || !files) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(isVideo ? "" : withoutDocument(state, documentId));
      // Deleting the file itself is Admin-only; for everyone else the reference simply goes.
      if (files.canManageVersions) {
        try {
          await api.deleteDocument(token, files.instanceId, documentId);
        } catch {
          setError("Removed from this field, but the file itself couldn’t be deleted.");
        }
      }
      await files.onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function download(documentId: string) {
    const token = getAccessToken();
    if (!token || !files) {
      return;
    }
    try {
      const { downloadUrl } = await api.getDocumentDownloadUrl(token, files.instanceId, documentId);
      window.open(downloadUrl, "_blank", "noopener");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setNotice(e.message); // KEY-938: a demo's documents are names only
      } else {
        setError("Could not get the download link.");
      }
    }
  }

  const rows = attached.map((d) => (
    <div key={d.id} style={attrFileRow}>
      {isImage && files
        ? <AttributeImageThumb instanceId={files.instanceId} doc={d} />
        : <span style={attrFileIcon}><Icon name="file" size={15} /></span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.fileName}</div>
        <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{d.uploadedByEmail} · {timeAgo(d.uploadedAtUtc)}</div>
      </div>
      <DocStatusBadge status={d.status} />
      {/* `!== false` on purpose: absent means an older server that never answered, and the pre-KEY-537
          behaviour there is to offer the download and let the server decide. */}
      {d.status === "Clean" && d.canDownload !== false && (
        <button type="button" onClick={() => void download(d.id)} title="Download" aria-label={`Download ${d.fileName}`} style={iconBtn}>
          <Icon name="download" size={15} />
        </button>
      )}
      {editable && (
        <button type="button" disabled={busy} onClick={() => void detach(d.id)} title="Remove" aria-label={`Remove ${d.fileName}`} style={{ ...iconBtn, color: "var(--st-overdue)" }}>
          <Icon name="trash" size={15} />
        </button>
      )}
    </div>
  ));

  const linkRow = link !== null && (
    <div style={attrFileRow}>
      <span style={attrFileIcon}><Icon name="link" size={15} /></span>
      <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--accent-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {link}
      </a>
      {editable && (
        <button type="button" disabled={busy} onClick={() => void onSave("")} title="Remove" aria-label={`Remove ${decl.label} link`} style={{ ...iconBtn, color: "var(--st-overdue)" }}>
          <Icon name="trash" size={15} />
        </button>
      )}
    </div>
  );

  const empty = rows.length === 0 && !linkRow && (
    <div style={{ fontSize: 13.5, color: "var(--text-faint)" }}>—</div>
  );

  // The player is an addition on top of the existing file row (name/status/download/remove stay put) —
  // it renders nothing until there's something playable (a Clean upload's SAS URL, or a classifiable link).
  const videoPreview = isVideo && files && (
    <AttributeVideoPlayer instanceId={files.instanceId} doc={attached[0]} link={link} title={decl.label} />
  );

  return (
    <div>
      <AttributeLabel decl={decl} />
      {error && <p style={{ margin: "0 0 6px", fontSize: 12, color: "var(--st-overdue)" }}>{error}</p>}
      {notice && <p role="status" style={{ margin: "0 0 6px", fontSize: 12, color: "var(--text-muted)" }}>{notice}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {videoPreview}
        {rows}
        {linkRow}
        {empty}
      </div>
      {unresolved > 0 && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--st-amber, #b45309)" }}>
          {unresolved === 1 ? "A file that was here is" : `${unresolved} files that were here are`} no longer
          available — quarantined after a failed malware scan, or deleted. Attach {multiple ? "another" : "a replacement"} below.
        </p>
      )}
      {disabledReason === "preview" && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          File uploads aren’t available in preview — end users will attach {isVideo ? "a video" : multiple ? "files" : "a file"} here.
        </p>
      )}
      {disabledReason === "no-upload-access" && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          You need edit access to this {noun.one} to attach a file.
        </p>
      )}
      {editable && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 10 }}>
          {!atCapacity && (
            <label className="kd-btn-dashed" style={{ cursor: busy ? "default" : "pointer" }}>
              <Icon name="upload" size={15} />
              {busy ? "Uploading…" : `Upload ${isVideo ? "video" : isImage ? "image" : "file"}`}
              <input
                type="file"
                accept={isImage ? "image/*" : isVideo ? "video/*" : undefined}
                style={{ display: "none" }}
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    void attach(f);
                  }
                  e.target.value = "";
                }}
              />
            </label>
          )}
          {isVideo && attached.length === 0 && link === null && (
            <>
              <input
                type="url"
                value={linkDraft}
                placeholder="…or paste a video link"
                aria-label={`${decl.label} link`}
                className="kd-focusable"
                style={{ ...attrInput, width: "auto", flex: "1 1 220px" }}
                onChange={(e) => setLinkDraft(e.target.value)}
              />
              <button
                type="button"
                disabled={busy || !isSafeHttpUrl(linkDraft)}
                style={{ ...reviewPill, opacity: isSafeHttpUrl(linkDraft) ? 1 : 0.5 }}
                onClick={() => {
                  void onSave(serialiseVideoAttributeValue({ kind: "Link", url: linkDraft.trim() }));
                  setLinkDraft("");
                }}
              >
                Use link
              </button>
            </>
          )}
        </div>
      )}
      {editable && atCapacity && !isVideo && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-faint)" }}>
          This field holds one file — remove it to attach a different one.
        </p>
      )}
    </div>
  );
}

/** A thumbnail for a clean image attachment; falls back to the file icon while resolving or on failure. */
function AttributeImageThumb({ instanceId, doc }: { instanceId: string; doc: DialDocument }) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    // canDownload false (KEY-537) means the server will refuse the SAS, so there is no thumbnail to be
    // had — ask for nothing rather than take a 403 per row. The filename/status line beside it is
    // unaffected: the file is still listed, just not readable.
    if (doc.status !== "Clean" || doc.canDownload === false) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    const token = getAccessToken();
    if (!token) {
      return;
    }
    void api.getDocumentDownloadUrl(token, instanceId, doc.id)
      .then(({ downloadUrl }) => {
        if (!cancelled) {
          setSrc(downloadUrl);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [instanceId, doc.id, doc.status, doc.canDownload]);

  if (!src) {
    return <span style={attrFileIcon}><Icon name="file" size={15} /></span>;
  }
  return (
    <img
      src={src}
      alt={doc.fileName}
      onError={() => setSrc(null)}
      style={{ width: 44, height: 44, flex: "none", borderRadius: 8, objectFit: "cover", border: "var(--border-w) solid var(--border)" }}
    />
  );
}

/**
 * Playback for a Video attribute's current value (KEY-254): a Clean upload resolves a short-lived SAS URL
 * eagerly on mount, exactly like {@link AttributeImageThumb}; a link value is classified client-side since,
 * unlike a guidance video, it never passes through the server's embed resolution. Renders nothing until
 * there's something playable — the surrounding filename/status/download row is unaffected either way.
 * Exported (not via the package index) so it's directly unit-testable without standing up ActionPointPanel.
 */
export function AttributeVideoPlayer({ instanceId, doc, link, title }: {
  instanceId: string;
  doc?: DialDocument;
  link?: string | null;
  title: string;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!doc || doc.status !== "Clean" || doc.canDownload === false) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    const token = getAccessToken();
    if (!token) {
      return;
    }
    void api.getDocumentDownloadUrl(token, instanceId, doc.id)
      .then(({ downloadUrl }) => {
        if (!cancelled) {
          setSrc(downloadUrl);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [instanceId, doc?.id, doc?.status, doc?.canDownload]);

  if (doc) {
    return src ? (
      <VideoPlayer video={{ title, embedKind: "file", playbackUrl: src }} onPlaybackError={() => setSrc(null)} />
    ) : null;
  }

  const classified = classifyVideoUrl(link);
  return classified ? <VideoPlayer video={{ title, ...classified }} /> : null;
}

const attrFileRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "9px 11px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
};

const attrFileIcon: CSSProperties = {
  width: 30,
  height: 30,
  flex: "none",
  borderRadius: 9,
  background: "var(--accent-soft)",
  color: "var(--accent-text)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// ---- Review date + auto-revert schedule (Phase 6, KEY-51/54) ----

const reviewInput: CSSProperties = {
  padding: "7px 10px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 13,
};

const reviewPill: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  fontWeight: 600,
  padding: "7px 12px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  cursor: "pointer",
};

/**
 * The "schedule review" affordance: a badge saying what is due when, and — for anyone allowed to move the
 * node's status — a date + revert-target form behind it. Node-agnostic on purpose: an action point's own
 * status (KEY-51) and one item's status on one axis (KEY-376) offer exactly the same thing, and the server
 * validates both the same way, so they share one control rather than drifting apart.
 */
function ReviewSchedule({
  reviewDate,
  revertTo,
  overdue,
  palette,
  statusName,
  canEdit,
  nodeKey,
  nodeLabel,
  reminderLeadDays,
  reminderRepeatDays,
  recurrenceUnit,
  recurrenceCount,
  dialLadder,
  channels,
  compact = false,
  forceOpen = false,
  dateLabel = DEFAULT_ITEM_DATE_LABEL,
  onSchedule,
}: {
  reviewDate: string | null;
  revertTo: string | null;
  overdue: boolean;
  palette: GeneratedStatus[];
  statusName: (statusKey: string) => string;
  canEdit: boolean;
  /** Identity of the node this control is showing, so the form re-syncs when React reuses it elsewhere. */
  nodeKey: string;
  /** Which node this control belongs to, for screen readers — an item card carries one per status axis,
   * so "Schedule review" and "Review date" on their own would name several different things. */
  nodeLabel?: string;
  /** This node's own reminder horizon, or null when it follows the dial's ladder (KEY-413). */
  reminderLeadDays?: number | null;
  reminderRepeatDays?: number | null;
  /** How often this review comes round again, or null for a one-off. */
  recurrenceUnit?: string | null;
  recurrenceCount?: number | null;
  /** The days this dial reminds on when a node states no horizon of its own. Empty = no reminders. */
  dialLadder?: number[] | null;
  /** The channels the CURRENT USER would actually hear this on, resolved from their subscriptions. Shown,
   * never set: a reminder fans out to everyone watching the action point, so choosing channels here would
   * be one person deciding how a dozen colleagues are contacted. */
  channels?: string[];
  /** Tighter type for the per-axis rows inside an item card, where the control sits beside a picker. */
  compact?: boolean;
  /** Opens the sheet immediately and hides the badge. Used when a status the user has just picked REQUIRES
   * a date (KEY-412): there is nothing to toggle, the date is the thing being asked for. */
  forceOpen?: boolean;
  /** What the date is called (KEY-809) — an item check may rename it, e.g. "Target date". */
  dateLabel?: string;
  onSchedule: (
    reviewDate: string | null,
    revertToStatusKey: string | null,
    reminderLeadDays: number | null,
    reminderRepeatDays: number | null,
    recurrenceUnit: string | null,
    recurrenceCount: number | null,
  ) => void;
}) {
  const [open, setOpen] = useState(forceOpen);
  const [date, setDate] = useState("");
  const [revert, setRevert] = useState("");
  const [lead, setLead] = useState("");
  const [repeat, setRepeat] = useState("");
  const [repeating, setRepeating] = useState(false);
  const [recurs, setRecurs] = useState(false);
  const [recurEvery, setRecurEvery] = useState("");
  const [recurUnit, setRecurUnit] = useState("Months");

  // Re-sync the form whenever the schedule changes (e.g. after a save or navigating nodes).
  useEffect(() => {
    setDate(reviewDate ? reviewDate.slice(0, 10) : "");
    setRevert(revertTo ?? "");
    setLead(reminderLeadDays === null || reminderLeadDays === undefined ? "" : String(reminderLeadDays));
    setRepeating(reminderRepeatDays !== null && reminderRepeatDays !== undefined);
    setRepeat(reminderRepeatDays === null || reminderRepeatDays === undefined ? "" : String(reminderRepeatDays));
    setRecurs(!!recurrenceUnit);
    setRecurUnit(recurrenceUnit || "Months");
    setRecurEvery(recurrenceCount === null || recurrenceCount === undefined ? "" : String(recurrenceCount));
    setOpen(forceOpen);
  }, [nodeKey, reviewDate, revertTo, reminderLeadDays, reminderRepeatDays, recurrenceUnit, recurrenceCount, forceOpen]);

  const badge = reviewDate ? (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 5 : 6,
        fontSize: compact ? 12 : 13,
        fontWeight: 600,
        color: overdue ? "var(--st-overdue)" : "var(--text-muted)",
      }}
    >
      <Icon name="clock" size={compact ? 12 : 14} />
      {revertTo
        ? `${overdue ? "Reverting" : "Reverts"} to ${statusName(revertTo)} · ${fmtDate(utcDay(reviewDate))}`
        : itemDateBadge(dateLabel, fmtDate(utcDay(reviewDate)))}
    </span>
  ) : null;

  if (!canEdit) {
    return badge;
  }

  const leadNum = lead.trim() === "" ? null : Number(lead);
  const repeatNum = !repeating || repeat.trim() === "" ? null : Number(repeat);
  // Mirrors the server's ReviewReminderLadder.IsValid — a repeat needs something to repeat from, and a
  // lead has to be a real number of days. Parity, not a second opinion: the server still refuses.
  const leadValid = leadNum === null || (Number.isInteger(leadNum) && leadNum >= 0 && leadNum <= 3650);
  const repeatValid = repeatNum === null || (Number.isInteger(repeatNum) && repeatNum >= 1 && repeatNum <= 3650);
  const reminderValid = leadValid && repeatValid && (leadNum !== null || repeatNum === null);

  // The cycle. Sent as three answers, not two: "None" ends it, and null says nothing about it — which is
  // what a plain status change from the picker sends, so it can't cancel a cycle on its way past.
  const recurNum = recurEvery.trim() === "" ? null : Number(recurEvery);
  const recurValid = !recurs || (recurNum !== null && Number.isInteger(recurNum) && recurNum >= 1);

  // The days this node will actually be announced on, so the sheet states the outcome rather than the
  // settings. Same rule as the server's ladder builder.
  const rungs: number[] = (() => {
    if (leadNum === null || !reminderValid) {
      return dialLadder ?? [];
    }
    if (repeatNum === null) {
      return [leadNum];
    }
    const out: number[] = [];
    for (let d = leadNum; d > 0; d -= repeatNum) {
      out.push(d);
    }
    out.push(0);
    return out;
  })();

  const dayWord = (d: number) => (d === 0 ? "on the day" : d === 1 ? "1 day before" : `${d} days before`);
  const save = () =>
    onSchedule(
      date ? `${date}T00:00:00.000Z` : null,
      date && revert ? revert : null,
      date ? leadNum : null,
      date ? repeatNum : null,
      recurs ? recurUnit : "None",
      recurs ? recurNum : null,
    );
  const label = (field: string) => (nodeLabel ? `${field} — ${nodeLabel}` : field);
  const renamed = dateLabel !== DEFAULT_ITEM_DATE_LABEL;
  const dateWord = dateLabel.toLowerCase();

  const sheet = open ? (
    <Sheet
      title={renamed ? `${reviewDate ? "Edit" : "Set"} ${dateWord}` : reviewDate ? "Edit review" : "Schedule review"}
      subtitle={nodeLabel ?? undefined}
      onClose={() => setOpen(false)}
      footer={
        <>
          <button
            onClick={() => { save(); setOpen(false); }}
            disabled={!date || !reminderValid || !recurValid}
            className="kd-focusable"
            style={{ ...reviewPill, opacity: date && reminderValid && recurValid ? 1 : 0.5, fontWeight: 700 }}
          >
            Save
          </button>
          {reviewDate && (
            <button
              onClick={() => { onSchedule(null, null, null, null, "None", null); setOpen(false); }}
              className="kd-focusable"
              style={{ ...reviewPill, color: "var(--st-overdue)" }}
            >
              {renamed ? `Clear ${dateWord}` : "Clear review"}
            </button>
          )}
          <button
            onClick={() => setOpen(false)}
            className="kd-focusable"
            style={{ ...reviewPill, marginLeft: "auto", color: "var(--text-muted)" }}
          >
            Cancel
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <label style={sheetLabel} htmlFor={`rv-date-${nodeKey}`}>{dateLabel}</label>
        <input
          id={`rv-date-${nodeKey}`}
          type="date"
          aria-label={label(dateLabel)}
          data-help-anchor={HELP_ANCHORS.apReviewDate}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="kd-focusable"
          style={{ ...reviewInput, width: "100%" }}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <label style={sheetLabel} htmlFor={`rv-revert-${nodeKey}`}>When it passes</label>
        <select
          id={`rv-revert-${nodeKey}`}
          aria-label={label("Revert to status")}
          value={revert}
          onChange={(e) => setRevert(e.target.value)}
          className="kd-focusable"
          style={{ ...reviewInput, width: "100%" }}
        >
          <option value="">Leave the status alone — just remind me</option>
          {palette.map((st) => (
            <option key={st.key} value={st.key}>Change it to {st.name}</option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <label style={sheetLabel} htmlFor={`rv-lead-${nodeKey}`}>Warn me</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input
            id={`rv-lead-${nodeKey}`}
            type="number"
            min={0}
            max={3650}
            aria-label={label("Days before to send a reminder")}
            value={lead}
            placeholder="—"
            onChange={(e) => setLead(e.target.value)}
            className="kd-focusable"
            style={{ ...reviewInput, width: 88 }}
          />
          <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>days before</span>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--text-muted)", cursor: leadNum === null ? "not-allowed" : "pointer", opacity: leadNum === null ? 0.55 : 1 }}>
          <input
            type="checkbox"
            checked={repeating}
            disabled={leadNum === null}
            aria-label={label("Repeat the reminder")}
            onChange={(e) => setRepeating(e.target.checked)}
          />
          then repeat every
          <input
            type="number"
            min={1}
            max={3650}
            aria-label={label("Repeat every N days")}
            value={repeat}
            disabled={!repeating || leadNum === null}
            placeholder="2"
            onChange={(e) => setRepeat(e.target.value)}
            className="kd-focusable"
            style={{ ...reviewInput, width: 68 }}
          />
          days
        </label>
        {!reminderValid && (
          <span role="alert" style={{ fontSize: 12, color: "var(--st-overdue)" }}>
            {leadNum === null
              ? "Say how many days before to warn you, or switch the repeat off."
              : "Days must be whole numbers — and a repeat has to be at least 1 day."}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <span style={sheetLabel}>Afterwards</span>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--text-muted)", cursor: "pointer", flexWrap: "wrap" }}>
          <input
            type="checkbox"
            checked={recurs}
            aria-label={label("Repeat this review")}
            onChange={(e) => setRecurs(e.target.checked)}
          />
          repeat this review every
          <input
            type="number"
            min={1}
            aria-label={label("Repeat the review every")}
            value={recurEvery}
            disabled={!recurs}
            placeholder="12"
            onChange={(e) => setRecurEvery(e.target.value)}
            className="kd-focusable"
            style={{ ...reviewInput, width: 68 }}
          />
          <select
            aria-label={label("Repeat the review in")}
            value={recurUnit}
            disabled={!recurs}
            onChange={(e) => setRecurUnit(e.target.value)}
            className="kd-focusable"
            style={reviewInput}
          >
            <option value="Days">days</option>
            <option value="Weeks">weeks</option>
            <option value="Months">months</option>
          </select>
        </label>
        {recurs && (
          <span style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
            Counted from when the review is actually done, not from the date it was due — so one carried out
            late doesn't shorten the next, and one nobody does stays overdue rather than quietly re-dating
            itself.
          </span>
        )}
        {!recurValid && (
          <span role="alert" style={{ fontSize: 12, color: "var(--st-overdue)" }}>
            Say how often it repeats, or untick it.
          </span>
        )}
      </div>

      {/* The outcome, not the settings. Someone reading this should not have to do the arithmetic to find
          out which days they have just signed up for. */}
      <div style={{ padding: "10px 12px", borderRadius: "var(--radius-sm)", background: "var(--surface-inset)", display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>You'll be reminded</div>
        {rungs.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Never — this dial doesn't send review reminders.
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.5 }}>
            {rungs.map(dayWord).join(", ")}
            {leadNum === null && <span style={{ color: "var(--text-faint)" }}> · this dial's usual schedule</span>}
          </div>
        )}
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          {channels && channels.length > 0
            ? <>By <strong style={{ color: "var(--text)" }}>{channels.join(" and ")}</strong> — change that with Subscribe.</>
            : <>You aren't subscribed to this dial, so you won't hear about it. Use Subscribe to change that.</>}
        </div>
      </div>
    </Sheet>
  ) : null;

  if (forceOpen) {
    return sheet;
  }

  const action = reviewDate ? "Edit review" : "Schedule review";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {badge}
      <button
        onClick={() => setOpen(true)}
        // Icon-only on an item card (KEY-485), where one of these sits beside every status axis and the
        // words were the widest thing in the row. The clock keeps its name for anyone not reading pixels.
        aria-label={nodeLabel ? label(action) : action}
        title={action}
        aria-expanded={open}
        className="kd-focusable"
        data-help-anchor={HELP_ANCHORS.apScheduleReview}
        style={{ ...reviewPill, color: "var(--text-muted)", padding: compact ? "4px 6px" : "5px 9px", fontSize: compact ? 12 : 12.5, display: "inline-flex", alignItems: "center", gap: 5 }}
      >
        <Icon name="clock" size={compact ? 13 : 13} />
        {!compact && action}
      </button>
      {sheet}
    </div>
  );
}

const sheetLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--text)",
};

/** The action point's own review schedule (KEY-51) — the no-items shape. */
function ReviewControl({
  segment,
  statusName,
  canEdit,
  dialLadder,
  channels,
  onSchedule,
}: {
  segment: VSegment;
  statusName: (statusKey: string) => string;
  canEdit: boolean;
  dialLadder?: number[] | null;
  channels?: string[];
  onSchedule: (
    reviewDate: string | null,
    revertToStatusKey: string | null,
    reminderLeadDays: number | null,
    reminderRepeatDays: number | null,
    recurrenceUnit: string | null,
    recurrenceCount: number | null,
  ) => void;
}) {
  // KEY-412: the status's policy holds here too — it is a property of the status, not of items. An action
  // point has no expiry switch of its own, hence the unconditional `true`.
  const offered = reviewDateOffered(segment.status, true);
  return (
    <ReviewSchedule
      reviewDate={segment.reviewDate ?? null}
      revertTo={segment.revertTo ?? null}
      overdue={segment.overdue}
      palette={segment.palette}
      statusName={statusName}
      canEdit={canEdit && offered}
      nodeKey={segment.key}
      nodeLabel={segment.name}
      reminderLeadDays={segment.reviewReminderLeadDays ?? null}
      reminderRepeatDays={segment.reviewReminderRepeatDays ?? null}
      recurrenceUnit={segment.reviewRecurrenceUnit ?? null}
      recurrenceCount={segment.reviewRecurrenceCount ?? null}
      dialLadder={dialLadder}
      channels={channels}
      onSchedule={onSchedule}
    />
  );
}

/**
 * One item's review schedule on ONE status axis (KEY-376). Per-axis rather than per-item because that is
 * where an item's state actually lives: "the survey expires in a year" and "the sign-off never expires"
 * are two different answers on the same item.
 */
function ItemReviewControl({
  item,
  dimension,
  status,
  canEdit,
  dialLadder,
  channels,
  onSchedule,
}: {
  item: GeneratedItem;
  dimension: GeneratedDimension;
  status: ItemStatus | undefined;
  canEdit: boolean;
  dialLadder?: number[] | null;
  channels?: string[];
  onSchedule: (
    reviewDate: string | null,
    revertToStatusKey: string | null,
    reminderLeadDays: number | null,
    reminderRepeatDays: number | null,
    recurrenceUnit: string | null,
    recurrenceCount: number | null,
  ) => void;
}) {
  const reviewDate = status?.reviewDate ?? null;
  const current = dimension.palette.statuses.find((s) => s.key === status?.statusKey);
  // KEY-412: a status can decline review dates, and the item can decline them outright. Either way the
  // form is withheld — but a date already stored still SHOWS, because the policy governs what may be set
  // and the stored expiry still fires.
  const offered = reviewDateOffered(current, item.allowsReviewDate !== false);
  // Same rule as an action point's badge: a node already at a terminal status isn't "late", it's parked.
  const overdue = !current?.isTerminal && isPastDate(utcDay(reviewDate));
  // An item's revert target is always a status on this axis, so the axis's own palette names it — no need
  // for the dial-wide lookup the action-point control uses.
  const statusName = (key: string) => dimension.palette.statuses.find((s) => s.key === key)?.name ?? key;
  return (
    <ReviewSchedule
      reviewDate={reviewDate}
      revertTo={status?.revertToStatusKey ?? null}
      overdue={overdue}
      palette={dimension.palette.statuses}
      statusName={statusName}
      canEdit={canEdit && offered}
      nodeKey={`${item.key}|${dimension.key}`}
      nodeLabel={`${item.name}, ${dimension.label}`}
      dateLabel={itemDateLabel(dimension)}
      reminderLeadDays={status?.reviewReminderLeadDays ?? null}
      reminderRepeatDays={status?.reviewReminderRepeatDays ?? null}
      recurrenceUnit={status?.reviewRecurrenceUnit ?? null}
      recurrenceCount={status?.reviewRecurrenceCount ?? null}
      dialLadder={dialLadder}
      channels={channels}
      compact
      onSchedule={onSchedule}
    />
  );
}

/**
 * Author-written guidance, or one settled sentence saying there is none. Exported since KEY-542 so the
 * section's Guidance tab renders the same shape — including the same empty state, because "no guidance
 * added yet" should not be phrased two ways at two levels of the same dial.
 */
export function Prose({ text, fallback, muted }: { text: string; fallback: string; muted?: boolean }) {
  if (!richTextHasContent(text)) {
    return <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: "var(--text-muted)" }}>{fallback}</p>;
  }
  // Author-written guidance is stored as rich text — render it formatted (and safely).
  return <RichText value={text} style={{ fontSize: 15, lineHeight: 1.7, color: muted ? "var(--text-muted)" : "var(--text)" }} />;
}

/**
 * Read-only list of the author's external-resource links, shown beneath guidance (the structured
 * counterpart to inline links in the prose). Each opens in a new tab; unsafe hrefs — anything that
 * isn't http/https/mailto — are dropped defensively even though the server already validated them.
 */
export function ExternalResources({ links, compact }: { links?: GeneratedLink[] | null; compact?: boolean }) {
  const safe = (links ?? []).filter((l) => !!l.url && isSafeHref(l.url));
  if (safe.length === 0) {
    return null;
  }
  return (
    <div style={{ marginTop: compact ? 6 : 12, borderTop: "var(--border-w) dashed var(--border)", paddingTop: 12 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-faint)", marginBottom: 10 }}>
        External resources · {safe.length}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {safe.map((link, i) => (
          <a
            key={i}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 11px",
              borderRadius: "var(--radius-sm)",
              border: "var(--border-w) solid var(--border)",
              background: "var(--surface-2)",
              textDecoration: "none",
              color: "var(--text)",
            }}
          >
            <Icon name="link" size={15} style={{ color: "var(--accent)", flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {link.label || link.url}
              </span>
              <span style={{ display: "block", fontSize: 11.5, color: "var(--text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {link.url}
              </span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

/** Placeholder for a live-only tab (Files / Discussion) when shown in a non-persisted preview. */
/**
 * Read-only transcript of the discussions on the items within one action point, shown beneath the
 * segment's own thread. Posting happens on each item (its Discussion section in the item detail); this
 * is the aggregate view so the action point shows everything discussed under it in one place.
 */
function ItemDiscussionsTranscript({
  instanceId,
  sectionKey,
  segmentKey,
  itemNameByKey,
  meUserId,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  instanceId: string;
  sectionKey: string;
  segmentKey: string;
  itemNameByKey: Map<string, string>;
  /** The reader, so a restricted comment's audience line can say "you". */
  meUserId: string | null;
  /** This dial's word for an action point (KEY-539) — this component doesn't receive the detail. */
  noun?: SegmentNoun;
}) {
  const { api, getAccessToken, useSegmentRealtime } = useWorkspaceServices();
  const [threads, setThreads] = useState<{ itemKey: string; messages: MessageDto[] }[]>([]);

  const reload = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const result = await api.listSegmentConversations(token, instanceId, sectionKey, segmentKey);
      // A deleted comment stays as a tombstone (KEY-582), so a thread of nothing but tombstones still reads.
      setThreads(result.itemThreads.filter((t) => t.messages.length > 0));
    } catch {
      // best-effort transcript; the segment thread above still works
    }
  }, [instanceId, sectionKey, segmentKey]);

  useEffect(() => {
    void reload();
  }, [reload]);
  // Item messages fire notification events on this segment scope, so refresh the transcript on them too.
  useSegmentRealtime(instanceId, sectionKey, segmentKey, { onEvent: () => void reload(), onReconnect: reload });

  if (threads.length === 0) {
    return null;
  }

  return (
    <section>
      <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-faint)", marginBottom: 4 }}>
        From the items in this {noun.one}
      </div>
      <p style={{ margin: "0 0 12px", fontSize: 12.5, color: "var(--text-faint)" }}>
        Comment on an item from its own Discussion section, under the item.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {threads.map((thread) => (
          <div key={thread.itemKey}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>
              {itemNameByKey.get(thread.itemKey) ?? "Item"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, borderLeft: "2px solid var(--border)", paddingLeft: 12 }}>
              {thread.messages.map((m) => (
                <TranscriptMessageRow key={m.id} message={m} meUserId={meUserId} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DisabledTab({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: "22px 16px",
        textAlign: "center",
        fontSize: 13.5,
        color: "var(--text-faint)",
        border: "var(--border-w) dashed var(--border)",
        borderRadius: "var(--radius)",
        background: "var(--surface-2)",
      }}
    >
      {label}
    </div>
  );
}

/**
 * KEY-460: what stands where the items would be, when the action point isn't ready for them yet.
 *
 * Deliberately not a `DisabledTab`: that says "not available here", which is true of a preview and wrong
 * here — this IS available, once someone does one specific thing. So it names the fields, and hands the
 * person who can fill them in the way to get there rather than leaving them to find the Info tab.
 */
function RequirementLock({
  missing,
  canFill,
  onOpenInfo,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  missing: SegmentRequirement[];
  canFill: boolean;
  onOpenInfo?: () => void;
  /** This dial's word for an action point (KEY-539); omitted means the platform's word. */
  noun?: SegmentNoun;
}) {
  return (
    <div
      role="status"
      style={{
        padding: "20px 18px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        border: "var(--border-w) solid var(--rag-red, #e7161b)",
        borderRadius: "var(--radius)",
        background: hexA("#e7161b", 0.06),
      }}
    >
      <Icon name="lock" size={16} style={{ color: "var(--rag-red, #e7161b)", flex: "none", marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
          {missing.length === 1 ? "One field is needed first" : `${missing.length} fields are needed first`}
        </div>
        <p style={{ margin: "6px 0 0", fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
          {requirementMessage(missing, canFill, noun)}
        </p>
        {/* Chips rather than a <ul>: Tailwind's preflight strips list markers in the user app, so the
            bullets a list implies never render and the names read as a stray indented paragraph. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
          {missing.map((m) => (
            <span
              key={m.attributeKey}
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                padding: "4px 9px",
                borderRadius: 999,
                border: `1px solid ${hexA("#e7161b", 0.35)}`,
                background: "var(--surface)",
                color: "var(--rag-red, #e7161b)",
              }}
            >
              {m.label}
            </span>
          ))}
        </div>
        {canFill && onOpenInfo && (
          // `.btn` is a web-admin class and has no styling in the user app — the workspace styles its own
          // buttons inline, as reviewPill does.
          <button type="button" className="kd-focusable" style={{ ...reviewPill, marginTop: 12 }} onClick={onOpenInfo}>
            Go to Key Info
          </button>
        )}
      </div>
    </div>
  );
}

function OverviewTab({ segment, children }: { segment: VSegment; children?: ReactNode }) {
  // An action point with no items reports its own status, whichever kind of dial it sits on (KEY-259).
  const statusLine = !segment.itemActive
    ? `Current status: ${segment.status?.name ?? "not set"}.`
    : `${segment.done} of ${segment.total} items complete.`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* KEY-584: the segment's own description, shown above its status — same placement/pattern as a
          section's description on its Overview tab (panels.tsx). */}
      {richTextHasContent(segment.description) && (
        <RichText value={segment.description} style={{ margin: 0, color: "var(--text-muted)", fontSize: 15, lineHeight: 1.6 }} />
      )}
      {/* KEY-542 removed the guidance, documents and links this used to repeat from the Guidance tab, which left
          the status line on its own on the tab people land on. KEY-569 gives Overview a job again — what this
          action point asks for and what's still unanswered — built from its fields, so nothing is said twice. */}
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: "var(--text-muted)" }}>{statusLine}</p>
      {children}
    </div>
  );
}

// ---- Items (itemised dials): each item scored across the dial's status dimensions ----

function ItemsList({
  segment,
  dimensions,
  sectionKey,
  itemStatusIndex,
  canSet,
  allocationUi,
  sectionLabel,
  onSetItemStatus,
  onScheduleItemRevert,
  dialLadder,
  channels,
  detail,
  canEditAttributes,
  attributeAccess,
  onSetNodeAttribute,
  attributeFiles,
  itemEdit,
  appointmentEdit,
  onOpenSegment,
  docs,
  itemHistory,
  instanceId,
  canEditDocs,
  onCreateDocumentTag,
  canManageDocVersions,
  documentsEnabled,
  onDocsChanged,
  uploadAutomation,
  messagesEnabled,
  meUserId,
  canPost,
  canModerate,
  restricted,
}: {
  onOpenSegment?: (segmentKey: string) => void;
  segment: VSegment;
  dimensions: GeneratedDimension[];
  sectionKey: string;
  itemStatusIndex: Map<string, string>;
  canSet: (status: GeneratedStatus, itemKey: string) => boolean;
  /** KEY-916: allocation on this dial — absent when it's off. */
  allocationUi?: AllocationUi;
  sectionLabel?: string;
  onSetItemStatus: (itemKey: string, dimensionKey: string, statusKey: string, review?: ReviewScheduleInput) => void;
  onScheduleItemRevert: (itemKey: string, dimensionKey: string, reviewDate: string | null,
    revertToStatusKey: string | null, reminderLeadDays: number | null, reminderRepeatDays: number | null,
    recurrenceUnit: string | null, recurrenceCount: number | null) => void;
  /** The dial's own reminder ladder and the caller's channels, for the review sheet's readout.
   * Threaded rather than re-derived at each level so the sheet and the Subscribe control can never
   * disagree about what this person will actually receive. */
  dialLadder?: number[] | null;
  channels?: string[];
  detail: InstanceDetail;
  canEditAttributes: boolean;
  attributeAccess?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  onSetNodeAttribute: (request: SetNodeAttributeRequest) => void | Promise<void>;
  attributeFiles: AttributeFilesContext;
  itemEdit?: ItemEditControls;
  appointmentEdit?: AppointmentEditControls;
  docs: DialDocument[];
  itemHistory: ItemStatusEvent[];
  instanceId: string;
  canEditDocs: boolean;
  onCreateDocumentTag?: (label: string) => Promise<string | null>;
  canManageDocVersions: boolean;
  documentsEnabled: boolean;
  onDocsChanged: () => Promise<void>;
  uploadAutomation?: UploadAutomationControls;
  messagesEnabled: boolean;
  meUserId: string | null;
  canPost: boolean;
  canModerate: boolean;
  restricted?: RestrictedMessaging;
}) {
  // Item cards collapse to title + status colour; openKey holds the one expanded card, or null when all
  // are shut (KEY-485). An accordion rather than a set: an expanded card is tall — four tabs, a status row
  // per axis, an upload dropzone — so several at once buried the list they belong to and made "which item
  // am I editing" a scrolling question.
  const [openKey, setOpenKey] = useState<string | null>(null);
  // KEY-812: the item just added, whose card opens with its title ready to type over ("New to-do").
  const [renameKey, setRenameKey] = useState<string | null>(null);
  const { api, getAccessToken, useSegmentRealtime } = useWorkspaceServices();
  // Message tallies for the item cards' Discussion chips — one segment-level fetch (the same call the
  // panel's Discussion tab aggregates from), refreshed on the segment's realtime events. Best-effort:
  // a failed fetch just leaves the chips uncounted; the thread itself loads (and errors) in its pane.
  const [messageCounts, setMessageCounts] = useState<ReadonlyMap<string, number>>(() => new Map());
  const reloadMessageCounts = useCallback(async () => {
    if (!messagesEnabled) {
      return;
    }
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      const result = await api.listSegmentConversations(token, instanceId, sectionKey, segment.key);
      setMessageCounts(new Map(result.itemThreads.map((t) => [t.itemKey, t.messages.filter((m) => !m.isDeleted).length])));
    } catch {
      // chip counts are cosmetic
    }
  }, [api, getAccessToken, instanceId, sectionKey, segment.key, messagesEnabled]);
  useEffect(() => {
    void reloadMessageCounts();
  }, [reloadMessageCounts]);
  useSegmentRealtime(instanceId, sectionKey, segment.key, { onEvent: () => void reloadMessageCounts(), onReconnect: reloadMessageCounts });
  // Opening a card shuts whichever one was open; clicking the open card shuts it and leaves none.
  const toggleItem = (key: string) => setOpenKey((prev) => (prev === key ? null : key));
  if (dimensions.length === 0) {
    return <p style={{ marginTop: 16, fontSize: 14, color: "var(--text-muted)" }}>No checks are set up for this dial.</p>;
  }
  const atCap = !!itemEdit && itemEdit.maxItemsPerSegment != null && segment.items.length >= itemEdit.maxItemsPerSegment;
  // KEY-753: this section's word for its items ("appointment"), "item" when it declares none.
  const words = itemNouns(detail, sectionKey);
  return (
    <div style={{ border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius)", overflow: "visible" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "11px 14px",
          borderBottom: "var(--border-w) solid var(--border)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{words.Many} ({segment.items.length})</span>
          {/* "Expand all" went with the accordion (KEY-485): with one card open at a time there is no
              "all" to expand, and a Collapse-all that only ever shut one card was a button pretending
              to do more than it did. Shutting the open card is a click on its own header. */}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 700, color: segment.colour }}>
            <span style={{ width: 9, height: 9, borderRadius: 99, background: segment.colour }} />
            {segment.done} / {segment.total} complete
          </span>
          {itemEdit?.canAdd && (
            <button
              onClick={async () => {
                const key = await itemEdit.onAdd();
                if (key) {
                  setOpenKey(key);
                  setRenameKey(key);
                }
              }}
              disabled={atCap}
              className="kd-row-hover kd-focusable"
              title={atCap ? `${words.One} limit reached for this ${segmentNoun(detail, sectionKey).one}` : `Add ${article(words.one)} ${words.one}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                font: "inherit",
                fontSize: 12.5,
                fontWeight: 700,
                cursor: atCap ? "default" : "pointer",
                padding: "6px 11px",
                borderRadius: "var(--radius-sm)",
                border: "var(--border-w) solid var(--accent-border)",
                background: "var(--accent-soft)",
                color: "var(--accent-text)",
                opacity: atCap ? 0.5 : 1,
              }}
            >
              <Icon name="plus" size={14} />
              Add {words.one}
            </button>
          )}
        </span>
      </div>
      {segment.items.length === 0 ? (
        <div style={{ padding: "16px 14px", fontSize: 13.5, color: "var(--text-faint)" }}>
          No {words.many} yet.{itemEdit?.canAdd ? ` Use “Add ${words.one}” to create the first one.` : ""}
        </div>
      ) : (
        segment.items.map((item, i) => (
          <ItemRow
            key={item.key}
            item={item}
            first={i === 0}
            dimensions={dimensions}
            sectionKey={sectionKey}
            segmentKey={segment.key}
            itemStatusIndex={itemStatusIndex}
            canSet={canSet}
            allocationUi={allocationUi}
            sectionLabel={sectionLabel}
            segmentName={segment.name}
            onSetItemStatus={onSetItemStatus}
            onScheduleItemRevert={onScheduleItemRevert}
            dialLadder={dialLadder}
            channels={channels}
            detail={detail}
            canEditAttributes={canEditAttributes}
            attributeAccess={attributeAccess}
            onSetNodeAttribute={onSetNodeAttribute}
            attributeFiles={attributeFiles}
            itemEdit={itemEdit}
            appointmentEdit={appointmentEdit}
            docs={docs.filter((d) => d.itemKey === item.key)}
            itemHistory={itemHistory}
            instanceId={instanceId}
            canEditDocs={canEditDocs}
            onCreateDocumentTag={onCreateDocumentTag}
            canManageDocVersions={canManageDocVersions}
            documentsEnabled={documentsEnabled}
            onDocsChanged={onDocsChanged}
            uploadAutomation={uploadAutomation}
            messagesEnabled={messagesEnabled}
            messageCount={messageCounts.get(item.key) ?? 0}
            meUserId={meUserId}
            canPost={canPost}
            canModerate={canModerate}
            restricted={restricted}
            open={openKey === item.key}
            onToggle={() => toggleItem(item.key)}
            startRenaming={renameKey === item.key}
            onRenameStarted={() => setRenameKey(null)}
          />
        ))
      )}
      {/* KEY-859: what's listed earlier in the day but runs on into this part of it — counted here, listed there. */}
      {continuingRows(segment.spanning, detail.sections.find((s) => s.key === sectionKey)).map((row) => (
        <div
          key={`${row.segmentKey}/${row.itemKey}`}
          className="kd-item-row"
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px 9px 35px", borderTop: "var(--border-w) dashed var(--border)", fontSize: 13.5, color: "var(--text-faint)" }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            {row.name} <span style={{ fontSize: 12.5 }}>· {row.note}</span>
          </span>
          {onOpenSegment && (
            <button type="button" className="kd-focusable" onClick={() => onOpenSegment(row.segmentKey)}
              style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", color: "var(--accent-text)", cursor: "pointer", padding: 0 }}>
              Open
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ItemRow({
  item,
  first,
  dimensions,
  sectionKey,
  segmentKey,
  itemStatusIndex,
  canSet: canSetAt,
  allocationUi,
  sectionLabel,
  segmentName,
  onSetItemStatus,
  onScheduleItemRevert,
  dialLadder,
  channels,
  detail,
  canEditAttributes,
  attributeAccess,
  onSetNodeAttribute,
  attributeFiles,
  itemEdit,
  appointmentEdit,
  docs,
  itemHistory,
  instanceId,
  canEditDocs,
  onCreateDocumentTag,
  canManageDocVersions,
  documentsEnabled,
  onDocsChanged,
  uploadAutomation,
  messagesEnabled,
  messageCount,
  meUserId,
  canPost,
  canModerate,
  restricted,
  open,
  onToggle,
  startRenaming = false,
  onRenameStarted,
}: {
  item: GeneratedItem;
  first: boolean;
  dimensions: GeneratedDimension[];
  sectionKey: string;
  segmentKey: string;
  itemStatusIndex: Map<string, string>;
  canSet: (status: GeneratedStatus, itemKey: string) => boolean;
  allocationUi?: AllocationUi;
  sectionLabel?: string;
  segmentName?: string;
  onSetItemStatus: (itemKey: string, dimensionKey: string, statusKey: string, review?: ReviewScheduleInput) => void;
  onScheduleItemRevert: (itemKey: string, dimensionKey: string, reviewDate: string | null,
    revertToStatusKey: string | null, reminderLeadDays: number | null, reminderRepeatDays: number | null,
    recurrenceUnit: string | null, recurrenceCount: number | null) => void;
  /** The dial's own reminder ladder and the caller's channels, for the review sheet's readout.
   * Threaded rather than re-derived at each level so the sheet and the Subscribe control can never
   * disagree about what this person will actually receive. */
  dialLadder?: number[] | null;
  channels?: string[];
  detail: InstanceDetail;
  canEditAttributes: boolean;
  attributeAccess?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  onSetNodeAttribute: (request: SetNodeAttributeRequest) => void | Promise<void>;
  attributeFiles: AttributeFilesContext;
  itemEdit?: ItemEditControls;
  appointmentEdit?: AppointmentEditControls;
  docs: DialDocument[];
  itemHistory: ItemStatusEvent[];
  instanceId: string;
  canEditDocs: boolean;
  onCreateDocumentTag?: (label: string) => Promise<string | null>;
  canManageDocVersions: boolean;
  documentsEnabled: boolean;
  onDocsChanged: () => Promise<void>;
  uploadAutomation?: UploadAutomationControls;
  messagesEnabled: boolean;
  /** Non-deleted messages on this item's thread, for the Discussion chip (0 = uncounted or none). */
  messageCount: number;
  meUserId: string | null;
  canPost: boolean;
  canModerate: boolean;
  restricted?: RestrictedMessaging;
  open: boolean;
  onToggle: () => void;
  /** KEY-812: open with the title in rename mode — set for the item just added. */
  startRenaming?: boolean;
  onRenameStarted?: () => void;
}) {
  // This row's statuses are asked about at THIS item (KEY-918).
  const canSet = useCallback((status: GeneratedStatus) => canSetAt(status, item.key), [canSetAt, item.key]);
  // KEY-916: who has this item, and what the sheet calls it.
  const itemScope = { sectionKey, segmentKey, itemKey: item.key };
  const itemAssignment = allocationUi ? assignmentAt(detail, itemScope) : null;
  const itemPath = `${sectionLabel ?? sectionKey} › ${segmentName ?? segmentKey} › ${item.name}`;
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(false);
  // KEY-485: an item card carries a read view and an edit view. The read view is the one people spend the
  // day in — statuses, fields, files, the thread — and the edit view is where the card's own shape is
  // changed: its name, whether it exists, and what an upload to it does. Those three were previously
  // mixed in with the day-to-day controls, so every reader saw switches they had no reason to touch.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.name);
  // Escape cancels via blur; this flag tells the blur handler to skip the save on that path.
  const skipBlurSave = useRef(false);
  // Custom (instance-added) items carry a generated "c-" key; base items come from the definition.
  const isCustom = item.key.startsWith("c-");
  // KEY-821: an event read from someone's calendar is neither renamed nor hidden here — its title is the calendar's,
  // and it is changed (by its owner) through the appointment controls instead.
  const calendar = (item as ResolvedItem).calendar ?? null;
  const removable = !calendar && (isCustom ? !!itemEdit?.canRemoveCustom : !!itemEdit?.canHideBase);
  const renamable = !calendar && (isCustom ? !!itemEdit?.canRenameCustom : !!itemEdit?.canRenameBase);
  const appointmentEditable = !!appointmentEdit && calendar?.canEdit === true;
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Activity = user-generated data the server won't let us hard-delete (a status change in the history, an
  // attached file, or a node-attribute value); seeded default statuses don't count. A pristine custom item
  // can be deleted outright; everything else is hidden (reversibly), keeping its evidence and audit intact.
  const hasActivity =
    docs.length > 0
    || itemHistory.some((h) => h.sectionKey === sectionKey && h.segmentKey === segmentKey && h.itemKey === item.key)
    || (detail.nodeAttributeValues ?? []).some((v) =>
      v.nodeType === "Item" && v.sectionKey === sectionKey && v.segmentKey === segmentKey && v.itemKey === item.key && !!v.value);
  const removeMode: "delete" | "hide" = isCustom && !hasActivity ? "delete" : "hide";
  // The toggle earns its place only if there is something behind it. Gating is inherited, not invented:
  // renaming and removal already answer to the customisation policy, and the upload switches to
  // canEditDocs — a reader passes all three and never sees the button.
  const canEditStructure = renamable || removable || !!(uploadAutomation && !uploadAutomation.simple);
  const leaveEditing = () => {
    setEditing(false);
    setRenaming(false);
    setConfirming(false);
  };
  const startRename = () => {
    setDraft(item.name);
    setRenaming(true);
  };
  const commitRename = () => {
    const next = draft.trim();
    setRenaming(false);
    if (next && next !== item.name) itemEdit?.onRename(item.key, next, isCustom);
  };
  const statusFor = (dim: GeneratedDimension) =>
    dim.palette.statuses.find((s) => s.key === itemStatusIndex.get(itemKeyOf(sectionKey, segmentKey, item.key, dim.key)));
  // The whole status row for one axis, not just its key: the review schedule (KEY-376) lives on it.
  const itemStatusRowFor = (dimensionKey: string) =>
    (detail.itemStatuses ?? []).find((s) =>
      s.sectionKey === sectionKey && s.segmentKey === segmentKey && s.itemKey === item.key && s.dimensionKey === dimensionKey);
  // A dimension can be scoped to specific items, so this item is scored by its own axes only — one item may
  // track a document while its neighbour carries a plain yes/no. Unscoped dimensions apply to everything.
  const itemDimensions = dimensions.filter((d) => itemScopeApplies(d.appliesToItemKeys, sectionKey, segmentKey, item.key));
  const gating = itemDimensions.filter((d) => d.requiredForCompletion !== false);
  const scored = gating.length > 0 ? gating : itemDimensions;
  const complete = scored.length > 0 && scored.every((d) => !!statusFor(d)?.isTerminal);
  // "Not applicable": every scored dimension at an N/A status. The rollup ignores such an item entirely
  // (see ItemRollup.IsItemExcluded), so it must read as "not counted" rather than as work outstanding.
  const excluded = scored.length > 0 && scored.every((d) => !!statusFor(d)?.isExcludedFromRollup);
  // The dot shows the item's real status colour (matching its per-dimension pickers), not a binary
  // complete/incomplete flag: the least-advanced status across its scored dimensions, or unset grey
  // (StatusPicker's UNSET) when any dimension has no status yet — or when the item is N/A, since its
  // colour would otherwise read as progress on something that isn't being scored.
  const scoredStatuses = scored.map((d) => statusFor(d));
  const dotColour = itemDotColour(scoredStatuses, excluded);
  // KEY-512: an item scored on several axes gets one dot each, in the order the card lists them below, so a
  // collapsed row says where every axis stands instead of flattening them into one. Optional axes are in —
  // the dots are per-axis statuses, not an aggregate, so they don't compete with the Complete pill. A single
  // axis keeps the single rolled-up dot above: there is nothing to spread out, and greying it for an unset or
  // N/A item still says something the run can't.
  const axisDots = itemDimensions.length > 1
    ? itemAxisDots(dimensions, itemStatusIndex, sectionKey, segmentKey, item.key)
    : null;
  // Item attributes scoped to specific items only render on those items; an empty/absent scope = all.
  // The scope names whole section/segment/item paths, so a same-keyed item in another segment is a
  // different item and does not inherit the field.
  const itemDecls = (detail.nodeAttributes ?? []).filter((d) =>
    d.target === "Item" && itemScopeApplies(d.appliesToKeys, sectionKey, segmentKey, item.key));
  const itemRowOf = (attrKey: string) =>
    (detail.nodeAttributeValues ?? []).find((v) =>
      v.nodeType === "Item" && v.attributeKey === attrKey && v.sectionKey === sectionKey
      && v.segmentKey === segmentKey && v.itemKey === item.key);
  const itemValueOf = (attrKey: string) => itemRowOf(attrKey)?.value ?? "";
  const itemSourcedOf = (attrKey: string) => itemRowOf(attrKey)?.sourcedFromLabel ?? null;

  // Status-gate guided fill (KEY-248) for this item's per-axis statuses: a gated pick parks here until the
  // fields it needs are saved, then commits by itself. One parked pick per item — a second gated pick
  // replaces the first.
  const [pendingPick, setPendingPick] = useState<{ dimensionKey: string; statusKey: string } | null>(null);
  const itemGateKeySet = gateKeys(itemDimensions.flatMap((d) => d.palette.statuses));
  const pendingTarget = pendingPick
    ? itemDimensions.find((d) => d.key === pendingPick.dimensionKey)?.palette.statuses.find((s) => s.key === pendingPick.statusKey)
    : undefined;
  const pendingMissing = pendingTarget ? missingGateAttributes(pendingTarget, itemDecls, itemValueOf) : [];
  const demandedKeys = new Set(pendingMissing.map((d) => d.key));
  // Gate-backed fields hide while empty; they surface when a pick demands them or once they hold a value.
  // Unseeable fields (KEY-299) never render at all.
  const visibleItemDecls = itemDecls.filter((d) =>
    (attributeAccess?.(d).see ?? true)
    && (demandedKeys.has(d.key) || !itemGateKeySet.has(d.key) || gateValueFilled(itemValueOf(d.key))));
  // KEY-378: dial-wide fields the author placed on this item. Never gate-backed — DeclaredAttributeKeys
  // only admits Segment and Item targets as gateable — so they are not filtered by the pick above.
  const placedItemDecls = placedDialDecls(detail, attributeAccess, sectionKey, segmentKey, item.key);

  // KEY-412: whether this item does expiries at all. Custom (instance-added) items carry no definition
  // row, so the flag is absent on them and they take the default — allowed.
  const itemAllowsReviewDate = item.allowsReviewDate !== false;
  // A parked pick that is waiting for a REQUIRED review date rather than for gate fields. Derived, not
  // stored: the answer changes the moment the instance refreshes with the date on it.
  const pendingNeedsReviewDate = !!pendingPick && !!pendingTarget && pendingMissing.length === 0
    && reviewDateRequiredFor(pendingTarget, itemAllowsReviewDate, itemStatusRowFor(pendingPick.dimensionKey)?.reviewDate);
  // KEY-919: a "Needs a comment" status wants one saying why — only for a genuine change on that axis; the
  // server exempts re-setting the status the item already holds there.
  const itemNeedsComment = (dimensionKey: string, target: GeneratedStatus | undefined) =>
    !!target && target.key !== itemStatusIndex.get(itemKeyOf(sectionKey, segmentKey, item.key, dimensionKey))
    && commentRequiredFor(target);
  const pendingNeedsComment = !!pendingPick && !!pendingTarget && pendingMissing.length === 0
    && itemNeedsComment(pendingPick.dimensionKey, pendingTarget);
  const pendingDateLabel = itemDateLabel(pendingPick ? itemDimensions.find((d) => d.key === pendingPick.dimensionKey) : null);
  // KEY-812: an item sitting at a status that requires a date, without one. A new item starts on its scale's
  // first status with no date (seeding is a system write, which never asks), so a scale whose STARTING status
  // requires a date — a to-do's target date — would otherwise never ask. Derived from the data, so it also
  // catches bulk-added, imported and base items. Only offered to someone who could set that status.
  const undated = (() => {
    if (pendingPick) return null;
    for (const dim of itemDimensions) {
      const status = statusFor(dim);
      if (status && canSet(status) && reviewDateRequiredFor(status, itemAllowsReviewDate, itemStatusRowFor(dim.key)?.reviewDate)) {
        return { dim, status, label: itemDateLabel(dim) };
      }
    }
    return null;
  })();

  const pickItemStatus = (dim: GeneratedDimension, statusKey: string) => {
    const target = dim.palette.statuses.find((s) => s.key === statusKey);
    // KEY-916: a status that hands the work on asks who to first, and travels with them.
    if (allocationUi && target?.handsOn && target.key !== statusFor(dim)?.key) {
      allocationUi.open({
        scope: itemScope, nodeName: itemPath, mode: "handOn", handOnStatusName: target.name,
        onHandOn: (handOnTo) => {
          onSetItemStatus(item.key, dim.key, statusKey, { reviewDate: null, revertToStatusKey: null, handOnTo });
        },
      });
      return;
    }
    // Only park the pick when the user can fill the fields in; otherwise let the server answer as before.
    // Per-attribute rules (KEY-299) gate the same way: one unusable required field un-parks the lot.
    const gateMissing = target && canEditAttributes ? missingGateAttributes(target, itemDecls, itemValueOf) : [];
    const missing = gateMissing.some((d) => {
      const a = attributeAccess?.(d);
      return a !== undefined && (!a.see || !a.edit);
    })
      ? []
      : gateMissing;
    if (missing.length === 0) {
      // The status may still demand a review date (KEY-412). It cannot be applied and then given one —
      // the server refuses the first call — so park the pick and ask for the date, then send both.
      // Likewise a status that needs a comment saying why (KEY-919): it travels with the change.
      if (reviewDateRequiredFor(target, itemAllowsReviewDate, itemStatusRowFor(dim.key)?.reviewDate)
        || itemNeedsComment(dim.key, target)) {
        setPendingPick({ dimensionKey: dim.key, statusKey });
        return;
      }
      setPendingPick(null);
      onSetItemStatus(item.key, dim.key, statusKey);
      return;
    }
    setPendingPick({ dimensionKey: dim.key, statusKey });
  };

  // Item keys are only unique within their segment, so React can reuse this row for a DIFFERENT node when
  // the selection moves — a parked pick must not follow it there.
  useEffect(() => setPendingPick(null), [sectionKey, segmentKey, item.key]);

  // Commit the parked pick the moment its last required field lands (values arrive via instance refresh).
  useEffect(() => {
    if (!pendingPick) {
      return;
    }
    if (!pendingTarget) {
      setPendingPick(null);
      return;
    }
    if (missingGateAttributes(pendingTarget, itemDecls, itemValueOf).length === 0) {
      // Fields are all in — but a required review date is supplied by hand, not by a refresh, so leave the
      // pick parked for the form below to commit.
      if (reviewDateRequiredFor(pendingTarget, itemAllowsReviewDate, itemStatusRowFor(pendingPick.dimensionKey)?.reviewDate)
        || itemNeedsComment(pendingPick.dimensionKey, pendingTarget)) {
        return;
      }
      setPendingPick(null);
      onSetItemStatus(item.key, pendingPick.dimensionKey, pendingPick.statusKey);
    }
  });

  // The expanded card shows one pane at a time behind small pill tabs, capping its height: Progress (the
  // default — changing status is why the card is opened), Details (attributes), Files, Discussion. Tabs
  // render only when they have somewhere to go — no attributes means no Details, a capability off means no
  // Files / Discussion — and a one-pane card drops the strip entirely, which is how the admin draft
  // preview (documents and messages off) keeps its plain body.
  //
  // The gate flow stays whole on Progress: a parked pick's demanded fields materialise THERE, next to the
  // notice, and settle onto Details once filled (they were hidden while empty, so Details never loses a
  // field mid-flow — it only gains one after the commit).
  const demandedDecls = visibleItemDecls.filter((d) => demandedKeys.has(d.key));
  const detailsDecls = visibleItemDecls.filter((d) => !demandedKeys.has(d.key));
  // Files backing a seeable attribute of this item blend into its Files pane as read-only rows — the field
  // on Details stays the one write path. Dangling refs (a doc whose attribute is no longer declared here)
  // and unseeable fields (KEY-299) stay out; the dial-wide Files panel remains the everything-flat view.
  const seeableDecls = itemDecls.filter((d) => attributeAccess?.(d).see ?? true);
  const seeableAttrKeys = new Set(seeableDecls.map((d) => d.key));
  const attrDocs = attributeFiles.docs.filter((d) =>
    !!d.attributeKey && seeableAttrKeys.has(d.attributeKey) && d.segmentKey === segmentKey && d.itemKey === item.key);
  const fileCount = docs.length + attrDocs.length;
  // Filled tally for the collapsed row's fields chip — over every seeable field, including gate-backed
  // ones still hidden while empty: they're part of what this item will eventually need filled in.
  const filledDeclCount = seeableDecls.filter((d) => gateValueFilled(itemValueOf(d.key))).length;
  // KEY-670: the item card's headings read the dial's own words too, at the ITEM level — so "guidance.item" can
  // differ from the action point's "guidance.segment" above it.
  const tabDefs: ItemTabDef[] = [{ id: "progress", label: dialLabel(detail, "progress"), alert: !!pendingPick || !!undated }];
  // KEY-762: a section with simple item cards (a to-do, a dentist's appointment) shows ONE pane: no Files, no
  // Discussion, and the Details fields — and any authored guidance — drawn within Progress, so there is no strip.
  // KEY-853: the action point's own setting wins over its section's.
  const simple = simpleItemCards(detail, sectionKey, segmentKey);
  // KEY-378: placed dial-wide fields count towards the Details tab too — an item that carries only those
  // would otherwise have no tab to show them on, and the placement would silently do nothing.
  if (!simple && detailsDecls.length + placedItemDecls.length > 0) {
    tabDefs.push({ id: "details", label: dialLabel(detail, "details"), count: detailsDecls.length + placedItemDecls.length });
  }
  // KEY-588: the item's authored guidance — text, videos, documents and external links — gets its own pane,
  // as an action point's does. The links used to sit at the foot of Files, where they read as the item's
  // evidence (and made an empty Files pane look populated); the videos and documents sat above the tabs,
  // with the documents drawn only when there was also a video.
  // Counted as the pane draws them: a document with no download URL and a link with no URL render nothing.
  const guidanceCount = (item.videos?.length ?? 0)
    + (item.documents?.filter((d) => !!d.downloadUrl).length ?? 0)
    + (item.links?.filter((l) => !!l.url).length ?? 0);
  const hasGuidance = guidanceCount > 0 || richTextHasContent(item.guidance);
  if (!simple && hasGuidance) {
    tabDefs.push({ id: "guidance", label: dialLabel(detail, "guidance.item"), count: guidanceCount });
  }
  if (!simple && documentsEnabled) {
    tabDefs.push({ id: "files", label: dialLabel(detail, "files.item"), count: fileCount });
  }
  if (!simple && messagesEnabled) {
    tabDefs.push({ id: "discussion", label: dialLabel(detail, "discussion.item"), count: messageCount });
  }
  // KEY-774 reversed "Progress on every open": a card now opens on the tab last chosen on ANY item card, so
  // working down a list on Details stays on Details. Seeded on every open, and when React reuses this row for a
  // different item; still one shared memory rather than one per item. Read once at open rather than
  // subscribed, so a choice on one open card doesn't flip the tab of another that is already open.
  const [itemTab, setItemTabState] = useState<ItemTabId>(() => rememberedTab<ItemTabId>("item") ?? "progress");
  const setItemTab = useCallback((t: ItemTabId) => {
    setItemTabState(t);
    rememberTab("item", t);
  }, []);
  useEffect(() => {
    if (open) {
      setItemTabState(rememberedTab<ItemTabId>("item") ?? "progress");
    }
    // Shutting a card puts it back in its read view: an edit view left armed on a collapsed card is a
    // mode you cannot see you are in.
    leaveEditing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sectionKey, segmentKey, item.key]);
  // KEY-812: a just-added item arrives as "New to-do" — put the title straight into edit, text selected, so
  // the first keystroke names it. An effect rather than initial state: the row mounts on the refresh that
  // lands before the add call returns its key. Declared after the reset above, which runs on the same
  // opening and would otherwise put the title straight back.
  useEffect(() => {
    if (!startRenaming || !open) return;
    if (renamable) startRename();
    onRenameStarted?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startRenaming, open]);
  // A tab can disappear under you (the last Details field cleared, say) — fall back to Progress.
  const activeTab = tabDefs.some((t) => t.id === itemTab) ? itemTab : "progress";

  // KEY-753: this section's word for one of its items.
  const word = itemNouns(detail, sectionKey).one;

  return (
    <div className="kd-item-row" style={{ padding: "11px 14px", borderTop: first ? "none" : "var(--border-w) solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          title={open ? "Collapse" : "Expand"}
          className="kd-focusable"
          style={{ display: "inline-flex", flex: "none", alignItems: "center", justifyContent: "center", padding: 2, background: "none", border: "none", cursor: "pointer", color: "var(--text-faint)", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.12s" }}
        >
          <Icon name="chevDown" size={14} />
        </button>
        {axisDots ? <AxisDotRun dots={axisDots} size={9} /> : <StatusDot colour={dotColour} size={9} />}
        {!open ? (
          <button
            type="button"
            onClick={onToggle}
            title="Expand"
            data-help-anchor={HELP_ANCHORS.itemRowToggle}
            style={{ font: "inherit", flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, textAlign: "left", background: "none", border: "none", color: "var(--text)", cursor: "pointer", padding: 0, whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {item.name}
          </button>
        ) : renaming ? (
          <input
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") {
                skipBlurSave.current = true;
                setRenaming(false);
              }
            }}
            onBlur={() => {
              if (skipBlurSave.current) {
                skipBlurSave.current = false;
                return;
              }
              commitRename();
            }}
            className="kd-focusable"
            aria-label={`Rename ${item.name}`}
            style={{ flex: 1, minWidth: 0, font: "inherit", fontSize: 14, fontWeight: 500, padding: "3px 7px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--brand-400, #818cf8)", background: "var(--surface)", color: "var(--text)" }}
          />
        ) : renamable && editing ? (
          <span style={{ flex: 1, minWidth: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <button
              type="button"
              onClick={startRename}
              title="Click to rename"
              style={{ font: "inherit", fontSize: 14, fontWeight: 500, minWidth: 0, textAlign: "left", background: "none", border: "none", color: "var(--text)", cursor: "text", padding: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {item.name}
            </button>
            <button
              type="button"
              onClick={startRename}
              aria-label={`Rename ${item.name}`}
              title="Rename"
              className="kd-focusable"
              style={{ display: "inline-flex", flex: "none", alignItems: "center", justifyContent: "center", padding: 3, background: "none", border: "none", borderRadius: "var(--radius-sm)", color: "var(--text-faint)", cursor: "pointer" }}
            >
              <Icon name="pencil" size={13} />
            </button>
          </span>
        ) : (
          <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{item.name}</span>
        )}
        {/* KEY-859: when it is, for something added to a day — "All day", "10:00–18:00". */}
        {spanLabel((item as ResolvedItem).span) && (
          <span style={{ flex: "none", fontSize: 12.5, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{spanLabel((item as ResolvedItem).span)}</span>
        )}
        {/* Glance chips: a collapsed row answers "anything for me here?" without being expanded. Quiet
            rows stay quiet — every chip renders only when it has something to say. */}
        {!open && undated && (
          <span
            title={`${undated.status.name} needs ${undated.label === DEFAULT_ITEM_DATE_LABEL ? "a review date" : `a ${undated.label.toLowerCase()}`}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: "none", fontSize: 11.5, fontWeight: 600, color: "var(--st-amber, #b45309)", background: hexA(GATE_AMBER, 0.1), border: `var(--border-w) solid ${hexA(GATE_AMBER, 0.35)}`, borderRadius: 99, padding: "1px 8px" }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 99, background: "var(--st-amber, #b45309)" }} />
            needs {undated.label === DEFAULT_ITEM_DATE_LABEL ? "a date" : undated.label.toLowerCase()}
          </span>
        )}
        {!open && pendingPick && (
          <span
            title="A status change is parked, waiting on a required field"
            style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: "none", fontSize: 11.5, fontWeight: 600, color: "var(--st-amber, #b45309)", background: hexA(GATE_AMBER, 0.1), border: `var(--border-w) solid ${hexA(GATE_AMBER, 0.35)}`, borderRadius: 99, padding: "1px 8px" }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 99, background: "var(--st-amber, #b45309)" }} />
            pending
          </span>
        )}
        {!open && seeableDecls.length > 0 && (
          <span
            title={`${filledDeclCount} of ${seeableDecls.length} field${seeableDecls.length === 1 ? "" : "s"} filled in`}
            style={{ display: "inline-flex", alignItems: "center", flex: "none", fontSize: 11.5, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--text-faint)", background: "var(--surface-inset)", border: "var(--border-w) solid var(--border)", borderRadius: 99, padding: "1px 8px" }}
          >
            {filledDeclCount}/{seeableDecls.length} fields
          </span>
        )}
        {!open && !simple && fileCount > 0 && (
          <span
            title={`${fileCount} file${fileCount === 1 ? "" : "s"} attached`}
            aria-label={`${fileCount} file${fileCount === 1 ? "" : "s"} attached`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, flex: "none", fontSize: 11.5, fontWeight: 600, color: "var(--text-faint)", background: "var(--surface-inset)", border: "var(--border-w) solid var(--border)", borderRadius: 99, padding: "1px 8px" }}
          >
            <Icon name="clip" size={12} />
            {fileCount}
          </span>
        )}
        {!open && !simple && messageCount > 0 && (
          <span
            title={`${messageCount} comment${messageCount === 1 ? "" : "s"}`}
            aria-label={`${messageCount} comment${messageCount === 1 ? "" : "s"}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, flex: "none", fontSize: 11.5, fontWeight: 600, color: "var(--text-faint)", background: "var(--surface-inset)", border: "var(--border-w) solid var(--border)", borderRadius: 99, padding: "1px 8px" }}
          >
            <Icon name="chat" size={12} />
            {messageCount}
          </span>
        )}
        {open && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "2px 9px",
              borderRadius: 99,
              background: complete ? hexA("#3eb034", 0.14) : "var(--surface-inset)",
              color: complete ? "#3eb034" : "var(--text-faint)",
            }}
            title={excluded ? `Not applicable — this ${word} is left out of the completion count` : undefined}
          >
            {excluded ? "N/A" : complete ? "Complete" : "In progress"}
          </span>
        )}
        {open && canEditStructure && (
          <button
            type="button"
            onClick={() => (editing ? leaveEditing() : setEditing(true))}
            aria-pressed={editing}
            aria-label={editing ? `Finish editing ${item.name}` : `Edit ${item.name}`}
            title={editing ? `Finish editing this ${word}` : `Edit this ${word}`}
            className="kd-focusable"
            style={{
              display: "inline-flex", flex: "none", alignItems: "center", gap: 5, font: "inherit",
              fontSize: 11.5, fontWeight: 700, padding: "4px 9px", borderRadius: 99, cursor: "pointer",
              border: `var(--border-w) solid ${editing ? "var(--accent-border)" : "var(--border)"}`,
              background: editing ? "var(--accent-soft)" : "var(--surface)",
              color: editing ? "var(--accent-text)" : "var(--text-muted)",
            }}
          >
            <Icon name={editing ? "check" : "pencil"} size={12} />
            {editing ? "Done" : "Edit"}
          </button>
        )}
        {open && appointmentEditable && (confirmingDelete ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "none" }}>
            <span style={{ fontSize: 11.5, color: "var(--text-faint)", whiteSpace: "nowrap" }}>Delete from your calendar?</span>
            <button
              type="button"
              onClick={() => {
                setConfirmingDelete(false);
                appointmentEdit!.onDelete(item.key);
              }}
              className="kd-focusable"
              style={{ font: "inherit", fontSize: 12, fontWeight: 700, padding: "4px 9px", borderRadius: "var(--radius-sm)", border: `var(--border-w) solid ${hexA("#e7161b", 0.4)}`, background: hexA("#e7161b", 0.1), color: "#e7161b", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="kd-focusable"
              style={{ font: "inherit", fontSize: 12, fontWeight: 600, padding: "4px 9px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text-muted)", cursor: "pointer" }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: "none" }}>
            <button
              type="button"
              onClick={() => appointmentEdit!.onEdit(item.key)}
              className="kd-focusable"
              style={{ font: "inherit", fontSize: 12, fontWeight: 600, padding: "4px 9px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text-muted)", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Change
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              aria-label={`Delete ${item.name} from your calendar`}
              title="Delete from your calendar"
              className="kd-del-btn kd-focusable"
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, flex: "none", borderRadius: "var(--radius-sm)", border: "none", background: "none", color: "var(--text-faint)", cursor: "pointer" }}
            >
              <Icon name="trash" size={14} />
            </button>
          </span>
        ))}
        {open && editing && itemEdit && removable && (confirming ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "none" }}>
            {removeMode === "hide" && (
              <span style={{ fontSize: 11.5, color: "var(--text-faint)", whiteSpace: "nowrap" }}>
                {isCustom ? "Has activity — hide (restorable)?" : `Hide this ${word}?`}
              </span>
            )}
            <button
              onClick={() => {
                setConfirming(false);
                itemEdit.onRemove(item.key, removeMode);
              }}
              className="kd-focusable"
              style={{ font: "inherit", fontSize: 12, fontWeight: 700, padding: "4px 9px", borderRadius: "var(--radius-sm)", border: `var(--border-w) solid ${hexA("#e7161b", 0.4)}`, background: hexA("#e7161b", 0.1), color: "#e7161b", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              {removeMode === "delete" ? "Delete" : "Hide"}
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="kd-focusable"
              style={{ font: "inherit", fontSize: 12, fontWeight: 600, padding: "4px 9px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text-muted)", cursor: "pointer" }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            title={removeMode === "delete" ? `Delete ${word}` : `Hide ${word}`}
            aria-label={`${removeMode === "delete" ? "Delete" : "Hide"} ${item.name}`}
            className="kd-del-btn kd-focusable"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, flex: "none", borderRadius: "var(--radius-sm)", border: "none", background: "none", color: "var(--text-faint)", cursor: "pointer" }}
          >
            <Icon name={removeMode === "delete" ? "trash" : "ban"} size={16} />
          </button>
        ))}
      </div>
      {open && item.description && item.description.trim() && (
        <div style={{ margin: "4px 0 0 21px", fontSize: 13, lineHeight: 1.6, color: "var(--text-muted)", whiteSpace: "pre-wrap" }}>
          {item.description}
        </div>
      )}
      {open && (
        <div style={{ marginTop: 8, marginLeft: 21, display: "flex", flexDirection: "column", gap: 8 }}>
        {isCustom && renamable && itemEdit?.onSetSpan && isDaySlotKey(segmentKey) && (
          <DaySpanEditor span={(item as ResolvedItem).span} onSave={(span) => itemEdit.onSetSpan!(item.key, span)} />
        )}
        {tabDefs.length > 1 && (
          <ItemTabs tabs={tabDefs} active={activeTab} onSelect={setItemTab} groupLabel={item.name} />
        )}
        {activeTab === "progress" && (<>
        {/* KEY-916: who has this item, or the button to hand it out. */}
        {allocationUi && (itemAssignment
          ? (
            <div>
              <AllocationBadge
                instanceId={allocationUi.instanceId}
                assignment={itemAssignment}
                access={allocationUi.access}
                onChanged={allocationUi.onRefresh}
                onReassign={() => allocationUi.open({ scope: itemScope, nodeName: itemPath, mode: "allocate", statuses: itemDimensions[0]?.palette.statuses })}
              />
            </div>
          )
          : allocationUi.access?.canAllocate(itemScope) && (
            <div>
              <AllocateButton onClick={() => allocationUi.open({ scope: itemScope, nodeName: itemPath, mode: "allocate", statuses: itemDimensions[0]?.palette.statuses })} />
            </div>
          ))}
        {itemDimensions.length === 0 && (
          <div style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
            No status axis applies to this item, so it isn't scored.
          </div>
        )}
        {itemDimensions.map((dim) => (
          <div key={dim.key} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "var(--text-muted)", minWidth: 92 }} title={richTextToPlain(dim.guidance) || undefined}>
              {dim.label}
              {dim.requiredForCompletion === false && <span style={{ color: "var(--text-faint)" }}> (optional)</span>}
            </span>
            <StatusPicker
              statuses={dim.palette.statuses}
              current={statusFor(dim)}
              canSet={canSet}
              onPick={(statusKey) => pickItemStatus(dim, statusKey)}
            />
            {/* An expiry is a property of the status somebody set, so the control only offers itself to
                whoever could set that status — the same gate the action point's own review uses. */}
            <ItemReviewControl
              item={item}
              dimension={dim}
              status={itemStatusRowFor(dim.key)}
              canEdit={(() => {
                const current = statusFor(dim);
                return !!current && canSet(current);
              })()}
              dialLadder={dialLadder}
              channels={channels}
              onSchedule={(reviewDate, revertToStatusKey, leadDays, repeatDays, recurUnit, recurCount) =>
                onScheduleItemRevert(item.key, dim.key, reviewDate, revertToStatusKey, leadDays, repeatDays, recurUnit, recurCount)}
            />
          </div>
        ))}
        {pendingTarget && pendingMissing.length > 0 && (
          <PendingGateNotice
            statusName={pendingTarget.name}
            missing={pendingMissing}
            onCancel={() => setPendingPick(null)}
          />
        )}
        {/* Name first: the date sheet is modal and steals focus, which would blur the rename input of a
            freshly added item (committing nothing and ending rename). Once renaming ends, `undated` is
            still true, so the sheet opens by itself. */}
        {undated && !renaming && (
          <GateHighlight demanded statusName={undated.status.name}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                <strong style={{ color: "var(--text)" }}>{undated.status.name}</strong> needs {undated.label === DEFAULT_ITEM_DATE_LABEL ? "a review date" : `a ${undated.label.toLowerCase()}`}.
              </span>
              <ReviewSchedule
                reviewDate={null}
                revertTo={null}
                overdue={false}
                palette={undated.dim.palette.statuses}
                statusName={(k) => undated.dim.palette.statuses.find((s) => s.key === k)?.name ?? k}
                canEdit
                nodeKey={`${item.key}|${undated.dim.key}|undated`}
                nodeLabel={`${item.name}, ${undated.label.toLowerCase()}`}
                dateLabel={undated.label}
                compact
                forceOpen
                onSchedule={(reviewDate, revertToStatusKey) =>
                  onScheduleItemRevert(item.key, undated.dim.key, reviewDate, revertToStatusKey, null, null, null, null)}
              />
            </div>
          </GateHighlight>
        )}
        {(pendingNeedsReviewDate || pendingNeedsComment) && pendingPick && pendingTarget && (
          <GateHighlight demanded statusName={pendingTarget.name}>
            <PendingStatusPrompt
              key={`${pendingPick.dimensionKey}|${pendingPick.statusKey}`}
              statusName={pendingTarget.name}
              dateWord={pendingNeedsReviewDate
                ? (pendingDateLabel === DEFAULT_ITEM_DATE_LABEL ? "a review date" : `a ${pendingDateLabel.toLowerCase()}`)
                : null}
              needsComment={pendingNeedsComment}
              compact
              onCancel={() => setPendingPick(null)}
              onCommit={(review) => {
                const pick = pendingPick;
                setPendingPick(null);
                onSetItemStatus(item.key, pick.dimensionKey, pick.statusKey, review);
              }}
              renderDate={(onChosen) => (
                <ReviewSchedule
                  reviewDate={null}
                  revertTo={null}
                  overdue={false}
                  palette={itemDimensions.find((d) => d.key === pendingPick.dimensionKey)?.palette.statuses ?? []}
                  statusName={(k) => itemDimensions.find((d) => d.key === pendingPick.dimensionKey)?.palette.statuses.find((s) => s.key === k)?.name ?? k}
                  canEdit
                  nodeKey={`${item.key}|${pendingPick.dimensionKey}|required`}
                  nodeLabel={`${item.name}, required ${pendingDateLabel.toLowerCase()}`}
                  dateLabel={pendingDateLabel}
                  compact
                  forceOpen
                  onSchedule={(reviewDate, revertToStatusKey) => onChosen(reviewDate, revertToStatusKey)}
                />
              )}
            />
          </GateHighlight>
        )}
        {demandedDecls.map((d) => (
          <GateHighlight key={d.key} demanded statusName={pendingTarget?.name}>
            <AttributeField
              noun={segmentNoun(detail, sectionKey)}
              decl={d}
              value={itemValueOf(d.key)}
              sourcedFrom={itemSourcedOf(d.key)}
              canEdit={canEditAttributes && (attributeAccess?.(d).edit ?? true)}
              scope={{ sectionKey, segmentKey, itemKey: item.key }}
              files={attributeFiles}
              members={detail.members}
              people={detail.people}
              contacts={detail.contacts}
              responsible={responsibleContextOf(detail)}
              contactPrefill={contactPrefillFor(d, itemDecls, itemValueOf, detail.contacts)}
              instanceId={detail.id}
              clearedFields={detail.clearedFields}
              suggestions={attributeValuesInUse(detail.nodeAttributeValues, d.key)}
              onSave={(value) => onSetNodeAttribute({ target: "Item", sectionKey, segmentKey, itemKey: item.key, attributeKey: d.key, value })}
            />
          </GateHighlight>
        ))}
        {editing && uploadAutomation && !uploadAutomation.simple && (
          <ItemUploadAutomation item={item} controls={uploadAutomation} />
        )}
        </>)}
        {/* KEY-762: on a simple card the fields sit under the statuses, on the one pane. */}
        {(activeTab === "details" || (simple && activeTab === "progress")) && (<>
        {/* KEY-436: the one attribute surface that never had a grouping wrapper — its fields were mapped
            straight out. Authored groups are drawn here exactly as on the other four. */}
        <FieldsetRuns
          decls={detailsDecls}
          render={(d) => (
            <AttributeField
              noun={segmentNoun(detail, sectionKey)}
              key={d.key}
              decl={d}
              value={itemValueOf(d.key)}
              sourcedFrom={itemSourcedOf(d.key)}
              canEdit={canEditAttributes && (attributeAccess?.(d).edit ?? true)}
              scope={{ sectionKey, segmentKey, itemKey: item.key }}
              files={attributeFiles}
              members={detail.members}
              people={detail.people}
              contacts={detail.contacts}
              responsible={responsibleContextOf(detail)}
              contactPrefill={contactPrefillFor(d, itemDecls, itemValueOf, detail.contacts)}
              instanceId={detail.id}
              clearedFields={detail.clearedFields}
              suggestions={attributeValuesInUse(detail.nodeAttributeValues, d.key)}
              onSave={(value) => onSetNodeAttribute({ target: "Item", sectionKey, segmentKey, itemKey: item.key, attributeKey: d.key, value })}
            />
          )}
        />
        <AttributePairingNotes decls={visibleItemDecls} valueOf={itemValueOf} />
        {placedItemDecls.length > 0 && (
          <div style={{ marginTop: detailsDecls.length > 0 ? 18 : 0 }}>
            <PlacedDialAttributes
              detail={detail}
              decls={placedItemDecls}
              canEdit={canEditAttributes}
              access={attributeAccess}
              onSet={onSetNodeAttribute}
            />
          </div>
        )}
        </>)}
        {activeTab === "files" && documentsEnabled && (<>
        {attrDocs.map((d) => (
          <AttributeFileRow
            key={d.id}
            doc={d}
            fieldLabel={itemDecls.find((decl) => decl.key === d.attributeKey)?.label ?? "Attribute"}
            onShowField={() => setItemTab("details")}
          />
        ))}
        <Documents
          docs={docs}
          canEdit={canEditDocs}
          tags={detail.documentTags ?? []}
          onCreateTag={onCreateDocumentTag}
          canManageVersions={canManageDocVersions}
          instanceId={instanceId}
          sectionKey={sectionKey}
          segmentKey={segmentKey}
          itemKey={item.key}
          onChanged={onDocsChanged}
          uploadLabel={`Upload a file to this ${word}`}
          emptyText={`No files attached to this ${word} yet.`}
          compact
        />
        </>)}
        {(activeTab === "guidance" || (simple && hasGuidance && activeTab === "progress")) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 460 }}>
            {richTextHasContent(item.guidance) && (
              <RichText value={item.guidance} style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text-muted)" }} />
            )}
            <GuidanceVideos videos={item.videos} />
            <GuidanceDocuments documents={item.documents} />
            <ExternalResources links={item.links} compact />
          </div>
        )}
        {activeTab === "discussion" && messagesEnabled && (
          <ConversationThread
            noun={segmentNoun(detail, sectionKey)}
            instanceId={instanceId}
            sectionKey={sectionKey}
            segmentKey={segmentKey}
            itemKey={item.key}
            meUserId={meUserId}
            canPost={canPost}
            canModerate={canModerate}
            restricted={restricted}
            compact
            exportTitle={transcriptTitle(detail, sectionKey, segmentKey, item.key)}
          />
        )}
        </div>
      )}
    </div>
  );
}

// ---- Files (real per-segment documents: SAS upload, AV status, SAS download) ----

/**
 * A small pill-button segmented control choosing the category the next upload will be tagged with.
 *
 * It is captioned (KEY-485). Bare words above a dropzone read as an unexplained property of the action
 * point rather than as a choice about the file you are adding, and the only feedback they gave was
 * indirect: the file list stays flat until two files sit in different buckets, then grows headings. The
 * caption says whose choice it is and when it applies; the per-chip titles say what each one means.
 *
 * The pills are the tags THIS dial has added (KEY-499), then "Other", and anyone who may upload here may add
 * another inline. KEY-644 took the two product-supplied phases (Found/Resolution) out of the row.
 *
 * Controlled: the "+ Add a tag" draft is owned by {@link Documents}, not here (KEY-644). It used to live only
 * in this component, so a name typed and never Added was invisible to the upload and the file went to Other.
 * The upload now resolves the draft itself, and this picker shows that it will: while a name is typed no
 * pill reads as selected, and a line underneath says what the next upload will carry.
 */
function CategoryPicker({
  value,
  onChange,
  disabled,
  tags,
  canCreate,
  adding,
  draft,
  saving,
  error,
  onStartAdding,
  onDraftChange,
  onCommit,
  onCancel,
}: {
  value: DocumentCategory;
  /** Picking a pill also abandons any typed draft — the click is the clearer statement of intent. */
  onChange: (next: DocumentCategory) => void;
  disabled: boolean;
  tags: readonly DocumentTag[];
  /** False when the host cannot write tags (the admin draft preview): the pills still render, the "add"
   * affordance does not. */
  canCreate: boolean;
  adding: boolean;
  draft: string;
  /** An Add is in flight. */
  saving: boolean;
  error: string | null;
  onStartAdding: () => void;
  onDraftChange: (next: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  // One of these renders per item card as well as on the action point's own Files pane, so the caption
  // needs a unique id — a fixed one would leave every group but the first pointing at someone else's label.
  const labelId = useId();
  const options = categoryOptions(tags);
  const pending = resolvePendingTag(draft, tags);
  const pendingLabel =
    pending.kind === "create" ? pending.label
    : pending.kind === "existing" ? (options.find((o) => o.id === pending.category)?.label ?? pending.category)
    : null;
  const hasDraft = draft.trim().length > 0;

  return (
    <div style={{ marginBottom: 8 }}>
      <div id={labelId} style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-faint)", marginBottom: 5 }}>
        Tag the next upload as
      </div>
      <div role="group" aria-labelledby={labelId} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt) => {
          // A typed name outranks the selected pill — it is what the next upload will be filed under — so
          // nothing reads as selected while one is pending.
          const on = pendingLabel === null && value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              disabled={disabled}
              aria-pressed={on}
              title={opt.hint}
              onClick={() => onChange(opt.id)}
              className="kd-focusable"
              style={{ ...tagPillStyle(on), cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}
            >
              {on && <Icon name="check" size={12} stroke={3} />}
              {opt.label}
            </button>
          );
        })}
        {canCreate && !adding && (
          <button
            type="button"
            disabled={disabled}
            title="Add a tag of your own to this dial. Everyone here will be able to file uploads under it."
            onClick={onStartAdding}
            className="kd-focusable"
            style={{
              ...tagPillStyle(false),
              borderStyle: "dashed",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.5 : 1,
            }}
          >
            + Add a tag
          </button>
        )}
        {canCreate && adding && (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input
              autoFocus
              value={draft}
              maxLength={40}
              disabled={disabled}
              aria-label="New tag name"
              placeholder="Tag name"
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); onCommit(); }
                if (e.key === "Escape") { e.preventDefault(); onCancel(); }
              }}
              className="kd-focusable"
              style={{
                font: "inherit", fontSize: 11.5, padding: "4px 10px", borderRadius: 99, width: 140,
                border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)",
              }}
            />
            <button
              type="button"
              disabled={disabled || !hasDraft}
              onClick={onCommit}
              className="kd-focusable"
              style={{
                ...tagPillStyle(false),
                borderColor: "var(--accent-border)",
                color: "var(--accent-text)",
                cursor: disabled || !hasDraft ? "default" : "pointer",
                opacity: hasDraft ? 1 : 0.5,
              }}
            >
              {saving ? "Adding…" : "Add"}
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={onCancel}
              className="kd-focusable"
              style={{ ...tagPillStyle(false), cursor: disabled ? "default" : "pointer" }}
            >
              Cancel
            </button>
          </span>
        )}
      </div>
      {error ? (
        <p role="alert" style={{ margin: "6px 0 0", fontSize: 12, color: "var(--st-overdue)" }}>{error}</p>
      ) : pendingLabel !== null && (
        <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
          The next upload will be tagged “{pendingLabel}”{pending.kind === "create" ? ", a new tag for this dial" : ""}.
        </p>
      )}
    </div>
  );
}

function DocStatusBadge({ status }: { status: DialDocument["status"] }) {
  const styles: Record<DialDocument["status"], { bg: string; fg: string; label: string }> = {
    Pending: { bg: hexA("#f5a51c", 0.16), fg: "#b9770e", label: "Scanning…" },
    Clean: { bg: hexA("#3eb034", 0.16), fg: "#2f8a28", label: "Clean" },
    Quarantined: { bg: hexA("#e7161b", 0.14), fg: "#c11118", label: "Quarantined" },
  };
  const s = styles[status];
  return <span style={{ borderRadius: 99, padding: "2px 8px", fontSize: 10.5, fontWeight: 700, background: s.bg, color: s.fg }}>{s.label}</span>;
}

/**
 * A read-only row for a file that backs one of an item's Document/Image/Video attributes, blended into
 * the item's Files pane so "everything attached to this item" reads as one list. The field itself (on
 * the Details pane) stays the only place the file is uploaded, replaced or removed — the chip jumps there.
 */
function AttributeFileRow({ doc, fieldLabel, onShowField }: {
  doc: DialDocument;
  fieldLabel: string;
  onShowField: () => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface-2)" }}>
      <span style={{ width: 30, height: 30, borderRadius: 9, background: "var(--accent-soft)", color: "var(--accent-text)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
        <Icon name="file" size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {doc.fileName}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{doc.uploadedByEmail} · {timeAgo(doc.uploadedAtUtc)}</div>
      </div>
      <DocStatusBadge status={doc.status} />
      <button
        type="button"
        onClick={onShowField}
        className="kd-focusable"
        title={`Held by the ${fieldLabel} field — view it on Details`}
        style={{
          font: "inherit", fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 99, whiteSpace: "nowrap", flex: "none",
          border: "var(--border-w) solid var(--accent-border)", background: "var(--accent-soft)", color: "var(--accent-text)", cursor: "pointer",
        }}
      >
        {fieldLabel} · field
      </button>
    </div>
  );
}

/**
 * A document list with SAS upload / AV status / SAS download / delete. Used both for the action point as
 * a whole (the Files tab, with per-row item attribution) and, scoped via <c>itemKey</c>, inline under a
 * single item. Uploads carry <c>itemKey</c> so the file attaches to that item; the action-point list
 * leaves it undefined.
 */
function Documents({
  docs,
  canEdit,
  canManageVersions,
  instanceId,
  sectionKey,
  segmentKey,
  itemKey,
  onChanged,
  uploadLabel = "Upload a file",
  emptyText = "No supporting files attached yet.",
  attribution,
  compact = false,
  tags = [],
  onCreateTag,
}: {
  docs: DialDocument[];
  canEdit: boolean;
  /** The dial's own upload tags (KEY-499), for the picker and for grouping order. Defaults to none, which
   * is every dial that has added nothing — and the admin draft preview, which has no instance to have. */
  tags?: readonly DocumentTag[];
  /** Add a tag to the dial. Absent in hosts that cannot write one; the "+ Add a tag" pill is then hidden. */
  onCreateTag?: (label: string) => Promise<string | null>;
  /**
   * Instance admins may restore an earlier version from the history dropdown, and delete a file outright.
   * Both are instance-Admin-only on the server: deletion is the one irreversible document action, so it
   * sits above uploading evidence (canEdit) rather than alongside it.
   */
  canManageVersions: boolean;
  instanceId: string;
  sectionKey: string;
  segmentKey: string;
  itemKey?: string;
  onChanged: () => Promise<void>;
  uploadLabel?: string;
  emptyText?: string;
  attribution?: (doc: DialDocument) => ReactNode;
  compact?: boolean;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<DocumentCategory>("Uncategorized");
  // The picker's "+ Add a tag" input lives HERE rather than in the picker (KEY-644), because the upload has
  // to see it: a name typed and never Added used to vanish, and the file was quietly filed as Other.
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [savingTag, setSavingTag] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);
  /** KEY-938: a demo dial's own words for a refused upload or download — a notice, not a failure. */
  const [notice, setNotice] = useState<string | null>(null);

  function closeDraft() {
    setAdding(false);
    setDraft("");
    setTagError(null);
  }

  function pickCategory(next: DocumentCategory) {
    setCategory(next);
    closeDraft();
  }

  /**
   * Turns the typed draft into the category to file under, adding the tag to the dial first when it is new.
   * With nothing typed, the selected pill stands. On success the result is selected and the draft closed,
   * so the pill row reads what just happened; on a refusal nothing changes and the reason comes back.
   *
   * The host reloads the detail before `onCreateTag` resolves, so the new pill already exists by the time it
   * is selected.
   */
  async function settleDraft(): Promise<{ category: DocumentCategory } | { label: string; refused: string }> {
    const pending = resolvePendingTag(draft, tags);
    if (pending.kind === "none") {
      return { category };
    }
    let next: DocumentCategory;
    if (pending.kind === "existing") {
      next = pending.category;
    } else {
      const refused = onCreateTag ? await onCreateTag(pending.label) : "Tags can't be added here.";
      if (refused) {
        return { label: pending.label, refused };
      }
      next = pending.label;
    }
    setCategory(next);
    closeDraft();
    return { category: next };
  }

  async function commitDraft() {
    if (savingTag || busy) {
      return;
    }
    setSavingTag(true);
    setTagError(null);
    try {
      const settled = await settleDraft();
      if ("refused" in settled) {
        // The draft survives so the name can be corrected rather than retyped.
        setTagError(settled.refused);
      }
    } finally {
      setSavingTag(false);
    }
  }

  async function upload(file: File) {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    setTagError(null);
    try {
      // A typed name is the tag the uploader means, Added or not (KEY-644). If the dial refuses it, upload
      // NOTHING: filing the evidence as Other with the name still on screen is exactly the bug this replaced.
      // The resolved category is used from this local, not re-read from state set a moment ago.
      const settled = await settleDraft();
      if ("refused" in settled) {
        setTagError(`Not uploaded — couldn't add tag “${settled.label}”: ${settled.refused}`);
        return;
      }
      const ticket = await api.requestDocumentUpload(token, instanceId, {
        sectionKey,
        segmentKey,
        itemKey,
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        category: settled.category,
      });
      await api.uploadDocumentBytes(ticket.uploadUrl, file);
      // Tell the server the bytes landed: this fires the auto-set-status rule for any enabled item the
      // file targets. Best-effort — the file is uploaded regardless; only the status automation depends on it.
      try {
        await api.confirmDocumentUploaded(token, instanceId, ticket.documentId);
      } catch {
        /* the upload itself succeeded; the status automation simply won't fire */
      }
      await onChanged();
    } catch (e) {
      // KEY-938: a demo dial refuses at the moment the file would have gone up, with its own words (a 409).
      // The tags were chosen and the button pressed; nothing left their machine. A notice, not an error.
      if (e instanceof ApiError && e.status === 409) {
        setNotice(e.message);
      } else {
        setError(e instanceof ApiError ? e.message : "Upload failed.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function download(documentId: string) {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      const { downloadUrl } = await api.getDocumentDownloadUrl(token, instanceId, documentId);
      window.open(downloadUrl, "_blank", "noopener");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setNotice(e.message); // KEY-938: a demo's documents are names only
      } else {
        setError("Could not get the download link.");
      }
    }
  }

  async function remove(documentId: string) {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      await api.deleteDocument(token, instanceId, documentId);
      await onChanged();
    } catch {
      setError("Could not delete the document.");
    }
  }

  const iconBox = compact ? 30 : 36;

  function DocRow(d: DialDocument) {
    return (
      <div
        key={d.id}
        style={{
          display: "flex",
          alignItems: "center",
          gap: compact ? 10 : 12,
          padding: compact ? "9px 11px" : "11px 13px",
          borderRadius: "var(--radius-sm)",
          border: "var(--border-w) solid var(--border)",
          background: "var(--surface-2)",
        }}
      >
        <span
          style={{
            width: iconBox,
            height: iconBox,
            borderRadius: 9,
            background: "var(--accent-soft)",
            color: "var(--accent-text)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          <Icon name="file" size={compact ? 15 : 17} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: compact ? 13.5 : 14, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {d.fileName}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            {attribution?.(d)}
            <span>{d.uploadedByEmail} · {timeAgo(d.uploadedAtUtc)}</span>
          </div>
        </div>
        <DocStatusBadge status={d.status} />
        {d.versionCount > 1 && (
          <VersionMenu doc={d} instanceId={instanceId} canManageVersions={canManageVersions} onChanged={onChanged} />
        )}
        {/* KEY-537: the row stays and the button goes. They already know the file is here — hiding the
            row would tell them less, and a button that 403s would tell them worse. */}
        {d.status === "Clean" && d.canDownload !== false && (
          <button onClick={() => void download(d.id)} title="Download" aria-label="Download" style={iconBtn}>
            <Icon name="download" size={16} />
          </button>
        )}
        {canManageVersions && (
          <button onClick={() => void remove(d.id)} title="Delete" aria-label="Delete" style={{ ...iconBtn, color: "var(--st-overdue)" }}>
            <Icon name="trash" size={16} />
          </button>
        )}
      </div>
    );
  }

  // Only split the list into sub-sections once something has actually been tagged — an instance where
  // nobody uses categories yet keeps the plain flat list it always had.
  const showGroupHeaders = docs.some((d) => d.category !== "Uncategorized");
  // Derived from the documents, NOT by filtering a fixed option list: a file carrying a withdrawn tag must
  // still appear, under its own name. See documentGroups.
  const groups = showGroupHeaders ? documentGroups(docs, tags) : null;

  return (
    <div>
      {error && <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--st-overdue)" }}>{error}</p>}
      {notice && <p role="status" style={{ margin: "0 0 8px", fontSize: 12.5, color: "var(--text-muted)" }}>{notice}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: compact ? 12 : 14, marginBottom: 12 }}>
        {docs.length === 0 && <p style={{ color: "var(--text-faint)", fontSize: compact ? 13 : 14, margin: 0 }}>{emptyText}</p>}
        {groups
          ? groups.map((g) => (
              <div key={g.id} style={{ display: "flex", flexDirection: "column", gap: compact ? 8 : 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-faint)" }}>
                  {g.label} ({g.docs.length})
                </div>
                {g.docs.map((d) => DocRow(d))}
              </div>
            ))
          : docs.map((d) => DocRow(d))}
      </div>
      {canEdit && (
        <>
          {/* Nothing to choose between in a host that has no tags and can't add one (the admin draft
              preview): a lone "Other" pill would be a control with one position. */}
          {(tags.length > 0 || onCreateTag) && (
            <CategoryPicker
              value={category}
              onChange={pickCategory}
              disabled={busy || savingTag}
              tags={tags}
              canCreate={!!onCreateTag}
              adding={adding}
              draft={draft}
              saving={savingTag}
              error={tagError}
              onStartAdding={() => setAdding(true)}
              onDraftChange={(next) => { setDraft(next); setTagError(null); }}
              onCommit={() => void commitDraft()}
              onCancel={closeDraft}
            />
          )}
          <label className="kd-btn-dashed" data-help-anchor={HELP_ANCHORS.apUploadEvidence} style={{ cursor: busy || savingTag ? "default" : "pointer" }}>
            <Icon name="upload" size={16} />
            {busy ? "Uploading…" : uploadLabel}
            <input
              type="file"
              style={{ display: "none" }}
              disabled={busy || savingTag}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  void upload(f);
                }
                e.target.value = "";
              }}
            />
          </label>
        </>
      )}
    </div>
  );
}

/**
 * Version-history dropdown for a file that has more than one version. Shows "v{n}" for the current version
 * and, when opened, lazy-loads the whole family (newest first) so any version can be viewed. Instance admins
 * additionally get a "Restore" action that brings an earlier version back as the new current version.
 */
function VersionMenu({
  doc,
  instanceId,
  canManageVersions,
  onChanged,
}: {
  doc: DialDocument;
  instanceId: string;
  canManageVersions: boolean;
  onChanged: () => Promise<void>;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<DialDocument[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (versions === null) {
      const token = getAccessToken();
      if (!token) {
        return;
      }
      try {
        setVersions(await api.listDocumentVersions(token, instanceId, doc.id));
      } catch {
        setError("Could not load the version history.");
      }
    }
  }

  async function view(documentId: string) {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      const { downloadUrl } = await api.getDocumentDownloadUrl(token, instanceId, documentId);
      window.open(downloadUrl, "_blank", "noopener");
    } catch {
      setError("Could not open that version.");
    }
  }

  async function restore(documentId: string) {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.rollbackDocument(token, instanceId, documentId);
      setOpen(false);
      setVersions(null); // force a re-fetch next open (a new current version now exists)
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not restore that version.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        onClick={() => void toggle()}
        title={`${doc.versionCount} versions`}
        aria-label="Version history"
        style={{ ...iconBtn, alignItems: "center", gap: 3 }}
      >
        <Icon name="layers" size={15} />
        <span style={{ fontSize: 11, fontWeight: 700 }}>v{doc.versionNumber}</span>
        <Icon name="chevDown" size={12} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              zIndex: 30,
              minWidth: 250,
              maxWidth: 340,
              background: "var(--surface)",
              border: "var(--border-w) solid var(--border)",
              borderRadius: "var(--radius)",
              boxShadow: "var(--shadow-lg)",
              padding: 6,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-faint)", padding: "4px 8px 6px" }}>
              Version history
            </div>
            {error && <p style={{ margin: "2px 8px 6px", fontSize: 12, color: "var(--st-overdue)" }}>{error}</p>}
            {versions === null && !error && <p style={{ margin: "2px 8px", fontSize: 12, color: "var(--text-faint)" }}>Loading…</p>}
            {versions?.map((v) => {
              // The row this menu hangs off IS the current version (the listing shows only current versions).
              const isCurrent = v.id === doc.id;
              return (
                <div key={v.id} className="kd-row-hover" style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", borderRadius: "var(--radius-sm)" }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", width: 26, flex: "none" }}>v{v.versionNumber}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      {isCurrent && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--accent-text)", background: "var(--accent-soft)", borderRadius: 99, padding: "0 6px" }}>
                          Current
                        </span>
                      )}
                      <DocStatusBadge status={v.status} />
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.uploadedByEmail} · {timeAgo(v.uploadedAtUtc)}
                    </div>
                  </div>
                  {/* The version dropdown hands an OLDER id to the very same download route, so it is the
                      obvious way round a gate applied only to the current row — the server refuses it, and
                      this keeps the offer honest. Every version of a family shares one answer. */}
                  {v.status === "Clean" && v.canDownload !== false && (
                    <button onClick={() => void view(v.id)} title="View this version" aria-label="View this version" style={iconBtn}>
                      <Icon name="download" size={15} />
                    </button>
                  )}
                  {canManageVersions && v.status === "Clean" && !isCurrent && (
                    <button onClick={() => void restore(v.id)} disabled={busy} title="Restore this version as the current one" style={restoreBtn}>
                      Restore
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </span>
  );
}

/** A small chip naming the item a file is attached to (or "Action point" for a segment-level file). */
/** Where a file on the segment's Files tab lives: on an item, on the action point itself, or (KEY-461) on
 * a node attribute — shown beside whichever of the other two it's also attached to, not instead of it. */
type FileAttributionKind = "item" | "node" | "field";

const FILE_ATTRIBUTION_STYLE: Record<FileAttributionKind, { icon: "check" | "target" | "list"; background: string; color: string }> = {
  item: { icon: "check", background: "var(--accent-soft)", color: "var(--accent-text)" },
  node: { icon: "target", background: "var(--surface-inset)", color: "var(--text-faint)" },
  field: { icon: "list", background: "var(--surface-inset)", color: "var(--text-faint)" },
};

function FileAttributionChip({ label, kind }: { label: string; kind: FileAttributionKind }) {
  const { icon, background, color } = FILE_ATTRIBUTION_STYLE[kind];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 99,
        padding: "1px 8px",
        background,
        color,
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <Icon name={icon} size={11} />
      {label}
    </span>
  );
}

const iconBtn = {
  border: "none",
  background: "none",
  cursor: "pointer",
  color: "var(--text-faint)",
  display: "flex",
  padding: 4,
  flex: "none",
} as const;

const restoreBtn = {
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  cursor: "pointer",
  borderRadius: "var(--radius-sm)",
  padding: "3px 9px",
  fontSize: 11.5,
  fontWeight: 700,
  flex: "none",
} as const;
